import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { ArrowLeft, ArrowRight, CheckCircle, Clock, Plus, X, Trash2, Filter, Eye, FileText, Menu, Edit2, User, LogOut } from 'lucide-react';
import { authAPI, userAPI, claimAPI, quotaAPI, companyAPI, departmentAPI, webhookAPI, fileAPI, expenseGlCodeAPI, expenseAPI, auditAPI } from './services/api';
import * as XLSX from 'xlsx';
import glCodeMap from './data/glCodeMap.json';

const VIEW_PATHS = {
  dashboard: '/',
  'my-claims': '/my-claim',
  approval: '/approvals',
  report: '/reports',
  'system-activity': '/system-activity',
  'manage-users': '/manage/users',
  'manage-companies': '/manage/companies',
  'manage-departments': '/manage/departments',
  'manage-webhooks': '/manage/webhooks',
  'manage-expense-gl-codes': '/manage/expense-gl-codes',
  'manage-expenses': '/manage/expenses'
};

const MAX_ANALYZE_FILE_SIZE_MB = 10;
const MAX_ANALYZE_FILE_SIZE_BYTES = MAX_ANALYZE_FILE_SIZE_MB * 1024 * 1024;
const ANALYZE_UPLOAD_BATCH_SIZE = 20;
const ANALYZE_RECEIPT_ENABLED = typeof window !== 'undefined' &&
  ['staging-claim.themakeover.my', 'claim.themakeover.my', 'www.claim.themakeover.my']
    .includes(String(window.location.hostname || '').toLowerCase());

const normalizePath = (pathname = '/') => {
  const lowered = String(pathname || '/').toLowerCase();
  if (!lowered || lowered === '/') return '/';
  return lowered.replace(/\/+$/, '') || '/';
};

const parseRouteFromLocation = (locationLike = window.location) => {
  const path = normalizePath(locationLike.pathname);
  const hash = String(locationLike.hash || '').toLowerCase();

  let view = 'dashboard';
  if (path === '/my-claim' || path === '/my-claims') view = 'my-claims';
  else if (path === '/approvals' || path === '/approval') view = 'approval';
  else if (path === '/reports' || path === '/report') view = 'report';
  else if (path === '/system-activity') view = 'system-activity';
  else if (path === '/manage/users') view = 'manage-users';
  else if (path === '/manage/companies') view = 'manage-companies';
  else if (path === '/manage/departments') view = 'manage-departments';
  else if (path === '/manage/webhooks') view = 'manage-webhooks';
  else if (path === '/manage/expense-gl-codes') view = 'manage-expense-gl-codes';
  else if (path === '/manage/expenses') view = 'manage-expenses';

  let tab = 'pending';
  if (hash === '#pending-approvals') tab = 'pending';
  if (hash === '#returned-claims') tab = 'returned';

  return { view, tab, path };
};

const getActivityAccessKeyFromLocation = (locationLike = window.location) => {
  try {
    return String(new URLSearchParams(locationLike.search || '').get('key') || '').trim();
  } catch (error) {
    return '';
  }
};

const MANAGE_CORE_VIEWS = new Set([
  'manage-users',
  'manage-companies',
  'manage-departments',
  'manage-webhooks'
]);

const MANAGE_FINANCE_ONLY_VIEWS = new Set([
  'manage-expense-gl-codes',
  'manage-expenses'
]);

const getRoleLabel = (role) => (
  role === 'admin' ? 'Admin' : 'User'
);

const getRoleBadgeClass = (role) => (
  role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
);

const formatActivityActionLabel = (action) => String(action || '-')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase());

const formatActivityTimestamp = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString('en-MY', {
    timeZone: 'Asia/Kuala_Lumpur',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
};

const formatActivityFieldValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    const joined = value
      .map((item) => formatActivityFieldValue(item))
      .filter((item) => item && item !== '-')
      .join(', ');
    return joined || '-';
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch (error) {
      return '[Unserializable object]';
    }
  }
  return String(value);
};

const formatActivityDetails = (log) => {
  const detailValue = log?.changed_fields_diff ?? log?.description ?? log?.details;
  return formatActivityFieldValue(detailValue);
};

const buildFullLegalName = (firstName, lastName, fullName) => {
  const providedFullName = String(fullName || '').trim();
  if (providedFullName) return providedFullName;
  return [firstName, lastName]
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .join(' ')
    .trim();
};

const normalizeMalaysiaPhone = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  let normalized = raw.replace(/[^\d+]/g, '');
  if (normalized.startsWith('+')) {
    normalized = `+${normalized.slice(1).replace(/\D/g, '')}`;
  } else {
    normalized = normalized.replace(/\D/g, '');
  }

  if (normalized.startsWith('+60')) {
    return `+${normalized.slice(1)}`;
  }

  let digits = normalized.replace(/\D/g, '');
  if (digits.startsWith('60')) {
    return `+${digits}`;
  }

  if (digits.startsWith('0')) {
    digits = `60${digits.slice(1)}`;
  }

  return digits ? `+${digits}` : '';
};

const splitFullLegalName = (fullLegalName) => {
  const normalized = String(fullLegalName || '').trim().replace(/\s+/g, ' ');
  if (!normalized) {
    return { first_name: '', last_name: '' };
  }

  const parts = normalized.split(' ');
  if (parts.length === 1) {
    return { first_name: normalized, last_name: '' };
  }

  return {
    first_name: parts.shift() || '',
    last_name: parts.join(' ').trim()
  };
};

const USER_IMPORT_FIELD_DEFINITIONS = [
  { key: 'full_name', label: 'Full Legal Name', required: true, suggestions: ['user full name', 'full name', 'employee name', 'staff name'] },
  { key: 'phone', label: 'Phone', required: true, suggestions: ['phone no', 'phone', 'mobile', 'contact no'] },
  { key: 'email', label: 'Email', suggestions: ['email', 'work email', 'personal email'] },
  { key: 'entity', label: 'Entity', required: true, suggestions: ['entity', 'company'] },
  { key: 'department', label: 'Department', suggestions: ['department', 'dept'] },
  { key: 'proj_no', label: 'Sub-department', suggestions: ['sub-department', 'sub department', 'project no', 'proj no'] },
  { key: 'report_to', label: 'Report To', suggestions: ['report to', 'manager', 'reporting manager'] },
  { key: 'position', label: 'Position', suggestions: ['position', 'designation', 'job title'] },
  { key: 'join_date', label: 'Join Date', suggestions: ['join date', 'start date', 'date joined'] },
  { key: 'resign_date', label: 'Last Employment Date', suggestions: ['last employment date', 'resign date', 'termination date'] },
  { key: 'status', label: 'Status', suggestions: ['status', 'employment status', 'user status', 'staff status', 'is active', 'active inactive'] },
  { key: 'monthly_entitlement', label: 'Monthly Entitlement (RM)', suggestions: ['monthly entitlement', 'monthly entitlement rm'] },
  { key: 'yearly_entitlement', label: 'Yearly Entitlement (RM)', suggestions: ['yearly entitlement', 'yearly entitlement rm'] }
];

const USER_IMPORT_SOURCE_BLACKLIST = new Set([
  'column name',
  'gmail',
  'webmail',
  'admin',
  'submit claim',
  'claim status',
  'approve claim',
  'view approval history',
  'corporate card history'
]);

const USER_IMPORT_PREVIEW_BLACKLIST = new Set(['gmail', 'webmail', 'admin']);

const normalizeImportHeader = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .replace(/[_-]+/g, ' ')
  .replace(/[()]/g, '')
  .trim();

const buildUniqueHeaders = (headers) => {
  const seen = new Map();
  return headers.map((header, index) => {
    const base = String(header || '').trim() || `Column ${index + 1}`;
    const count = (seen.get(base) || 0) + 1;
    seen.set(base, count);
    return count > 1 ? `${base} (${count})` : base;
  });
};

const findBestImportHeaderRow = (sheetRows = []) => {
  const knownHeaders = USER_IMPORT_FIELD_DEFINITIONS.flatMap((field) => field.suggestions);
  let bestIndex = 0;
  let bestScore = -1;

  sheetRows.slice(0, 10).forEach((row, index) => {
    const normalizedCells = (row || []).map(normalizeImportHeader);
    const score = normalizedCells.reduce((total, cell) => (
      knownHeaders.some((header) => cell.includes(header)) ? total + 1 : total
    ), 0);

    if (score > bestScore) {
      bestScore = score;
      bestIndex = index;
    }
  });

  return bestIndex;
};

const inferUserImportMappings = (columns = []) => {
  const mapping = {};
  USER_IMPORT_FIELD_DEFINITIONS.forEach((field) => {
    const match = columns.find((column) => {
      const normalizedColumn = normalizeImportHeader(column);
      if (USER_IMPORT_SOURCE_BLACKLIST.has(normalizedColumn)) return false;
      return field.suggestions.some((suggestion) => normalizedColumn.includes(suggestion));
    });
    if (match) {
      mapping[field.key] = match;
    }
  });
  return mapping;
};

const getRelevantUserImportColumns = (columns = []) => {
  const allowedFragments = USER_IMPORT_FIELD_DEFINITIONS.flatMap((field) => field.suggestions);

  return columns.filter((column) => {
    const normalized = normalizeImportHeader(column);
    if (!normalized) return false;
    if (normalized === 'status') return true;
    if (/^column\s+\d+$/.test(normalized)) return false;
    if (USER_IMPORT_SOURCE_BLACKLIST.has(normalized)) return false;

    return allowedFragments.some((fragment) => normalized.includes(fragment));
  });
};

const getUserImportPreviewColumns = (columns = []) => getRelevantUserImportColumns(columns)
  .filter((column) => !USER_IMPORT_PREVIEW_BLACKLIST.has(normalizeImportHeader(column)))
  .slice(0, 6);

const buildUserImportDataset = (headers = [], rows = []) => {
  const normalizedHeaders = headers.map((header) => ({
    original: header,
    normalized: normalizeImportHeader(header)
  }));

  const emailHeader = normalizedHeaders.find((item) => item.normalized === 'email')?.original || '';
  const gmailHeader = normalizedHeaders.find((item) => item.normalized === 'gmail')?.original || '';
  const webmailHeader = normalizedHeaders.find((item) => item.normalized === 'webmail')?.original || '';

  const shouldAddSyntheticEmail = !emailHeader && (gmailHeader || webmailHeader);
  const nextHeaders = shouldAddSyntheticEmail ? [...headers, 'Email'] : [...headers];
  const nextRows = rows.map((row) => {
    if (!shouldAddSyntheticEmail) return row;

    return {
      ...row,
      Email: String(row[gmailHeader] || row[webmailHeader] || '').trim()
    };
  });

  return {
    headers: nextHeaders,
    rows: nextRows
  };
};

const deriveAccessProfile = (user) => {
  const isAdmin = Boolean(user && user.role === 'admin');
  const hasManageUserAccess = Boolean(user?.manage_user_access);
  const hasManageCompanyAccess = Boolean(user?.manage_company_access);
  const hasManageExpenseGlCodeAccess = Boolean(user?.manage_expense_gl_code_access);
  const hasAutoManagerAccess = Boolean(user?.has_reportees);

  return {
    isAdmin,
    hasManageUserAccess,
    hasManageCompanyAccess,
    hasManageExpenseGlCodeAccess,
    hasAutoManagerAccess,
    canSeeReports: isAdmin,
    canSeeAllClaims: isAdmin,
    canAccessManageUsers: hasManageUserAccess,
    canAccessManageCompany: hasManageCompanyAccess,
    canAccessManageExpenseGlCode: hasManageExpenseGlCodeAccess,
    canAccessManage: hasManageUserAccess || hasManageCompanyAccess || hasManageExpenseGlCodeAccess,
    canDoManagerApproval: hasAutoManagerAccess,
    canDoFinanceApproval: isAdmin
  };
};

const resolveClaimApproverType = (claim, user, accessProfile) => {
  if (!user) return 'claimant';
  const claimManagerId = Number(claim?.manager_id_snapshot);
  const userId = Number(user?.id);
  const isAssignedManager = Number.isFinite(claimManagerId) && Number.isFinite(userId) && claimManagerId === userId;
  if (
    accessProfile.canDoManagerApproval &&
    isAssignedManager &&
    String(claim?.rawStatus || claim?.status || '').toLowerCase() === 'pending_manager'
  ) {
    return 'manager';
  }
  if (accessProfile.canDoFinanceApproval) return 'finance';
  if (accessProfile.canDoManagerApproval) return 'manager';
  return 'claimant';
};

const canCurrentUserActOnClaim = (claim, user, accessProfile) => {
  if (!claim) return false;
  const workflowStatus = String(claim?.rawStatus || claim?.status || '').toLowerCase();
  if (!workflowStatus.startsWith('pending')) return false;

  const approverType = resolveClaimApproverType(claim, user, accessProfile);
  const managerApproval = String(claim?.managerApproval || '').toLowerCase();
  const financeApproval = String(claim?.financeApproval || '').toLowerCase();

  if (approverType === 'manager') {
    return workflowStatus === 'pending_manager' && managerApproval === 'pending';
  }

  if (approverType === 'finance') {
    const requiresManagerDecision = Boolean(claim?.manager_id_snapshot);
    const managerGatePassed = !requiresManagerDecision || managerApproval === 'approved';
    return workflowStatus === 'pending_finance' && managerGatePassed && financeApproval === 'pending';
  }

  return false;
};

const mapBackendUserToCurrentUser = (user) => ({
  id: user.id,
  name: user.full_name,
  phone: user.phone,
  email: user.email,
  role: user.role,
  manage_user_access: Boolean(user.manage_user_access),
  manage_company_access: Boolean(user.manage_company_access),
  manage_expense_gl_code_access: Boolean(user.manage_expense_gl_code_access),
  has_reportees: Boolean(user.has_reportees),
  entity: user.entity,
  department: user.department,
  proj_no: user.proj_no || '',
  full_name: user.full_name,
  manager: user.manager || null
});

const TMGClaimSystem = () => {
  const MANAGER_NOTIFICATION_WEBHOOK_URLS = [
    'https://tmogdxteam.zeabur.app/webhook-test/46aae655-6e9f-4dc3-b95a-f93048b8b50b',
    'https://tmogdxteam.zeabur.app/webhook/46aae655-6e9f-4dc3-b95a-f93048b8b50b'
  ];
  const DEFAULT_ENTITY_NAME = 'The Makeover Guys Sdn. Bhd.';
  const formatDateInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const formatMonthInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const addMonths = (date, months) => {
    const next = new Date(date.getFullYear(), date.getMonth(), 1);
    next.setMonth(next.getMonth() + months);
    return next;
  };

  const now = new Date();
  const currentMonthStart = formatDateInput(new Date(now.getFullYear(), now.getMonth(), 1));
  const currentMonthEnd = formatDateInput(now);
  const todayMonth = formatMonthInput(now);
  const minAllowedMonth = formatMonthInput(addMonths(now, -3));

  const isMonthInAllowedWindow = (monthValue) => {
    if (!monthValue) return false;
    return monthValue >= minAllowedMonth && monthValue <= todayMonth;
  };

  const claimMonthOptions = useMemo(() => {
    const options = [];
    for (let offset = 3; offset >= 0; offset -= 1) {
      const monthDate = addMonths(now, -offset);
      options.push({
        value: formatMonthInput(monthDate),
        label: monthDate.toLocaleString('en-MY', { month: 'long', year: 'numeric' })
      });
    }
    return options;
  }, [todayMonth]);

  const resolveClaimAmount = (claim) => {
    const claimed = Number(claim?.total_claimed_amount || 0);
    const receipt = Number(claim?.total_receipt_amount || 0);
    return claimed > 0 ? claimed : receipt;
  };

  const mapClaimStatus = (rawStatus, submissionDate) => {
    if (!submissionDate) return 'Draft';
    if (!rawStatus) return 'Draft';
    if (rawStatus === 'draft' || rawStatus === 'pending') return 'Draft';
    if (rawStatus === 'pending_manager' || rawStatus === 'pending_finance') return 'Pending';
    return rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1);
  };

  const isDraftClaim = (claim) => {
    const rawStatus = String(claim?.rawStatus || '').toLowerCase();
    const status = String(claim?.status || '').toLowerCase();
    return rawStatus === 'draft' || status === 'draft';
  };

  const formatMonthYearLabel = (value) => {
    if (!value) return '-';
    const match = String(value).match(/^(\d{4})-(0[1-9]|1[0-2])$/);
    if (!match) return String(value);
    const year = match[1];
    const monthIndex = Number(match[2]) - 1;
    const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    return `${months[monthIndex]}/${year}`;
  };

  const getPrimaryClaimItem = (claim) => {
    const items = Array.isArray(claim?.items) ? claim.items : [];
    return items[0] || null;
  };

const getListClaimType = (claim) => {
  const primaryItem = getPrimaryClaimItem(claim);
  return (
    claim?.claimType ||
    claim?.claim_type ||
    primaryItem?.claim_type ||
    primaryItem?.claimType ||
    '-'
  );
};

const getListProjectName = (claim) => {
  const primaryItem = getPrimaryClaimItem(claim);
  return (
    claim?.projectName ||
    claim?.description ||
    claim?.project_name ||
    primaryItem?.description ||
    primaryItem?.desc ||
    primaryItem?.project_name ||
    '-'
  );
};

const getListClaimMonth = (claim) => {
  if (claim?.month) return claim.month;
  if (claim?.monthYear) return formatMonthYearLabel(claim.monthYear);
  if (claim?.month_year) return formatMonthYearLabel(claim.month_year);
  if (claim?.claim_month_year) return formatMonthYearLabel(claim.claim_month_year);
  return '-';
};

  const normalizeName = (value) => String(value || '').trim().toLowerCase();

  const isLegacyManagerReturn = (claim) => {
    if (!claim) return false;
    if (claim.status !== 'returned') return false;
    if (claim.finance_approval_status !== 'returned') return false;
    if (!claim.finance_user_name || !claim.manager_name_snapshot) return false;
    return normalizeName(claim.finance_user_name) === normalizeName(claim.manager_name_snapshot);
  };

  const mapManagerApproval = (managerStatus, financeStatus, rawStatus, claim) => {
    if (rawStatus === 'returned' && (managerStatus === 'rejected' || isLegacyManagerReturn(claim))) {
      return 'Returned';
    }
    if (managerStatus === 'approved') return 'Approved';
    if (managerStatus === 'rejected') return 'Rejected';
    if (!managerStatus) return '-';
    if (managerStatus === 'pending' && financeStatus && financeStatus !== 'pending') return '-';
    return 'Pending';
  };

  const resolveLastActor = (claim, fallbackName = 'Unknown') => {
    if (claim?.last_actor_name) return claim.last_actor_name;
    if (claim?.finance_approval_status && claim.finance_approval_status !== 'pending') {
      return claim.finance_user_name || claim.manager_name_snapshot || claim.requestor_name || fallbackName;
    }
    if (claim?.manager_approval_status && claim.manager_approval_status !== 'pending') {
      return claim.manager_name_snapshot || claim.requestor_name || fallbackName;
    }
    return claim?.requestor_name || claim?.requestor || fallbackName;
  };

  const resolveFinanceApprover = (claim) => {
    // Finance approval actor should only show a finance approver identity.
    // Never fall back to manager/requestor for finance approver field.
    return claim?.finance_user_name || claim?.financeApprover || '';
  };

  const resolveFinanceApprovalView = (claim, legacyManagerReturn, financeApprovalDateRaw) => {
    if (legacyManagerReturn) {
      return {
        approval: '-',
        approvalDate: null,
        approver: '',
        returnReason: '',
        rejectionReason: ''
      };
    }

    const financeApprover = resolveFinanceApprover(claim);
    const hasFinanceAction = Boolean(financeApprover || claim?.finance_approval_date);
    const managerRejectedWithoutFinanceAction =
      claim?.manager_approval_status === 'rejected' &&
      !hasFinanceAction;

    if (managerRejectedWithoutFinanceAction) {
      return {
        approval: '-',
        approvalDate: null,
        approver: '',
        returnReason: '',
        rejectionReason: ''
      };
    }

    return {
      approval: claim.finance_approval_status === 'approved' ? 'Approved' :
        claim.finance_approval_status === 'rejected' ? 'Rejected' :
          claim.finance_approval_status === 'returned' ? 'Returned' : 'Pending',
      approvalDate: financeApprovalDateRaw ? new Date(financeApprovalDateRaw).toLocaleString('en-MY', {
        timeZone: 'Asia/Kuala_Lumpur',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }) : null,
      approver: financeApprover,
      returnReason: claim.finance_action_reason || '',
      rejectionReason: claim.finance_action_reason || ''
    };
  };

  const resolveManagerName = (claim) => (
    claim?.manager_name_snapshot ||
    claim?.current_manager_name ||
    claim?.manager ||
    'N/A'
  );

  const normalizeExpenseType = (value) => String(value || '').trim().replace(/\s+/g, ' ').toUpperCase();
  const normalizeQuotaToken = (value) => String(value || '').trim().replace(/\s+/g, ' ').toUpperCase();
  const isActiveFlag = (value, fallback = true) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;

    const normalized = String(value ?? '').trim().toLowerCase();
    if (['1', 'true', 'active', 'enabled', 'yes'].includes(normalized)) return true;
    if (['0', 'false', 'inactive', 'disabled', 'no'].includes(normalized)) return false;

    return fallback;
  };
  const normalizeExpenseAlias = (value) => normalizeExpenseType(value)
    .replace(/\s*\([^)]*\)/g, '')
    .replace(/\bDESPATCH\b/g, 'DISPATCH')
    .replace(/\bACCOMODATION\b/g, 'ACCOMMODATION')
    .replace(/\bOVERSEAS\b/g, 'OUTSTATION')
    .replace(/\bTRANSPORTATION\b/g, 'TRANSPORT')
    .replace(/\bTEAM BUILDING\s*\/\s*TEAM MEAL\b/g, 'TEAM BUILDING')
    .replace(/\bTRAINING EXPENSES\b/g, 'SEMINAR & TRAINING EXPENSES')
    .replace(/\bPETROL\b/g, 'PETROL & TRANSPORT')
    .replace(/\bOUTSTATION TRAVEL\b/g, 'OUTSTATION')
    .replace(/\bTRAVELLING EXPENSES\b/g, 'OUTSTATION')
    .replace(/\bENTERTAINMENT\b/g, 'ENTERTAINMENT')
    .replace(/\s+/g, ' ')
    .trim();

  const resolveQuotaErrorRows = (message, items) => {
    const text = String(message || '');
    const scopeMatch = text.match(/Insufficient quota for\s+(.+?)\s+\((?:monthly|yearly|unlimited)\s+entitlement\)/i);
    if (!scopeMatch?.[1] || !Array.isArray(items) || items.length === 0) return [];

    const scope = scopeMatch[1].trim();
    const parts = scope.split(/\s+-\s+/);
    const claimTypeToken = normalizeQuotaToken(parts[0] || '');
    const expenseTypeToken = normalizeQuotaToken(parts.slice(1).join(' - ') || '');

    return items
      .map((item, idx) => ({
        idx,
        claimType: normalizeQuotaToken(item.claimType),
        expenseType: normalizeQuotaToken(item.expensesType)
      }))
      .filter((row) => {
        if (expenseTypeToken) {
          return row.claimType === claimTypeToken && row.expenseType === expenseTypeToken;
        }
        return row.claimType === claimTypeToken;
      })
      .map((row) => row.idx);
  };

  const normalizeEntityName = (value) => String(value || '')
    .replace(/[.,]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();

  const resolveGlCode = (entity, expenseType) => {
    if (!entity || !expenseType) return '';
    const target = normalizeEntityName(entity);
    const expenseKey = normalizeExpenseType(expenseType);

    const apiEntityMap = expenseGlCodeMap?.[target];
    if (apiEntityMap && apiEntityMap[expenseKey]) {
      return apiEntityMap[expenseKey];
    }

    const entityKey = Object.keys(glCodeMap || {}).find((key) => normalizeEntityName(key) === target);
    if (!entityKey) return '';
    const entityMap = glCodeMap?.[entityKey];
    if (!entityMap) return '';
    return entityMap[expenseKey] || '';
  };

  const matchesExpenseGlEntityFilter = (itemEntity, selectedEntity) => {
    if (!selectedEntity) return true;
    return normalizeEntityName(itemEntity) === normalizeEntityName(selectedEntity);
  };

  const inferMimeTypeFromFilename = (filename) => {
    const name = String(filename || '').toLowerCase();
    if (name.endsWith('.pdf')) return 'application/pdf';
    if (name.endsWith('.png')) return 'image/png';
    if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
    return '';
  };

  const extractFileExtension = (filename) => {
    const name = String(filename || '').trim();
    const lastDot = name.lastIndexOf('.');
    if (lastDot <= 0 || lastDot === name.length - 1) return '.pdf';
    return name.slice(lastDot);
  };

  const sanitizeFilenamePart = (value) =>
    String(value || '')
      .trim()
      .replace(/[\\/:*?"<>|]/g, '')
      .replace(/\s+/g, ' ');

  const buildAttachmentDisplayName = ({
    claimId,
    claimType,
    claimMonthYear,
    itemIndex = 0,
    totalItems = 1,
    originalFilename
  }) => {
    const idPart = sanitizeFilenamePart(claimId || 'Claim');
    const typePart = sanitizeFilenamePart(claimType || 'Item');
    const monthYearPart = sanitizeFilenamePart(claimMonthYear || '').replace(/\//g, '-');
    const baseName = [idPart, typePart, monthYearPart].filter(Boolean).join('_');
    const suffix = totalItems > 1 ? `_${itemIndex + 1}` : '';
    const ext = extractFileExtension(originalFilename);
    return `${baseName || 'attachment'}${suffix}${ext}`;
  };

  const openAttachmentPreview = (url, _filename) => {
    try {
      const targetUrl = String(url || '').trim();
      if (!targetUrl || targetUrl === '#') return;

      const anchor = document.createElement('a');
      anchor.href = targetUrl;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      anchor.click();
    } catch (error) {
      console.error('Failed to preview attachment:', error);
      alert('Unable to open attachment preview.');
    }
  };

  const formatDocDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatClaimMonthLabel = (value) => {
    if (!value) return '';
    return formatMonthYearLabel(value).replace('/', '-') + ' CLAIMS';
  };

  const formatClaimMonthMmYy = (value) => {
    if (!value) return '';
    const normalized = String(value).trim();
    const isoMatch = normalized.match(/^(\d{4})-(\d{2})$/);
    if (isoMatch) {
      return `${isoMatch[2]}/${isoMatch[1].slice(-2)}`;
    }

    const slashMatch = normalized.match(/^([A-Z]{3})\/(\d{4})$/i);
    if (slashMatch) {
      const months = {
        JAN: '01', FEB: '02', MAR: '03', APR: '04', MAY: '05', JUN: '06',
        JUL: '07', AUG: '08', SEP: '09', OCT: '10', NOV: '11', DEC: '12'
      };
      const mm = months[String(slashMatch[1]).toUpperCase()];
      if (mm) return `${mm}/${String(slashMatch[2]).slice(-2)}`;
    }

    return normalized;
  };

  const parseDateForFilter = (value) => {
    if (!value) return null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
    const raw = String(value).trim();
    if (!raw) return null;

    // HTML date input value format: yyyy-mm-dd
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [year, month, day] = raw.split('-').map(Number);
      const parsed = new Date(year, month - 1, day);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    // Manual/locale format: dd/mm/yyyy
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
      const [day, month, year] = raw.split('/').map(Number);
      const parsed = new Date(year, month - 1, day);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const isDateWithinRange = (value, fromValue, toValue) => {
    const date = parseDateForFilter(value);
    if (!date) return true;

    const from = parseDateForFilter(fromValue);
    const to = parseDateForFilter(toValue);

    if (from) from.setHours(0, 0, 0, 0);
    if (to) to.setHours(23, 59, 59, 999);

    if (from && date < from) return false;
    if (to && date > to) return false;
    return true;
  };

  const getSubmissionDateForFilter = (claim) => (
    claim?.submissionDateRaw || claim?.submissionDate
  );

  const getLastActionDateForFilter = (claim) => (
    claim?.lastActionDateRaw || claim?.lastActionDate || claim?.submissionDateRaw || claim?.submissionDate
  );

  const resolveItemAmount = (item) => {
    const receipt = Number(item?.receipt_amount ?? item?.amount ?? 0);
    const claimed = Number(item?.claimed_amount ?? 0);
    return receipt > 0 ? receipt : claimed;
  };

  const isProjectsClaimType = (claimType) => String(claimType || '').trim().toLowerCase() === 'projects';
  const isHrAndOthersClaimType = (claimType) => String(claimType || '').trim().toLowerCase() === 'hr and others';
  const requiresDescriptionField = (claimType) => isProjectsClaimType(claimType) || isHrAndOthersClaimType(claimType);
  const requiresBusinessNameField = (claimType) => isProjectsClaimType(claimType);
  const requiresReceiptNumberField = (claimType) => isProjectsClaimType(claimType);

  const ENTITLEMENT_TYPE_OPTIONS = [
    { value: 'monthly', label: 'Monthly Entitlement' },
    { value: 'yearly', label: 'Yearly Entitlement' },
    { value: 'unlimited', label: 'No Limit' }
  ];

  const normalizeEntitlementType = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'yearly') return 'yearly';
    if (normalized === 'unlimited' || normalized === 'no_limit' || normalized === 'no limit') return 'unlimited';
    return 'monthly';
  };

  const getEntitlementTypeLabel = (value) => {
    const match = ENTITLEMENT_TYPE_OPTIONS.find((option) => option.value === value);
    return match ? match.label : 'Monthly Entitlement';
  };

  const getEntitlementTypeOptions = ({ claimType }) => {
    if (isProjectsClaimType(claimType)) {
      return ENTITLEMENT_TYPE_OPTIONS.filter((option) => option.value === 'unlimited');
    }
    return ENTITLEMENT_TYPE_OPTIONS;
  };

  const inferExpenseClaimType = (entity, expenseType) => {
    const normalizedEntity = normalizeEntityName(entity || '');
    const normalizedExpense = normalizeExpenseType(expenseType || '');
    if (!normalizedEntity || !normalizedExpense) return '';

    const match = expenseItems.find((item) =>
      isActiveFlag(item.is_active) &&
      normalizeEntityName(item.entity) === normalizedEntity &&
      normalizeExpenseType(item.expense_type) === normalizedExpense
    );

    if (match?.claim_type) {
      return match.claim_type;
    }

    const normalizedAlias = normalizeExpenseAlias(expenseType || '');
    if ([
      'C-SUITE ENTITLEMENTS',
      'DENTAL EXPENSES',
      'MEDICAL EXPENSES',
      'OPTICAL EXPENSES',
      'PETROL & TRANSPORT',
      'TOLL & PARKING'
    ].includes(normalizedAlias)) {
      return 'HR and Others';
    }

    return 'Projects';
  };

  const inferEntitlementType = ({ claimType, expenseType }) => {
    if (isProjectsClaimType(claimType)) return 'unlimited';

    const normalizedExpenseType = normalizeExpenseAlias(expenseType || '');
    if (['DENTAL EXPENSES', 'MEDICAL EXPENSES', 'OPTICAL EXPENSES'].includes(normalizedExpenseType)) {
      return 'yearly';
    }

    return 'monthly';
  };

  const getDisplayedEntitlementType = ({ claimType, expenseType, storedValue }) => {
    if (isProjectsClaimType(claimType)) return 'unlimited';

    if (isHrAndOthersClaimType(claimType)) {
      return normalizeEntitlementType(storedValue || inferEntitlementType({ claimType, expenseType }));
    }

    return inferEntitlementType({ claimType, expenseType });
  };

  const getEntityClaimTypeExpenseMap = (entity) => {
    const entityKey = normalizeEntityName(entity || '');
    const explicitMap = expenseOptionsMap?.[entityKey] || {};
    const glRowsForEntity = expenseGlCodes.filter(
      (item) => normalizeEntityName(item.entity) === entityKey
    );
    const expenseRowsForEntity = expenseItems.filter(
      (item) => normalizeEntityName(item.entity) === entityKey
    );
    const activeGlRows = expenseGlCodes.filter(
      (item) => isActiveFlag(item.is_active) && normalizeEntityName(item.entity) === entityKey
    );
    const activeExpenseRows = expenseRowsForEntity.filter((item) => isActiveFlag(item.is_active));
    const getGlClaimType = (item) =>
      String(item.claim_type || '').trim() || inferExpenseClaimType(item.entity, item.expense_type);
    const buildExpenseKey = (claimType, expenseType) =>
      `${String(claimType || '').trim()}||${normalizeExpenseType(expenseType)}`;
    const buildExpenseNameKey = (expenseType) => normalizeExpenseType(expenseType);
    const activeGlExpenseKeys = new Set(
      glRowsForEntity
        .filter((item) => isActiveFlag(item.is_active))
        .map((item) => buildExpenseKey(getGlClaimType(item), item.expense_type))
    );
    const activeGlExpenseNameKeys = new Set(
      glRowsForEntity
        .filter((item) => isActiveFlag(item.is_active))
        .map((item) => buildExpenseNameKey(item.expense_type))
    );
    const inactiveGlExpenseKeys = new Set(
      glRowsForEntity
        .filter((item) => !isActiveFlag(item.is_active, false))
        .map((item) => buildExpenseKey(getGlClaimType(item), item.expense_type))
    );
    const inactiveGlExpenseNameKeys = new Set(
      glRowsForEntity
        .filter((item) => !isActiveFlag(item.is_active, false))
        .map((item) => buildExpenseNameKey(item.expense_type))
    );
    const activeExpenseKeys = new Set(
      expenseRowsForEntity
        .filter((item) => isActiveFlag(item.is_active))
        .map((item) => buildExpenseKey(item.claim_type, item.expense_type))
    );
    const inactiveExpenseKeys = new Set(
      expenseRowsForEntity
        .filter((item) => !isActiveFlag(item.is_active, false))
        .map((item) => buildExpenseKey(item.claim_type, item.expense_type))
    );
    const activeAnyKeys = new Set([...activeGlExpenseKeys, ...activeExpenseKeys]);
    const shouldHideExpense = (claimType, expenseType) => {
      const expenseKey = buildExpenseKey(claimType, expenseType);
      const expenseNameKey = buildExpenseNameKey(expenseType);
      if (inactiveGlExpenseNameKeys.has(expenseNameKey) && !activeGlExpenseNameKeys.has(expenseNameKey)) {
        return true;
      }
      if (inactiveGlExpenseKeys.has(expenseKey) && !activeGlExpenseKeys.has(expenseKey)) {
        return true;
      }
      if (inactiveExpenseKeys.has(expenseKey) && !activeAnyKeys.has(expenseKey)) {
        return true;
      }
      return false;
    };

    const nextMap = {};

    Object.entries(explicitMap).forEach(([claimType, expenseTypes]) => {
      const normalizedClaimType = String(claimType || '').trim();
      if (!normalizedClaimType || !Array.isArray(expenseTypes)) return;
      if (!nextMap[normalizedClaimType]) nextMap[normalizedClaimType] = [];
      expenseTypes.filter(Boolean).forEach((expenseType) => {
        if (!shouldHideExpense(normalizedClaimType, expenseType) && !nextMap[normalizedClaimType].includes(expenseType)) {
          nextMap[normalizedClaimType].push(expenseType);
        }
      });
    });

    const pushExpense = (claimType, expenseType) => {
      if (!claimType || !expenseType) return;
      if (shouldHideExpense(claimType, expenseType)) return;
      if (!nextMap[claimType]) nextMap[claimType] = [];
      if (!nextMap[claimType].includes(expenseType)) {
        nextMap[claimType].push(expenseType);
      }
    };

    activeExpenseRows.forEach((item) => {
      const claimType = String(item.claim_type || '').trim();
      const expenseType = String(item.expense_type || '').trim();
      if (!claimType || !expenseType) return;
      pushExpense(claimType, expenseType);
    });

    activeGlRows.forEach((item) => {
      const expenseType = String(item.expense_type || '').trim();
      if (!expenseType) return;

      const directClaimType = String(item.claim_type || '').trim() || inferExpenseClaimType(item.entity, expenseType);
      if (directClaimType) {
        pushExpense(directClaimType, expenseType);
      }
    });

    return nextMap;
  };

  const getClaimTypeOptions = (entity, currentClaimType = '') => {
    const entityClaimTypeExpenseMap = getEntityClaimTypeExpenseMap(entity);
    const availableClaimTypes = Object.keys(entityClaimTypeExpenseMap)
      .filter((claimType) => Array.isArray(entityClaimTypeExpenseMap[claimType]) && entityClaimTypeExpenseMap[claimType].length > 0);

    const baseOptions = availableClaimTypes.length > 0
      ? availableClaimTypes
      : [];

    if (currentClaimType && !baseOptions.includes(currentClaimType)) {
      return [currentClaimType, ...baseOptions];
    }

    return baseOptions;
  };

  const getExpenseOptions = (entity, claimType, currentExpenseType, options = {}) => {
    const preserveCurrent = options.preserveCurrent === true;
    const entityKey = normalizeEntityName(entity || '');
    const entityClaimTypeExpenseMap = getEntityClaimTypeExpenseMap(entity);
    const managedOptions = entityClaimTypeExpenseMap?.[claimType] || [];
    const explicitOptions = expenseOptionsMap?.[entityKey]?.[claimType] || [];
    const baseOptions = managedOptions.length > 0
      ? managedOptions
      : explicitOptions.length > 0
        ? explicitOptions
        : [];

    if (preserveCurrent && currentExpenseType && !baseOptions.includes(currentExpenseType)) {
      return [currentExpenseType, ...baseOptions];
    }
    return baseOptions;
  };

  const getDefaultClaimTypeForEntity = (entity) => getClaimTypeOptions(entity)[0] || 'Projects';

  const getDefaultExpenseTypeForEntityClaim = (entity, claimType) =>
    getExpenseOptions(entity, claimType, '')[0] || '';

  const buildDefaultClaimItem = (entity) => {
    const defaultClaimType = getDefaultClaimTypeForEntity(entity);
    return {
      claimType: defaultClaimType,
      expensesType: getDefaultExpenseTypeForEntityClaim(entity, defaultClaimType),
      description: '',
      amount: '',
      businessName: '',
      receiptNumber: '',
      file: null
    };
  };

  const buildDefaultClaimForm = (entity) => ({
    monthYear: todayMonth,
    remarks: '',
    items: [buildDefaultClaimItem(entity || DEFAULT_ENTITY_NAME)]
  });

  const getManageExpenseTypeOptions = (currentExpenseType = '') => {
    const entityKey = normalizeEntityName(expenseGlCodeFormData.entity || '');
    const claimType = expenseGlCodeFormData.claim_type || 'Projects';
    const managedTypes = expenseItems
      .filter(item =>
        isActiveFlag(item.is_active) &&
        normalizeEntityName(item.entity) === entityKey &&
        String(item.claim_type || '').trim() === claimType
      )
      .map(item => item.expense_type);
    const allExpenseTypes = Array.from(new Set(managedTypes));
    const baseOptions = ['__create_new_expense_type__', ...allExpenseTypes];
    if (currentExpenseType && !allExpenseTypes.includes(currentExpenseType)) {
      return ['__create_new_expense_type__', currentExpenseType, ...allExpenseTypes];
    }
    return baseOptions;
  };

  // States
  const initialRoute = typeof window !== 'undefined'
    ? parseRouteFromLocation(window.location)
    : { view: 'dashboard', tab: 'pending' };
  const initialActivityAccessKey = typeof window !== 'undefined'
    ? getActivityAccessKeyFromLocation(window.location)
    : '';

  const [authState, setAuthState] = useState('login');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpMethod, setOtpMethod] = useState('');
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpResendCooldown, setOtpResendCooldown] = useState(0);
  const [otpResendState, setOtpResendState] = useState('idle');
  const [otpHasResent, setOtpHasResent] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeView, setActiveView] = useState(initialRoute.view);
  const [dashboardTab, setDashboardTab] = useState(initialRoute.tab);
  const [activityAccessKey, setActivityAccessKey] = useState(initialActivityAccessKey);
  const [claims, setClaims] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [reportClaims, setReportClaims] = useState([]);
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState('');
  const [activityLastUpdated, setActivityLastUpdated] = useState('');
  const [activityFilters, setActivityFilters] = useState({
    action: '',
    claimId: '',
    startDate: currentMonthStart,
    endDate: currentMonthEnd
  });
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [showAddClaimModal, setShowAddClaimModal] = useState(false);
  const [filterMonth, setFilterMonth] = useState('Feb / 2026');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [approvalTab, setApprovalTab] = useState('pending');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const myClaimsDefaultsInitializedRef = useRef(false);
  const approvalDefaultsInitializedRef = useRef(false);
  const reportDefaultsInitializedRef = useRef(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [returnReason, setReturnReason] = useState('');
  const [pendingAction, setPendingAction] = useState(null);
  const [showEditClaimModal, setShowEditClaimModal] = useState(false);
  const [editClaimData, setEditClaimData] = useState(null);
  const [editClaimOriginalData, setEditClaimOriginalData] = useState(null);
  const [editClaimForm, setEditClaimForm] = useState({
    monthYear: '',
    remarks: '',
    items: []
  });
  const [editValidationError, setEditValidationError] = useState(false);
  const [editQuotaErrorRows, setEditQuotaErrorRows] = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    dateType: 'last_action',
    lastActionStart: currentMonthStart,
    lastActionEnd: currentMonthEnd,
    submissionStart: '',
    submissionEnd: '',
    entityFilters: {},
    statusFilters: {
      approved: true,
      pending: true,
      returned: true,
      rejected: true
    }
  });
  const [showAnalyzeModal, setShowAnalyzeModal] = useState(false);
  const [analyzeFiles, setAnalyzeFiles] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isAnalyzeDragActive, setIsAnalyzeDragActive] = useState(false);
  const [analyzeFileStatuses, setAnalyzeFileStatuses] = useState([]);
  const [analyzeStagedItems, setAnalyzeStagedItems] = useState([]);
  const [analyzeSummary, setAnalyzeSummary] = useState({ created: 0, warningRows: 0, failed: 0 });
  const [analyzeSuggestedMonthYear, setAnalyzeSuggestedMonthYear] = useState('');
  const [analyzeSuggestedRemark, setAnalyzeSuggestedRemark] = useState('');
  const [analyzeHasCompleted, setAnalyzeHasCompleted] = useState(false);
  const buildSignatureRef = useRef('');
  const buildReloadTriggeredRef = useRef(false);

  // Company and department states
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyFormData, setCompanyFormData] = useState({ full_name: '', code: '' });
  const [showDepartmentModal, setShowDepartmentModal] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [departmentFormData, setDepartmentFormData] = useState({ name: '' });
  const [showManageDropdown, setShowManageDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showReportFilter, setShowReportFilter] = useState(false);
  const [reportStatusFilters, setReportStatusFilters] = useState({ approved: true, pending: false, returned: false, rejected: false });
  const [webhookList, setWebhookList] = useState([]);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [webhookActionLoadingId, setWebhookActionLoadingId] = useState('');
  const [expenseGlCodes, setExpenseGlCodes] = useState([]);
  const [expenseGlCodeLoadError, setExpenseGlCodeLoadError] = useState('');
  const [expenseGlCodeMap, setExpenseGlCodeMap] = useState({});
  const [showExpenseGlCodeModal, setShowExpenseGlCodeModal] = useState(false);
  const [editingExpenseGlCode, setEditingExpenseGlCode] = useState(null);
  const [expenseGlCodeSearchQuery, setExpenseGlCodeSearchQuery] = useState('');
  const [expenseGlCodeEntityFilter, setExpenseGlCodeEntityFilter] = useState('');
  const [expenseGlCodeFormData, setExpenseGlCodeFormData] = useState({
    entity: '',
    claim_type: '',
    expense_type: '',
    gl_code: '',
    entitlement_type: 'monthly',
    is_active: true
  });
  const [showInlineExpenseTypeModal, setShowInlineExpenseTypeModal] = useState(false);
  const [inlineExpenseTypeFormData, setInlineExpenseTypeFormData] = useState({
    entity: '',
    claim_type: 'Projects',
    expense_type: '',
    is_active: true
  });
  const [expenseItems, setExpenseItems] = useState([]);
  const [expenseOptionsMap, setExpenseOptionsMap] = useState({});
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expenseSearchQuery, setExpenseSearchQuery] = useState('');
  const [expenseEntityFilter, setExpenseEntityFilter] = useState('');
  const [expenseClaimTypeFilter, setExpenseClaimTypeFilter] = useState('');
  const [expenseFormData, setExpenseFormData] = useState({
    entity: '',
    claim_type: 'Projects',
    expense_type: '',
    is_active: true
  });

  // User management states
  const [userList, setUserList] = useState([]);
  const [userTotalPages, setUserTotalPages] = useState(1);
  const [userCurrentPage, setUserCurrentPage] = useState(1);
  const [userTotalCount, setUserTotalCount] = useState(0);
  const [userIsLoading, setUserIsLoading] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [debouncedUserSearchQuery, setDebouncedUserSearchQuery] = useState('');
  const [userEntityFilter, setUserEntityFilter] = useState('');
  const [reportToUserPool, setReportToUserPool] = useState([]);
  const [reportToUserPoolLoading, setReportToUserPoolLoading] = useState(false);
  const [reportToSearch, setReportToSearch] = useState('');
  const [showReportToDropdown, setShowReportToDropdown] = useState(false);
  const [showUserImportModal, setShowUserImportModal] = useState(false);
  const [userImportStep, setUserImportStep] = useState('mapping');
  const [userImportFileName, setUserImportFileName] = useState('');
  const [userImportColumns, setUserImportColumns] = useState([]);
  const [userImportRows, setUserImportRows] = useState([]);
  const [userImportMappings, setUserImportMappings] = useState({});
  const [userImportResult, setUserImportResult] = useState(null);
  const [userImportError, setUserImportError] = useState('');
  const [userImportStatus, setUserImportStatus] = useState('Ready to import');
  const [userImportIsProcessing, setUserImportIsProcessing] = useState(false);
  const userImportFileInputRef = useRef(null);
  const [userFormData, setUserFormData] = useState({
    email: '',
    full_legal_name: '',
    phone: '',
    position: '',
    join_date: '',
    resign_date: '',
    entity: DEFAULT_ENTITY_NAME,
    department: '',
    role: 'user',
    manage_user_access: false,
    manage_company_access: false,
    manage_expense_gl_code_access: false,
    manager_id: '',
    proj_no: '',
    is_active: true,
    monthly_entitlement: '0.00',
    yearly_entitlement: '0.00'
  });

  const resolveDefaultEntityForUserForm = () => {
    const activeCompanies = companies.filter((c) => c.is_active);
    if (activeCompanies.length === 0) return DEFAULT_ENTITY_NAME;

    const normalizedDefault = normalizeEntityName(DEFAULT_ENTITY_NAME);
    const exactMatch = activeCompanies.find(
      (c) => normalizeEntityName(c.full_name) === normalizedDefault
    );
    if (exactMatch) return exactMatch.full_name;

    const makeoverMatch = activeCompanies.find(
      (c) => normalizeEntityName(c.full_name).includes('THE MAKEOVER GUYS SDN BHD')
    );
    if (makeoverMatch) return makeoverMatch.full_name;

    return activeCompanies[0].full_name;
  };

  const resolveActiveCompanyEntityName = (entityName) => {
    const activeCompanies = companies.filter((c) => c.is_active);
    if (!entityName) return '';
    const normalizedTarget = normalizeEntityName(entityName);
    const match = activeCompanies.find(
      (c) => normalizeEntityName(c.full_name) === normalizedTarget
    );
    return match?.full_name || entityName;
  };

  const getExpenseGlEntityOptions = (selectedEntity = '') => {
    const options = companies
      .filter((c) => c.is_active)
      .map((c) => c.full_name);

    if (!selectedEntity) return options;

    const hasSelectedEntity = options.some(
      (entity) => normalizeEntityName(entity) === normalizeEntityName(selectedEntity)
    );

    return hasSelectedEntity ? options : [selectedEntity, ...options];
  };

  const [claimForm, setClaimForm] = useState(() => buildDefaultClaimForm(DEFAULT_ENTITY_NAME));
  const [claimValidationError, setClaimValidationError] = useState(false);
  const [claimQuotaErrorRows, setClaimQuotaErrorRows] = useState([]);
  const [isSubmittingClaim, setIsSubmittingClaim] = useState(false);
  const [isResubmittingClaim, setIsResubmittingClaim] = useState(false);
  const submitClaimInFlightRef = useRef(false);
  const resubmitClaimInFlightRef = useRef(false);
  useEffect(() => {
    if (!showAddClaimModal) return;

    const entity = currentUser?.entity || DEFAULT_ENTITY_NAME;
    setClaimForm((prev) => {
      const normalizedItems = (prev.items?.length ? prev.items : [buildDefaultClaimItem(entity)]).map((item) => {
        const allowedClaimTypes = getClaimTypeOptions(entity, item.claimType);
        const nextClaimType = allowedClaimTypes.includes(item.claimType)
          ? item.claimType
          : getDefaultClaimTypeForEntity(entity);

        const allowedExpenseTypes = getExpenseOptions(entity, nextClaimType, item.expensesType, { preserveCurrent: false });
        const nextExpenseType = allowedExpenseTypes.includes(item.expensesType)
          ? item.expensesType
          : getDefaultExpenseTypeForEntityClaim(entity, nextClaimType);

        return {
          ...item,
          claimType: nextClaimType,
          expensesType: nextExpenseType
        };
      });

      return {
        ...prev,
        items: normalizedItems
      };
    });
  }, [showAddClaimModal, currentUser?.entity, expenseGlCodes, expenseOptionsMap]);
  const hasMissingProjectDescriptionInNewClaim = useMemo(
    () => claimForm.items.some((item) =>
      requiresDescriptionField(item.claimType) && !String(item.description || '').trim()
    ),
    [claimForm.items]
  );
  const hasMissingProjectBusinessNameInNewClaim = useMemo(
    () => claimForm.items.some((item) =>
      requiresBusinessNameField(item.claimType) && !String(item.businessName || '').trim()
    ),
    [claimForm.items]
  );
  const hasMissingReceiptNumberInNewClaim = useMemo(
    () => claimForm.items.some((item) =>
      requiresReceiptNumberField(item.claimType) && !String(item.receiptNumber || '').trim()
    ),
    [claimForm.items]
  );
  const hasMissingProjectDescriptionInEditClaim = useMemo(
    () => editClaimForm.items.some((item) =>
      requiresDescriptionField(item.claimType) && !String(item.description || '').trim()
    ),
    [editClaimForm.items]
  );
  const hasMissingProjectBusinessNameInEditClaim = useMemo(
    () => editClaimForm.items.some((item) =>
      requiresBusinessNameField(item.claimType) && !String(item.businessName || '').trim()
    ),
    [editClaimForm.items]
  );
  const hasMissingReceiptNumberInEditClaim = useMemo(
    () => editClaimForm.items.some((item) =>
      requiresReceiptNumberField(item.claimType) && !String(item.receiptNumber || '').trim()
    ),
    [editClaimForm.items]
  );
  const accessProfile = useMemo(() => deriveAccessProfile(currentUser), [currentUser]);
  const isPublicActivityMode = activeView === 'system-activity' && Boolean(activityAccessKey);
  const canAccessApprovals = Boolean(currentUser);
  const canPerformApprovalActions = Boolean(accessProfile.canDoManagerApproval || accessProfile.canDoFinanceApproval);
  const canAccessReports = Boolean(accessProfile.canSeeReports);
  const canAccessManageUsers = Boolean(accessProfile.canAccessManageUsers);
  const canAccessManageCompany = Boolean(accessProfile.canAccessManageCompany);
  const canAccessManageExpenseGlCode = Boolean(accessProfile.canAccessManageExpenseGlCode);
  const canAccessManage = Boolean(accessProfile.canAccessManage);
  const cancelSessionRestoreRef = useRef(false);
  const refreshCurrentUser = useCallback(async () => {
    const userData = await userAPI.getCurrentUser();
    const user = userData.user;
    setCurrentUser(mapBackendUserToCurrentUser(user));
    return user;
  }, []);
  const loadActivityLogs = useCallback(async () => {
    if (!isPublicActivityMode && (authState !== 'authenticated' || !currentUser)) return;

    try {
      setActivityLoading(true);
      setActivityError('');

      const requestOptions = {
        action: activityFilters.action || undefined,
        claim_number: activityFilters.claimId ? String(activityFilters.claimId).trim() : undefined,
        start_date: activityFilters.startDate || undefined,
        end_date: activityFilters.endDate || undefined,
        page: 1,
        limit: 200
      };

      const response = isPublicActivityMode
        ? await auditAPI.getPublicLogs({
          ...requestOptions,
          key: activityAccessKey
        })
        : await auditAPI.getLogs(requestOptions);

      setAuditLogs(response?.logs || []);
      setActivityLastUpdated(new Date().toISOString());
    } catch (error) {
      console.error('Failed to load activity logs:', error);
      setActivityError(error?.message || 'Failed to load activity logs');
      setAuditLogs([]);
    } finally {
      setActivityLoading(false);
    }
  }, [authState, currentUser, activityFilters, activityAccessKey, isPublicActivityMode]);
  const reportToSourceUsers = reportToUserPool.length > 0 ? reportToUserPool : userList;
  const loadAllReportToUsers = useCallback(async () => {
    try {
      setReportToUserPoolLoading(true);
      const limit = 500;
      let page = 1;
      let totalPages = 1;
      const collected = [];

      while (page <= totalPages && page <= 100) {
        const response = await userAPI.getAllUsers(page, limit);
        const users = Array.isArray(response?.users) ? response.users : [];
        collected.push(...users);
        totalPages = Math.max(Number(response?.totalPages || 1), 1);
        page += 1;
      }

      const deduped = [];
      const seenIds = new Set();
      collected.forEach((user) => {
        const id = Number(user?.id);
        if (!id || seenIds.has(id)) return;
        seenIds.add(id);
        deduped.push(user);
      });

      setReportToUserPool(deduped);
    } catch (error) {
      console.error('Failed to load full Report To user pool:', error);
      setReportToUserPool([]);
    } finally {
      setReportToUserPoolLoading(false);
    }
  }, []);

  const reportToOptions = useMemo(() => (
    reportToSourceUsers
      .filter(u => u.is_active && (!editingUser || u.id !== editingUser.id))
      .sort((a, b) => String(a.full_name || '').localeCompare(String(b.full_name || '')))
  ), [reportToSourceUsers, editingUser]);
  const filteredReportToOptions = useMemo(() => {
    const keyword = String(reportToSearch || '').trim().toLowerCase();
    if (!keyword) return reportToOptions.slice(0, 50);
    return reportToOptions
      .filter(u =>
        String(u.full_name || '').toLowerCase().includes(keyword) ||
        String(u.email || '').toLowerCase().includes(keyword)
      )
      .slice(0, 50);
  }, [reportToOptions, reportToSearch]);

  useEffect(() => {
    setExportFilters((prev) => {
      const activeCompanyNames = companies
        .filter((company) => company.is_active)
        .map((company) => company.full_name);

      const hasExistingSelection = Object.keys(prev.entityFilters || {}).length > 0;
      const nextEntityFilters = {};

      activeCompanyNames.forEach((name) => {
        nextEntityFilters[name] = hasExistingSelection
          ? Boolean(prev.entityFilters?.[name])
          : true;
      });

      return {
        ...prev,
        entityFilters: nextEntityFilters
      };
    });
  }, [companies]);

  const sanitizeViewForUser = useCallback((requestedView) => {
    if (requestedView === 'approval' && !canAccessApprovals) return 'dashboard';
    if (requestedView === 'report' && !canAccessReports) return 'dashboard';
    if (requestedView === 'manage-users' && !canAccessManageUsers) return 'dashboard';
    if (requestedView !== 'manage-users' && MANAGE_CORE_VIEWS.has(requestedView) && !canAccessManageCompany) return 'dashboard';
    if (MANAGE_FINANCE_ONLY_VIEWS.has(requestedView) && !canAccessManageExpenseGlCode) return 'dashboard';
    return requestedView;
  }, [canAccessApprovals, canAccessReports, canAccessManageUsers, canAccessManageCompany, canAccessManageExpenseGlCode]);

  useEffect(() => {
    const applyRouteToState = () => {
      const { view, tab } = parseRouteFromLocation(window.location);
      const nextActivityKey = getActivityAccessKeyFromLocation(window.location);
      const allowPublicActivityView = authState !== 'authenticated' && view === 'system-activity' && Boolean(nextActivityKey);
      const sanitizedView = allowPublicActivityView ? 'system-activity' : sanitizeViewForUser(view);
      const sanitizedTab = canAccessApprovals ? tab : 'returned';
      skipUrlSyncRef.current = true;
      setActivityAccessKey((prev) => (prev === nextActivityKey ? prev : nextActivityKey));
      setActiveView((prev) => (prev === sanitizedView ? prev : sanitizedView));
      if (sanitizedView === 'dashboard') {
        setDashboardTab((prev) => (prev === sanitizedTab ? prev : sanitizedTab));
      }
    };

    applyRouteToState();

    const handleRouteChange = () => applyRouteToState();
    window.addEventListener('popstate', handleRouteChange);
    window.addEventListener('hashchange', handleRouteChange);
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      window.removeEventListener('hashchange', handleRouteChange);
    };
  }, [authState, canAccessApprovals, sanitizeViewForUser]);

  useEffect(() => {
    if (authState !== 'authenticated' || !currentUser) return;

    if (skipUrlSyncRef.current) {
      skipUrlSyncRef.current = false;
      return;
    }

    const path = VIEW_PATHS[activeView] || VIEW_PATHS.dashboard;
    const hash = activeView === 'dashboard'
      ? (canAccessApprovals && dashboardTab === 'pending' ? '#pending-approvals' : '#returned-claims')
      : '';

    const params = new URLSearchParams(window.location.search);
    params.delete('view');
    const search = params.toString();
    const nextUrl = `${path}${search ? `?${search}` : ''}${hash}`;
    const currentUrl = `${normalizePath(window.location.pathname)}${window.location.search}${window.location.hash}`;

    if (nextUrl !== currentUrl) {
      window.history.pushState({}, '', nextUrl);
    }
  }, [authState, currentUser, activeView, dashboardTab, canAccessApprovals]);

  useEffect(() => {
    if (activeView === 'manage-expenses') {
      setActiveView('manage-expense-gl-codes');
    }
  }, [activeView]);

  const resolveManagerPhoneForWebhook = (claim = {}) => (
    claim?.manager_phone_snapshot ||
    claim?.current_manager_phone ||
    claim?.manager_phone ||
    currentUser?.manager?.phone ||
    ''
  );
  const resolveManagerTelegramUserIdForWebhook = (claim = {}) => (
    claim?.manager_telegram_user_id ||
    claim?.manager_telegram_id ||
    ''
  );

  const notifyManagerClaimWebhook = async ({ action, claimId, claimData, claimDetails }) => {
    const resolvedClaim = claimDetails?.claim || claimData || {};
    const managerPhone = resolveManagerPhoneForWebhook(resolvedClaim);
    const managerTelegramUserId = resolveManagerTelegramUserIdForWebhook(resolvedClaim);
    const payload = {
      event: 'claim_notification',
      action,
      sent_at: new Date().toISOString(),
      claim_id: claimId,
      submitter: {
        id: currentUser?.id || null,
        name: currentUser?.full_name || currentUser?.name || '',
        email: currentUser?.email || '',
        entity: currentUser?.entity || ''
      },
      manager_phone: managerPhone,
      manager_telegram_user_id: managerTelegramUserId,
      claim: {
        ...resolvedClaim,
        manager_phone: managerPhone,
        manager_telegram_user_id: managerTelegramUserId
      }
    };

    const results = await Promise.allSettled(
      MANAGER_NOTIFICATION_WEBHOOK_URLS.map((url) => fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      }))
    );

    const failed = [];
    for (let index = 0; index < results.length; index += 1) {
      const result = results[index];
      const url = MANAGER_NOTIFICATION_WEBHOOK_URLS[index];
      if (result.status === 'rejected') {
        failed.push(`${url} (network error)`);
        continue;
      }
      if (!result.value.ok) {
        failed.push(`${url} (status ${result.value.status})`);
      }
    }

    if (failed.length > 0) {
      throw new Error(`Webhook notification failed for: ${failed.join(', ')}`);
    }
  };

  const DEV_MODE = false;

  // Session persistence - restore session from token on page load/refresh
  useEffect(() => {
    const restoreSession = async () => {
      const token = localStorage.getItem('tmg_token');
      if (!token) return;

      try {
        await refreshCurrentUser();
        if (cancelSessionRestoreRef.current) return;
        setAuthState('authenticated');
      } catch (error) {
        // Token expired or invalid - clear and stay on login
        console.error('Session restore failed:', error);
        localStorage.removeItem('tmg_token');
      }
    };

    restoreSession();
  }, [refreshCurrentUser]);

  useEffect(() => {
    const handleSessionExpired = (event) => {
      const message = event?.detail?.message || 'Session expired. Please log in again.';
      setCurrentUser(null);
      setClaims([]);
      setPendingApprovals([]);
      setAuthState('login');
      setActiveView('dashboard');
      setDashboardTab('pending');
      setEmail('');
      setOtp(['', '', '', '', '', '']);
      setOtpMethod('');
      alert(message);
    };

    window.addEventListener('tmg:session-expired', handleSessionExpired);
    return () => {
      window.removeEventListener('tmg:session-expired', handleSessionExpired);
    };
  }, []);

  useEffect(() => {
    if (authState === 'authenticated') {
      loadUserData();
    }
  }, [authState]);

  useEffect(() => {
    if (otpResendCooldown <= 0) {
      if (otpResendState === 'success') {
        setOtpResendState('idle');
      }
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setOtpResendCooldown((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [otpResendCooldown, otpResendState]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedUserSearchQuery(userSearchQuery.trim());
    }, 300);

    return () => window.clearTimeout(timer);
  }, [userSearchQuery]);

  useEffect(() => {
    if (authState !== 'authenticated') return;

    const syncCurrentUser = async () => {
      try {
        await refreshCurrentUser();
      } catch (error) {
        console.error('Failed to refresh current user:', error);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncCurrentUser();
      }
    };

    window.addEventListener('focus', syncCurrentUser);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', syncCurrentUser);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [authState, refreshCurrentUser]);

  useEffect(() => {
    if (
      authState === 'authenticated' &&
      activeView === 'approval' &&
      currentUser &&
      canAccessApprovals
    ) {
      loadPendingApprovals();
    }
  }, [authState, activeView, currentUser, canAccessApprovals]);

  useEffect(() => {
    if (
      authState === 'authenticated' &&
      activeView === 'report' &&
      currentUser &&
      canAccessReports
    ) {
      loadReportClaims();
    }
  }, [authState, activeView, currentUser, canAccessReports]);

  useEffect(() => {
    if (activeView !== 'my-claims' || myClaimsDefaultsInitializedRef.current) return;

    const now = new Date();
    const today = formatDateInput(now);
    const firstOfMonth = formatDateInput(new Date(now.getFullYear(), now.getMonth(), 1));
    setFromDate(firstOfMonth);
    setToDate(today);
    myClaimsDefaultsInitializedRef.current = true;
  }, [activeView]);

  useEffect(() => {
    if (activeView !== 'report' || reportDefaultsInitializedRef.current) return;

    const now = new Date();
    const today = formatDateInput(now);
    const firstOfMonth = formatDateInput(new Date(now.getFullYear(), now.getMonth(), 1));
    setFromDate(firstOfMonth);
    setToDate(today);
    setReportStatusFilters({
      approved: true,
      pending: false,
      returned: false,
      rejected: false
    });
    reportDefaultsInitializedRef.current = true;
  }, [activeView]);

  useEffect(() => {
    if (activeView !== 'approval' || approvalDefaultsInitializedRef.current) return;

    const now = new Date();
    const today = formatDateInput(now);
    const firstOfMonth = formatDateInput(new Date(now.getFullYear(), now.getMonth(), 1));
    setFromDate(firstOfMonth);
    setToDate(today);
    approvalDefaultsInitializedRef.current = true;
  }, [activeView]);

  useEffect(() => {
    if (
      activeView === 'system-activity' &&
      (
        (authState === 'authenticated' && currentUser) ||
        isPublicActivityMode
      )
    ) {
      loadActivityLogs();
    }
  }, [authState, activeView, currentUser, isPublicActivityMode, loadActivityLogs]);

  useEffect(() => {
    if (
      activeView !== 'system-activity' ||
      !(
        (authState === 'authenticated' && currentUser) ||
        isPublicActivityMode
      )
    ) return undefined;

    const intervalId = window.setInterval(() => {
      loadActivityLogs();
    }, 10000);

    return () => window.clearInterval(intervalId);
  }, [authState, activeView, currentUser, isPublicActivityMode, loadActivityLogs]);

  useEffect(() => {
    if (authState === 'authenticated' && activeView === 'manage-users') {
      loadUsers(userCurrentPage);
    }
  }, [authState, activeView, userCurrentPage, debouncedUserSearchQuery, userEntityFilter]);

  useEffect(() => {
    if (!showUserModal) return;
    loadAllReportToUsers();
  }, [showUserModal, loadAllReportToUsers]);

  useEffect(() => {
    if (activeView !== 'manage-users') return;
    setUserCurrentPage(1);
  }, [debouncedUserSearchQuery, userEntityFilter, activeView]);

  useEffect(() => {
    if (!showUserModal || !userFormData.manager_id || reportToSearch) return;
    const matchedUser = reportToOptions.find((u) => u.id === userFormData.manager_id);
    if (matchedUser) {
      setReportToSearch(matchedUser.full_name || matchedUser.email || '');
    }
  }, [showUserModal, userFormData.manager_id, reportToSearch, reportToOptions]);

  useEffect(() => {
    if (!showUserModal || editingUser) return;
    const entityExists = companies.some(
      (c) => c.is_active && c.full_name === userFormData.entity
    );
    if (!entityExists) {
      setUserFormData((prev) => ({ ...prev, entity: resolveDefaultEntityForUserForm() }));
    }
  }, [showUserModal, editingUser, companies, userFormData.entity]);

  useEffect(() => {
    if (authState === 'authenticated') {
      loadCompanies();
      loadDepartments();
    }
  }, [authState]);

  useEffect(() => {
    if (authState === 'authenticated' && activeView === 'manage-companies') {
      loadCompanies();
    }
  }, [authState, activeView]);

  useEffect(() => {
    if (authState === 'authenticated' && activeView === 'manage-departments') {
      loadDepartments();
    }
  }, [authState, activeView]);

  useEffect(() => {
    if (authState === 'authenticated') {
      loadExpenseGlCodes();
    }
  }, [authState]);

  useEffect(() => {
    if (authState === 'authenticated') {
      loadExpenses();
    }
  }, [authState]);

  useEffect(() => {
    if (authState !== 'authenticated') return undefined;

    buildSignatureRef.current =
      String(document.querySelector('script[src*="/static/js/main."]')?.getAttribute('src') || '').trim();
    buildReloadTriggeredRef.current = false;

    const intervalId = window.setInterval(() => {
      checkForFrontendUpdate();
    }, 60000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForFrontendUpdate();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [authState]);

  useEffect(() => {
    if (authState === 'authenticated' && activeView === 'manage-expense-gl-codes' && canAccessManageExpenseGlCode) {
      loadExpenseGlCodes({ includeInactive: true });
    }
  }, [authState, activeView, canAccessManageExpenseGlCode]);

  useEffect(() => {
    if (authState === 'authenticated' && activeView === 'manage-expenses' && canAccessManageExpenseGlCode) {
      loadExpenses();
    }
  }, [authState, activeView, canAccessManageExpenseGlCode]);

  useEffect(() => {
    if (authState === 'authenticated' && activeView === 'manage-webhooks' && canAccessManageCompany) {
      loadWebhookConfig();
    }
  }, [authState, activeView, canAccessManageCompany]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showUserDropdown && !e.target.closest('.user-dropdown-container')) {
        setShowUserDropdown(false);
      }
      if (showManageDropdown && !e.target.closest('.manage-dropdown-container')) {
        setShowManageDropdown(false);
      }
      if (showReportFilter && !e.target.closest('.report-filter-container')) {
        setShowReportFilter(false);
      }
      if (showReportToDropdown && !e.target.closest('.report-to-dropdown-container')) {
        setShowReportToDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserDropdown, showManageDropdown, showReportFilter, showReportToDropdown]);

  const loadUserData = async () => {
    try {
      // Load user's claims
      const response = await claimAPI.getMyClaims();
      const claimsData = response.claims || [];

      // Transform backend data to match frontend structure
      const transformedClaims = claimsData.map(claim => {
        const legacyManagerReturn = isLegacyManagerReturn(claim);
        const financeApprovalDateRaw = claim.finance_approval_date ||
          ((claim.finance_approval_status && claim.finance_approval_status !== 'pending') ? claim.last_modified_date : null);
        const financeView = resolveFinanceApprovalView(claim, legacyManagerReturn, financeApprovalDateRaw);
        return ({
          claimId: claim.id,
          userId: claim.user_id,
          id: claim.claim_number,
          monthYear: claim.month_year,
          entity: claim.entity,
          department: claim.department,
          projNo: claim.proj_no || '',
          amount: resolveClaimAmount(claim),
          manager: resolveManagerName(claim),
          managerApproval: mapManagerApproval(claim.manager_approval_status, claim.finance_approval_status, claim.status, claim),
          managerApprovalDate: (claim.manager_approval_date || (legacyManagerReturn ? claim.finance_approval_date : null)) ? new Date(claim.manager_approval_date || (legacyManagerReturn ? claim.finance_approval_date : null)).toLocaleString('en-MY', { timeZone: 'Asia/Kuala_Lumpur', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : null,
          managerRejectionReason: claim.manager_rejection_reason || '',
          managerReturnReason: claim.status === 'returned' ? (claim.manager_rejection_reason || (legacyManagerReturn ? (claim.finance_action_reason || '') : '')) : '',
          financeApproval: financeView.approval,
          financeApprovalDate: financeView.approvalDate,
          financeApprover: financeView.approver,
          financeReturnReason: financeView.returnReason,
          financeRejectionReason: financeView.rejectionReason,
          submissionDate: claim.submission_date ? new Date(claim.submission_date).toLocaleString('en-MY', { timeZone: 'Asia/Kuala_Lumpur', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : null,
          lastActionDate: (claim.last_action_at || claim.last_modified_date) ? new Date(claim.last_action_at || claim.last_modified_date).toLocaleString('en-MY', { timeZone: 'Asia/Kuala_Lumpur', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : null,
          submissionDateRaw: claim.submission_date || null,
          lastActionDateRaw: claim.last_action_at || claim.last_modified_date || null,
          manager_id_snapshot: claim.manager_id_snapshot ?? null,
          status: mapClaimStatus(claim.status, claim.submission_date),
          rawStatus: claim.status || 'draft',
          claimant: claim.requestor_name || currentUser?.full_name || currentUser?.name || 'Unknown',
          requestor: claim.requestor_name || currentUser?.full_name || currentUser?.name,
          submitter: claim.requestor_name || currentUser?.full_name || currentUser?.name,
          lastActor: resolveLastActor(claim, currentUser?.full_name || currentUser?.name || 'Unknown'),
          remarks: claim.remarks || '',
          items: claim.items || []
        });
      });

      setClaims(transformedClaims.filter((claim) => !isDraftClaim(claim)));
    } catch (error) {
      console.error('Failed to load claims:', error);
      alert('Failed to load claims. Please try again.');
    }
  };

  const loadPendingApprovals = async () => {
    try {
      if (!canPerformApprovalActions && activeView !== 'approval') {
        setPendingApprovals([]);
        return;
      }
      const approverType = accessProfile.canSeeAllClaims
        ? 'finance'
        : accessProfile.hasAutoManagerAccess
          ? 'manager'
          : 'claimant';

      const response = await claimAPI.getPendingApprovals(approverType, {
        page: 1,
        limit: 1000,
        include_history: activeView === 'approval'
      });
      const claimsData = response.claims || [];

      // Transform backend data to match frontend structure
      const transformedClaims = claimsData.map(claim => {
        const legacyManagerReturn = isLegacyManagerReturn(claim);
        const financeApprovalDateRaw = claim.finance_approval_date ||
          ((claim.finance_approval_status && claim.finance_approval_status !== 'pending') ? claim.last_modified_date : null);
        const financeView = resolveFinanceApprovalView(claim, legacyManagerReturn, financeApprovalDateRaw);
        return ({
          claimId: claim.id,
          userId: claim.user_id,
          id: claim.claim_number,
          monthYear: claim.month_year,
          entity: claim.entity,
          department: claim.department,
          projNo: claim.proj_no || '',
          amount: resolveClaimAmount(claim),
          manager: resolveManagerName(claim),
          managerApproval: mapManagerApproval(claim.manager_approval_status, claim.finance_approval_status, claim.status, claim),
          managerApprovalDate: (claim.manager_approval_date || (legacyManagerReturn ? claim.finance_approval_date : null)) ? new Date(claim.manager_approval_date || (legacyManagerReturn ? claim.finance_approval_date : null)).toLocaleString('en-MY', { timeZone: 'Asia/Kuala_Lumpur', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : null,
          managerRejectionReason: claim.manager_rejection_reason || '',
          managerReturnReason: claim.status === 'returned' ? (claim.manager_rejection_reason || (legacyManagerReturn ? (claim.finance_action_reason || '') : '')) : '',
          financeApproval: financeView.approval,
          financeApprovalDate: financeView.approvalDate,
          financeApprover: financeView.approver,
          financeReturnReason: financeView.returnReason,
          financeRejectionReason: financeView.rejectionReason,
          submissionDate: claim.submission_date ? new Date(claim.submission_date).toLocaleString('en-MY', { timeZone: 'Asia/Kuala_Lumpur', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : null,
          lastActionDate: (claim.last_action_at || claim.last_modified_date) ? new Date(claim.last_action_at || claim.last_modified_date).toLocaleString('en-MY', { timeZone: 'Asia/Kuala_Lumpur', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : null,
          submissionDateRaw: claim.submission_date || null,
          lastActionDateRaw: claim.last_action_at || claim.last_modified_date || null,
          manager_id_snapshot: claim.manager_id_snapshot ?? null,
          status: mapClaimStatus(claim.status, claim.submission_date),
          rawStatus: claim.status || 'draft',
          claimant: claim.requestor_name || 'Unknown',
          requestor: claim.requestor_name || 'Unknown',
          submitter: claim.requestor_name || 'Unknown',
          lastActor: resolveLastActor(claim, 'Unknown'),
          remarks: claim.remarks || '',
          items: claim.items || []
        });
      });

      setPendingApprovals(transformedClaims.filter((claim) => !isDraftClaim(claim)));
    } catch (error) {
      console.error('Failed to load pending approvals:', error);
    }
  };

  const loadReportClaims = async () => {
    let claimsData = [];
    let primaryLoadError = null;

    // Primary source for reports:
    // use approval history endpoint first so Reports and Approvals stay in sync.
    try {
      const approverType = canAccessReports
        ? 'finance'
        : accessProfile.hasAutoManagerAccess
          ? 'manager'
          : 'claimant';
      const historyResponse = await claimAPI.getPendingApprovals(approverType, {
        page: 1,
        limit: 2000,
        include_history: true
      });
      claimsData = Array.isArray(historyResponse?.claims) ? historyResponse.claims : [];
    } catch (error) {
      primaryLoadError = error;
      console.error('Failed to load report claims from approval history:', error);
    }

    // Secondary fallback:
    // keep support for dedicated reports endpoint when history source is unavailable.
    if (claimsData.length === 0) {
      try {
        const response = await claimAPI.getReportClaims({ page: 1, limit: 2000 });
        const reportClaims = Array.isArray(response?.claims) ? response.claims : [];
        if (reportClaims.length > 0) {
          claimsData = reportClaims;
        }
      } catch (fallbackError) {
        console.error('Failed to load fallback report claims:', fallbackError);
      }
    }

    try {
      const transformedClaims = claimsData.map(claim => {
        const legacyManagerReturn = isLegacyManagerReturn(claim);
        const financeApprovalDateRaw = claim.finance_approval_date ||
          ((claim.finance_approval_status && claim.finance_approval_status !== 'pending') ? claim.last_modified_date : null);
        const financeView = resolveFinanceApprovalView(claim, legacyManagerReturn, financeApprovalDateRaw);
        return ({
          claimId: claim.id,
          userId: claim.user_id,
          id: claim.claim_number,
          monthYear: claim.month_year,
          entity: claim.entity,
          department: claim.department,
          projNo: claim.proj_no || '',
          amount: resolveClaimAmount(claim),
          manager: resolveManagerName(claim),
          managerApproval: mapManagerApproval(claim.manager_approval_status, claim.finance_approval_status, claim.status, claim),
          managerApprovalDate: (claim.manager_approval_date || (legacyManagerReturn ? claim.finance_approval_date : null)) ? new Date(claim.manager_approval_date || (legacyManagerReturn ? claim.finance_approval_date : null)).toLocaleString('en-MY', { timeZone: 'Asia/Kuala_Lumpur', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : null,
          managerRejectionReason: claim.manager_rejection_reason || '',
          managerReturnReason: claim.status === 'returned' ? (claim.manager_rejection_reason || (legacyManagerReturn ? (claim.finance_action_reason || '') : '')) : '',
          financeApproval: financeView.approval,
          financeApprovalDate: financeView.approvalDate,
          financeApprover: financeView.approver,
          financeReturnReason: financeView.returnReason,
          financeRejectionReason: financeView.rejectionReason,
          submissionDate: claim.submission_date ? new Date(claim.submission_date).toLocaleString('en-MY', { timeZone: 'Asia/Kuala_Lumpur', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : null,
          lastActionDate: (claim.last_action_at || claim.last_modified_date) ? new Date(claim.last_action_at || claim.last_modified_date).toLocaleString('en-MY', { timeZone: 'Asia/Kuala_Lumpur', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : null,
          submissionDateRaw: claim.submission_date || null,
          lastActionDateRaw: claim.last_action_at || claim.last_modified_date || null,
          manager_id_snapshot: claim.manager_id_snapshot ?? null,
          status: mapClaimStatus(claim.status, claim.submission_date),
          rawStatus: claim.status || 'draft',
          claimant: claim.requestor_name || 'Unknown',
          requestor: claim.requestor_name || 'Unknown',
          submitter: claim.requestor_name || 'Unknown',
          lastActor: resolveLastActor(claim, 'Unknown'),
          remarks: claim.remarks || '',
          items: claim.items || []
        });
      });

      setReportClaims(transformedClaims.filter((claim) => !isDraftClaim(claim)));
      if (primaryLoadError && transformedClaims.length === 0) {
        alert('Failed to load report claims. Please try again.');
      }
    } catch (transformError) {
      console.error('Failed to transform report claims:', transformError);
      setReportClaims([]);
      alert('Failed to process report claims. Please try again.');
    }
  };

  const openLocalAttachmentPreview = (file, fallbackName = 'attachment') => {
    try {
      if (!(file instanceof File)) return;
      const objectUrl = URL.createObjectURL(file);
      openAttachmentPreview(objectUrl, file.name || fallbackName);
      // Revoke after opening to avoid leaking object URLs.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (error) {
      console.error('Failed to preview local attachment:', error);
      alert('Unable to open attachment preview.');
    }
  };

  const loadUsers = async (page = userCurrentPage, options = {}) => {
    try {
      setUserIsLoading(true);
      const response = await userAPI.getAllUsers(page, 25, {
        search: options.search ?? debouncedUserSearchQuery,
        entity: options.entity ?? userEntityFilter,
      });
      setUserList(response.users || []);
      setUserCurrentPage(response.page || page || 1);
      setUserTotalPages(Math.max(response.totalPages || 1, 1));
      setUserTotalCount(response.total || 0);
    } catch (error) {
      console.error('Failed to load users:', error);
      alert('Failed to load users. Please try again.');
    } finally {
      setUserIsLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await companyAPI.getCompanies();
      console.log('Companies API response:', response);
      const list = response?.companies || [];
      setCompanies(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  const loadDepartments = async () => {
    try {
      const response = await departmentAPI.getDepartments();
      console.log('Departments API response:', response);
      const list = response?.departments || [];
      setDepartments(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Failed to load departments:', error);
    }
  };

  const loadExpenseGlCodes = async (options = {}) => {
    try {
      const response = await expenseGlCodeAPI.getExpenseGlCodes({
        include_inactive: options.includeInactive !== false
      });
      const items = Array.isArray(response?.items) ? response.items : [];
      const normalizedItems = items.map((item) => ({
        ...item,
        is_active: isActiveFlag(item.is_active, true)
      }));
      setExpenseGlCodes(normalizedItems);
      setExpenseGlCodeLoadError('');

      const nextMap = {};
      normalizedItems.filter(item => item.is_active).forEach((item) => {
        const entityKey = normalizeEntityName(item.entity);
        if (!nextMap[entityKey]) nextMap[entityKey] = {};
        nextMap[entityKey][normalizeExpenseType(item.expense_type)] = item.gl_code;
      });
      setExpenseGlCodeMap(nextMap);
    } catch (error) {
      console.error('Failed to load expense GL codes:', error);
      setExpenseGlCodes([]);
      setExpenseGlCodeMap({});
      setExpenseGlCodeLoadError(error.message || 'Failed to load GL codes.');
    }
  };

  const loadExpenses = async () => {
    try {
      const response = await expenseAPI.getExpenses({ include_inactive: true });
      const items = Array.isArray(response?.items) ? response.items : [];
      const normalizedItems = items.map((item) => ({
        ...item,
        is_active: isActiveFlag(item.is_active, true)
      }));
      setExpenseItems(normalizedItems);

      const nextMap = {};
      normalizedItems.filter(item => item.is_active).forEach((item) => {
        const entityKey = normalizeEntityName(item.entity);
        if (!nextMap[entityKey]) nextMap[entityKey] = {};
        if (!nextMap[entityKey][item.claim_type]) nextMap[entityKey][item.claim_type] = [];
        if (!nextMap[entityKey][item.claim_type].includes(item.expense_type)) {
          nextMap[entityKey][item.claim_type].push(item.expense_type);
        }
      });
      setExpenseOptionsMap(nextMap);
    } catch (error) {
      console.error('Failed to load expenses:', error);
    }
  };

  const getLiveBuildSignature = async () => {
    try {
      const response = await fetch(`/asset-manifest.json?ts=${Date.now()}`, {
        cache: 'no-store'
      });
      if (!response.ok) return '';
      const manifest = await response.json();
      return String(
        manifest?.files?.['main.js'] ||
        manifest?.entrypoints?.find((entry) => String(entry).includes('/static/js/main.')) ||
        ''
      ).trim();
    } catch (error) {
      console.warn('Failed to check live build signature:', error);
      return '';
    }
  };

  const checkForFrontendUpdate = async () => {
    if (buildReloadTriggeredRef.current) return;

    const currentSignature = buildSignatureRef.current ||
      String(document.querySelector('script[src*="/static/js/main."]')?.getAttribute('src') || '').trim();
    if (!currentSignature) return;
    if (!buildSignatureRef.current) {
      buildSignatureRef.current = currentSignature;
    }

    const liveSignature = await getLiveBuildSignature();
    if (!liveSignature || liveSignature === buildSignatureRef.current) return;

    buildReloadTriggeredRef.current = true;
    window.location.reload();
  };

  const normalizeWebhookItem = (item, idx = 0) => {
    const itemId = item.id || item.webhook_id || item.uuid || `webhook-${idx}`;
    const itemUrl = item.webhook_url || item.url || '';
    const isActive = typeof item.is_active === 'boolean'
      ? item.is_active
      : item.status
        ? item.status === 'active'
        : true;
    return {
      id: itemId,
      url: itemUrl,
      is_active: isActive,
      created_at: item.created_at || item.updated_at || new Date().toISOString()
    };
  };

  const loadWebhookConfig = async () => {
    setWebhookLoading(true);
    try {
      const response = await webhookAPI.getWebhooks();
      const webhookList = Array.isArray(response)
        ? response
        : Array.isArray(response?.webhooks)
          ? response.webhooks
          : [];
      const normalized = webhookList.map((item, idx) => normalizeWebhookItem(item, idx));
      setWebhookList(normalized);
    } catch (error) {
      console.error('Failed to load webhooks:', error);
      setWebhookList([]);
      alert('Failed to load webhooks from server.');
    } finally {
      setWebhookLoading(false);
    }
  };

  const handleGenerateWebhookUrl = async () => {
    setWebhookLoading(true);
    try {
      const response = await webhookAPI.createWebhook();
      const serverUrl = response?.webhook_url || response?.url || '';
      const nextWebhook = {
        id: response?.id || `webhook-${Date.now()}`,
        url: serverUrl,
        is_active: true,
        created_at: new Date().toISOString()
      };
      if (serverUrl) {
        setWebhookList((prev) => [nextWebhook, ...prev]);
        alert('Webhook URL generated successfully.');
        return;
      }
      alert('Webhook generated but no URL was returned by server.');
    } catch (error) {
      console.error('Failed to generate webhook URL:', error);
      alert(error.message || 'Failed to generate webhook URL from server.');
    } finally {
      setWebhookLoading(false);
    }
  };

  const handleSyncExistingWebhooks = async () => {
    if (!currentUser?.id) {
      alert('Please login again and try sync.');
      return;
    }

    const legacyKey = `tmg_webhook_config_${currentUser.id}`;
    const globalLegacyKey = 'tmg_webhook_config_global';
    const raw = localStorage.getItem(legacyKey) || localStorage.getItem(globalLegacyKey);

    if (!raw) {
      alert('No legacy local webhooks found to sync.');
      return;
    }

    let legacyCount = 0;
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        legacyCount = parsed.length;
      } else if (parsed && typeof parsed === 'object') {
        legacyCount = Object.keys(parsed).length;
      }
    } catch (error) {
      console.error('Failed to parse legacy webhook data:', error);
      alert('Legacy webhook format is invalid.');
      return;
    }

    if (legacyCount <= 0) {
      alert('No legacy local webhooks found to sync.');
      return;
    }

    setWebhookLoading(true);
    try {
      for (let idx = 0; idx < legacyCount; idx += 1) {
        await webhookAPI.createWebhook();
      }
      await loadWebhookConfig();
      localStorage.removeItem(legacyKey);
      localStorage.removeItem(globalLegacyKey);
      alert(`Synced ${legacyCount} webhook(s) to database.`);
    } catch (error) {
      console.error('Failed to sync legacy webhooks:', error);
      alert(error.message || 'Failed to sync legacy webhooks.');
    } finally {
      setWebhookLoading(false);
    }
  };

  const handleCopyWebhookUrl = async (url) => {
    if (!url) {
      alert('Generate a webhook URL first.');
      return;
    }

    try {
      await navigator.clipboard.writeText(url);
      alert('Webhook URL copied.');
    } catch (error) {
      console.error('Failed to copy webhook URL:', error);
      alert('Failed to copy. Please copy manually.');
    }
  };

  const handleToggleWebhookStatus = async (webhook) => {
    const nextStatus = !webhook.is_active;
    setWebhookActionLoadingId(webhook.id);
    try {
      await webhookAPI.updateWebhookStatus(webhook.id, nextStatus);
      setWebhookList((prev) => prev.map((item) => item.id === webhook.id ? { ...item, is_active: nextStatus } : item));
    } catch (error) {
      console.error('Failed to update webhook status:', error);
      alert(error.message || 'Failed to update webhook status.');
    } finally {
      setWebhookActionLoadingId('');
    }
  };

  const handleDeleteWebhook = async (webhook) => {
    if (!window.confirm('Delete this webhook URL?')) return;
    setWebhookActionLoadingId(webhook.id);
    try {
      await webhookAPI.deleteWebhook(webhook.id);
      setWebhookList((prev) => prev.filter((item) => item.id !== webhook.id));
    } catch (error) {
      console.error('Failed to delete webhook:', error);
      alert(error.message || 'Failed to delete webhook.');
    } finally {
      setWebhookActionLoadingId('');
    }
  };

  const handleOpenCompanyModal = (company = null) => {
    if (company) {
      setEditingCompany(company);
      setCompanyFormData({ full_name: company.full_name, code: company.code });
    } else {
      setEditingCompany(null);
      setCompanyFormData({ full_name: '', code: '' });
    }
    setShowCompanyModal(true);
  };

  const handleSaveCompany = async () => {
    if (!companyFormData.full_name || !companyFormData.code) {
      alert('Please fill in both Full Name and Code.');
      return;
    }
    if (companyFormData.code.length < 3 || companyFormData.code.length > 4) {
      alert('Code must be 3 to 4 characters.');
      return;
    }
    try {
      if (editingCompany) {
        await companyAPI.updateCompany(editingCompany.id, companyFormData);
        alert('Company updated successfully.');
      } else {
        await companyAPI.createCompany(companyFormData);
        alert('Company created successfully.');
      }
      setShowCompanyModal(false);
      loadCompanies();
    } catch (error) {
      console.error('Failed to save company:', error);
      alert(error.message || 'Failed to save company.');
    }
  };

  const handleToggleCompanyActive = async (company) => {
    const action = company.is_active ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} "${company.full_name}"?`)) return;
    try {
      await companyAPI.updateCompany(company.id, { is_active: !company.is_active });
      loadCompanies();
    } catch (error) {
      console.error(`Failed to ${action} company:`, error);
      alert(error.message || `Failed to ${action} company.`);
    }
  };

  const handleOpenDepartmentModal = (dept = null) => {
    if (dept) {
      setEditingDepartment(dept);
      setDepartmentFormData({ name: dept.name });
    } else {
      setEditingDepartment(null);
      setDepartmentFormData({ name: '' });
    }
    setShowDepartmentModal(true);
  };

  const handleSaveDepartment = async () => {
    if (!departmentFormData.name) {
      alert('Please enter a department name.');
      return;
    }
    try {
      if (editingDepartment) {
        await departmentAPI.updateDepartment(editingDepartment.id, departmentFormData);
        alert('Department updated successfully.');
      } else {
        await departmentAPI.createDepartment(departmentFormData);
        alert('Department created successfully.');
      }
      setShowDepartmentModal(false);
      loadDepartments();
    } catch (error) {
      console.error('Failed to save department:', error);
      alert(error.message || 'Failed to save department.');
    }
  };

  const handleToggleDepartmentActive = async (dept) => {
    const action = dept.is_active ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} "${dept.name}"?`)) return;
    try {
      await departmentAPI.updateDepartment(dept.id, { is_active: !dept.is_active });
      loadDepartments();
    } catch (error) {
      console.error(`Failed to ${action} department:`, error);
      alert(error.message || `Failed to ${action} department.`);
    }
  };

  const handleOpenExpenseGlCodeModal = (item = null) => {
    if (item) {
      const resolvedEntity = resolveActiveCompanyEntityName(item.entity || '');
      const resolvedClaimType = item.claim_type || inferExpenseClaimType(resolvedEntity, item.expense_type || '');
      setEditingExpenseGlCode(item);
      setExpenseGlCodeFormData({
        entity: resolvedEntity,
        claim_type: resolvedClaimType,
        expense_type: item.expense_type || '',
        gl_code: item.gl_code || '',
        entitlement_type: getDisplayedEntitlementType({
          claimType: resolvedClaimType,
          expenseType: item.expense_type || '',
          storedValue: item.entitlement_type
        }),
        is_active: item.is_active !== undefined ? !!item.is_active : true
      });
    } else {
      setEditingExpenseGlCode(null);
      setExpenseGlCodeFormData({
        entity: '',
        claim_type: '',
        expense_type: '',
        gl_code: '',
        entitlement_type: 'monthly',
        is_active: true
      });
    }
    setInlineExpenseTypeFormData({
      entity: resolveActiveCompanyEntityName(item?.entity || ''),
      claim_type: 'Projects',
      expense_type: '',
      is_active: true
    });
    setShowExpenseGlCodeModal(true);
  };

  const handleOpenInlineExpenseTypeModal = () => {
    setInlineExpenseTypeFormData((prev) => ({
      entity: expenseGlCodeFormData.entity || prev.entity || '',
      claim_type: prev.claim_type || 'Projects',
      expense_type: '',
      is_active: true
    }));
    setShowInlineExpenseTypeModal(true);
  };

  const handleSaveExpenseGlCode = async () => {
    if (!expenseGlCodeFormData.entity || !expenseGlCodeFormData.claim_type || !expenseGlCodeFormData.expense_type || !expenseGlCodeFormData.gl_code) {
      alert('Please fill in Entity, Claim Type, Expense Type, and GL Code.');
      return;
    }

    try {
      if (editingExpenseGlCode) {
        await expenseGlCodeAPI.updateExpenseGlCode(editingExpenseGlCode.id, expenseGlCodeFormData);
        alert('Expense GL code updated successfully.');
      } else {
        await expenseGlCodeAPI.createExpenseGlCode(expenseGlCodeFormData);
        alert('Expense GL code created successfully.');
      }
      setShowInlineExpenseTypeModal(false);
      setShowExpenseGlCodeModal(false);
      loadExpenseGlCodes();
    } catch (error) {
      console.error('Failed to save expense GL code:', error);
      alert(error.message || 'Failed to save expense GL code.');
    }
  };

  const handleSaveInlineExpenseType = async () => {
    if (!inlineExpenseTypeFormData.entity || !inlineExpenseTypeFormData.claim_type || !String(inlineExpenseTypeFormData.expense_type || '').trim()) {
      alert('Please fill in Entity, Claim Type, and Expense Type.');
      return;
    }

    const payload = {
      entity: inlineExpenseTypeFormData.entity,
      claim_type: inlineExpenseTypeFormData.claim_type,
      expense_type: String(inlineExpenseTypeFormData.expense_type || '').trim(),
      is_active: inlineExpenseTypeFormData.is_active !== false
    };

    try {
      await expenseAPI.createExpense(payload);
      await loadExpenses();
      setExpenseGlCodeFormData((prev) => ({
        ...prev,
        entity: prev.entity || payload.entity,
        claim_type: payload.claim_type,
        expense_type: payload.expense_type
      }));
      setShowInlineExpenseTypeModal(false);
      alert('Expense type created. You can now continue adding the GL code.');
    } catch (error) {
      console.error('Failed to create inline expense type:', error);
      alert(error.message || 'Failed to create expense type.');
    }
  };

  const handleToggleExpenseGlCodeActive = async (item) => {
    const action = item.is_active ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} this GL code?`)) return;
    try {
      await expenseGlCodeAPI.updateExpenseGlCode(item.id, { is_active: !item.is_active });
      loadExpenseGlCodes();
    } catch (error) {
      console.error(`Failed to ${action} expense GL code:`, error);
      alert(error.message || `Failed to ${action} expense GL code.`);
    }
  };

  const handleOpenExpenseModal = (item = null) => {
    if (item) {
      setEditingExpense(item);
      setExpenseFormData({
        entity: item.entity || '',
        claim_type: item.claim_type || 'Projects',
        expense_type: item.expense_type || '',
        is_active: item.is_active !== undefined ? !!item.is_active : true
      });
    } else {
      setEditingExpense(null);
      setExpenseFormData({
        entity: '',
        claim_type: 'Projects',
        expense_type: '',
        is_active: true
      });
    }
    setShowExpenseModal(true);
  };

  const handleSaveExpense = async () => {
    if (!expenseFormData.entity || !expenseFormData.claim_type || !expenseFormData.expense_type) {
      alert('Please fill in Entity, Claim Type, and Expense Type.');
      return;
    }

    try {
      if (editingExpense) {
        await expenseAPI.updateExpense(editingExpense.id, expenseFormData);
        alert('Expense updated successfully.');
      } else {
        await expenseAPI.createExpense(expenseFormData);
        alert('Expense created successfully.');
      }
      setShowExpenseModal(false);
      loadExpenses();
    } catch (error) {
      console.error('Failed to save expense:', error);
      alert(error.message || 'Failed to save expense.');
    }
  };

  const handleToggleExpenseActive = async (item) => {
    const action = item.is_active ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} this expense?`)) return;
    try {
      await expenseAPI.updateExpense(item.id, { is_active: !item.is_active });
      loadExpenses();
    } catch (error) {
      console.error(`Failed to ${action} expense:`, error);
      alert(error.message || `Failed to ${action} expense.`);
    }
  };

  const handleOpenUserModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setUserFormData({
        email: user.email || '',
        full_legal_name: buildFullLegalName(user.first_name, user.last_name, user.full_name),
        phone: user.phone || '',
        position: user.position || '',
        join_date: user.join_date ? String(user.join_date).slice(0, 10) : '',
        resign_date: user.resign_date ? String(user.resign_date).slice(0, 10) : '',
        entity: user.entity || DEFAULT_ENTITY_NAME,
        department: user.department || '',
        proj_no: user.proj_no || '',
        role: user.role === 'admin' ? 'admin' : 'user',
        manage_user_access: Boolean(user.manage_user_access),
        manage_company_access: Boolean(user.manage_company_access),
        manage_expense_gl_code_access: Boolean(user.manage_expense_gl_code_access),
        manager_id: user.manager_id || '',
        is_active: user.is_active !== undefined ? user.is_active : true,
        monthly_entitlement: user.monthly_entitlement ?? '0.00',
        yearly_entitlement: user.yearly_entitlement ?? '0.00'
      });
      setReportToSearch(user.manager_name || '');
    } else {
      setEditingUser(null);
      setUserFormData({
        email: '',
        full_legal_name: '',
        phone: '',
        position: '',
        join_date: '',
        resign_date: '',
        entity: resolveDefaultEntityForUserForm(),
        department: '',
        proj_no: '',
        role: 'user',
        manage_user_access: false,
        manage_company_access: false,
        manage_expense_gl_code_access: false,
        manager_id: '',
        is_active: true,
        monthly_entitlement: '0.00',
        yearly_entitlement: '0.00'
      });
      setReportToSearch('');
    }
    setShowReportToDropdown(false);
    setShowUserModal(true);
  };

  const resetUserImportState = () => {
    setShowUserImportModal(false);
    setUserImportStep('mapping');
    setUserImportFileName('');
    setUserImportColumns([]);
    setUserImportRows([]);
    setUserImportMappings({});
    setUserImportResult(null);
    setUserImportError('');
    setUserImportStatus('Ready to import');
    setUserImportIsProcessing(false);
    if (userImportFileInputRef.current) {
      userImportFileInputRef.current.value = '';
    }
  };

  const handleOpenUserImportPicker = () => {
    setUserImportError('');
    if (userImportFileInputRef.current) {
      userImportFileInputRef.current.click();
    }
  };

  const handleUserImportFileSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUserImportIsProcessing(true);
      setUserImportStatus('Reading Excel file...');
      setUserImportError('');

      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
      const preferredSheetName = workbook.SheetNames.find((name) =>
        normalizeImportHeader(name).includes('master user list')
      ) || workbook.SheetNames[0];

      const worksheet = workbook.Sheets[preferredSheetName];
      const sheetRows = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: '',
        blankrows: false,
        raw: false
      });

      const headerRowIndex = findBestImportHeaderRow(sheetRows);
      const rawHeaders = sheetRows[headerRowIndex] || [];
      const rawHeadersUnique = buildUniqueHeaders(rawHeaders);
      const rawDataRows = sheetRows.slice(headerRowIndex + 1)
        .map((row, index) => {
          const rowObject = rawHeadersUnique.reduce((acc, header, columnIndex) => {
            acc[header] = row?.[columnIndex] ?? '';
            return acc;
          }, {});
          rowObject.__rowNumber = headerRowIndex + 2 + index;
          return rowObject;
        })
        .filter((row) => rawHeadersUnique.some((header) => String(row[header] || '').trim()));

      const { headers, rows: dataRows } = buildUserImportDataset(rawHeadersUnique, rawDataRows);

      if (!headers.length || !dataRows.length) {
        throw new Error('No usable rows found in the selected Excel file.');
      }

      setUserImportFileName(file.name);
      setUserImportColumns(headers);
      setUserImportRows(dataRows);
      setUserImportMappings(inferUserImportMappings(headers));
      setUserImportStep('mapping');
      setShowUserImportModal(true);
      setUserImportStatus(`Loaded ${dataRows.length} rows from ${preferredSheetName}`);
    } catch (error) {
      console.error('Failed to parse import file:', error);
      setUserImportError(error.message || 'Failed to read the Excel file.');
      setShowUserImportModal(true);
    } finally {
      setUserImportIsProcessing(false);
    }
  };

  const handleRunUserImport = async () => {
    const missingRequired = USER_IMPORT_FIELD_DEFINITIONS
      .filter((field) => field.required)
      .filter((field) => !userImportMappings[field.key]);

    if (missingRequired.length > 0) {
      setUserImportError(`Please map the required fields: ${missingRequired.map((field) => field.label).join(', ')}`);
      return;
    }

    try {
      setUserImportIsProcessing(true);
      setUserImportError('');
      setUserImportStep('progress');
      setUserImportStatus('Uploading rows and processing create/update actions...');

      const result = await userAPI.importUsers({
        mappings: userImportMappings,
        rows: userImportRows
      });

      setUserImportStatus('Import completed');
      setUserImportResult(result);
      setUserImportStep('result');
      await loadUsers();
    } catch (error) {
      console.error('Failed to import users:', error);
      setUserImportError(error.message || 'Failed to import users.');
      setUserImportStep('result');
    } finally {
      setUserImportIsProcessing(false);
    }
  };

  const userImportSelectableColumns = useMemo(
    () => getRelevantUserImportColumns(userImportColumns),
    [userImportColumns]
  );

  const userImportPreviewColumns = useMemo(
    () => getUserImportPreviewColumns(userImportColumns),
    [userImportColumns]
  );

  const handleSaveUser = async () => {
    // Validation
    if (!userFormData.email || !userFormData.full_legal_name || !userFormData.phone || !userFormData.entity || !userFormData.role) {
      alert('Please fill in all required fields: Email, Full Legal Name, Phone, Entity, and Role.');
      return;
    }
    if (!editingUser && !userFormData.join_date) {
      alert('Please fill in required field: Join Date.');
      return;
    }

    if (!userFormData.manager_id) {
      alert('Please select a Report To person.');
      return;
    }

    try {
      const { first_name, last_name } = splitFullLegalName(userFormData.full_legal_name);
      const payload = {
        ...userFormData,
        phone: normalizeMalaysiaPhone(userFormData.phone),
        first_name,
        last_name,
        full_name: userFormData.full_legal_name,
        role: userFormData.role === 'admin' ? 'admin' : 'user',
        manage_user_access: Boolean(userFormData.manage_user_access),
        manage_company_access: Boolean(userFormData.manage_company_access),
        manage_expense_gl_code_access: Boolean(userFormData.manage_expense_gl_code_access)
      };
      if (editingUser) {
        await userAPI.updateUser(editingUser.id, payload);
        if (Number(editingUser.id) === Number(currentUser?.id)) {
          await refreshCurrentUser();
        }
        alert('User updated successfully.');
      } else {
        await userAPI.createUser(payload);
        alert('User created successfully.');
      }
      setShowUserModal(false);
      loadUsers();
    } catch (error) {
      console.error('Failed to save user:', error);
      alert(error.message || 'Failed to save user.');
    }
  };

  // Deactivation reassignment states
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [deactivatingUser, setDeactivatingUser] = useState(null);
  const [reassignToUserId, setReassignToUserId] = useState('');

  const deepLinkProcessedRef = useRef(false);
  const skipUrlSyncRef = useRef(false);
  const desktopTableContainerRef = useRef(null);
  const desktopTableBottomTrackRef = useRef(null);
  const desktopTableBottomThumbRef = useRef(null);
  const desktopTableDragStateRef = useRef({
    isDragging: false,
    startX: 0,
    startScrollLeft: 0,
    pointerId: null,
    sourceNode: null
  });
  const desktopTableBottomThumbDragRef = useRef({
    isDragging: false,
    startX: 0,
    startScrollLeft: 0,
    pointerId: null
  });
  const [desktopTableHasOverflow, setDesktopTableHasOverflow] = useState(false);
  const [desktopTableScrollWidth, setDesktopTableScrollWidth] = useState(0);
  const [desktopTableClientWidth, setDesktopTableClientWidth] = useState(0);
  const [desktopTableScrollLeft, setDesktopTableScrollLeft] = useState(0);
  const [isDesktopTableDragging, setIsDesktopTableDragging] = useState(false);
  const [isDesktopBottomThumbDragging, setIsDesktopBottomThumbDragging] = useState(false);
  const [subordinatesOfDeactivating, setSubordinatesOfDeactivating] = useState([]);

  const updateDesktopTableScrollState = useCallback(() => {
    const container = desktopTableContainerRef.current;
    if (!container) {
      setDesktopTableHasOverflow(false);
      setDesktopTableScrollWidth(0);
      setDesktopTableClientWidth(0);
      return;
    }

    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    const hasOverflow = maxScrollLeft > 1;
    const scrollLeft = container.scrollLeft;
    setDesktopTableHasOverflow(hasOverflow);
    setDesktopTableScrollWidth(container.scrollWidth);
    setDesktopTableClientWidth(container.clientWidth);
    setDesktopTableScrollLeft(scrollLeft);
  }, []);

  useEffect(() => {
    const isDesktopViewActive = activeView === 'report' || activeView === 'dashboard' || activeView === 'approval' || activeView === 'my-claims';
    if (!isDesktopViewActive) {
      setDesktopTableHasOverflow(false);
      setDesktopTableScrollWidth(0);
      setDesktopTableClientWidth(0);
      setDesktopTableScrollLeft(0);
      return;
    }

    const container = desktopTableContainerRef.current;
    if (!container) {
      setDesktopTableHasOverflow(false);
      setDesktopTableScrollWidth(0);
      setDesktopTableClientWidth(0);
      setDesktopTableScrollLeft(0);
      return;
    }

    const handleScrollUpdate = () => updateDesktopTableScrollState();
    const isInteractiveElement = (node) => {
      if (!node || typeof node.closest !== 'function') return false;
      return Boolean(node.closest('button, a, input, select, textarea, label, [role="button"]'));
    };
    const getPointerX = (event) => {
      if (typeof event.clientX === 'number') return event.clientX;
      if (event.touches && event.touches[0] && typeof event.touches[0].clientX === 'number') {
        return event.touches[0].clientX;
      }
      return 0;
    };
    const clearSelection = () => {
      if (typeof window === 'undefined' || !window.getSelection) return;
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        selection.removeAllRanges();
      }
    };
    const applyDraggingClass = (isDragging) => {
      if (typeof document === 'undefined') return;
      document.body.classList.toggle('desktop-table-dragging', Boolean(isDragging));
    };
    const beginDrag = (event, sourceNode) => {
      if (!container) return;
      const isLeftMouseButton = event.pointerType !== 'mouse' || event.button === 0;
      if (!isLeftMouseButton) return;
      if ((container.scrollWidth - container.clientWidth) <= 1) return;
      if (sourceNode === container && isInteractiveElement(event.target)) return;

      desktopTableDragStateRef.current = {
        isDragging: true,
        startX: getPointerX(event),
        startScrollLeft: container.scrollLeft,
        pointerId: event.pointerId ?? null,
        sourceNode: sourceNode || container
      };
      setIsDesktopTableDragging(true);
      applyDraggingClass(true);
      clearSelection();
      if (sourceNode?.setPointerCapture && event.pointerId !== undefined) {
        try {
          sourceNode.setPointerCapture(event.pointerId);
        } catch {
          // Ignore pointer capture failures for unsupported environments.
        }
      }
      event.preventDefault();
    };
    const handlePointerMove = (event) => {
      const dragState = desktopTableDragStateRef.current;
      if (!dragState.isDragging || !container) return;
      const delta = getPointerX(event) - dragState.startX;
      container.scrollLeft = dragState.startScrollLeft - delta;
      clearSelection();
      event.preventDefault();
    };
    const endDrag = () => {
      const dragState = desktopTableDragStateRef.current;
      if (!dragState.isDragging) return;
      if (dragState.sourceNode?.releasePointerCapture && dragState.pointerId !== null) {
        try {
          dragState.sourceNode.releasePointerCapture(dragState.pointerId);
        } catch {
          // Ignore pointer release failures for unsupported environments.
        }
      }
      desktopTableDragStateRef.current = {
        isDragging: false,
        startX: 0,
        startScrollLeft: 0,
        pointerId: null,
        sourceNode: null
      };
      setIsDesktopTableDragging(false);
      applyDraggingClass(false);
    };
    const handleContainerPointerDown = (event) => beginDrag(event, container);
    const handleDragStart = (event) => event.preventDefault();
    const rafId = window.requestAnimationFrame(handleScrollUpdate);

    container.addEventListener('scroll', handleScrollUpdate, { passive: true });
    window.addEventListener('resize', handleScrollUpdate);
    container.addEventListener('pointerdown', handleContainerPointerDown);
    container.addEventListener('dragstart', handleDragStart);
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);

    let resizeObserver;
    if (typeof window !== 'undefined' && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(handleScrollUpdate);
      resizeObserver.observe(container);
      const tableNode = container.querySelector('table');
      if (tableNode) resizeObserver.observe(tableNode);
    }

    return () => {
      window.cancelAnimationFrame(rafId);
      container.removeEventListener('scroll', handleScrollUpdate);
      window.removeEventListener('resize', handleScrollUpdate);
      container.removeEventListener('pointerdown', handleContainerPointerDown);
      container.removeEventListener('dragstart', handleDragStart);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
      if (resizeObserver) resizeObserver.disconnect();
      endDrag();
      applyDraggingClass(false);
    };
  }, [activeView, dashboardTab, claims.length, updateDesktopTableScrollState]);

  const desktopBottomThumbWidth = desktopTableHasOverflow
    ? Math.max(64, (desktopTableClientWidth / Math.max(desktopTableScrollWidth, 1)) * desktopTableClientWidth)
    : 0;
  const desktopBottomThumbTravel = Math.max(desktopTableClientWidth - desktopBottomThumbWidth, 0);
  const desktopBottomScrollableDistance = Math.max(desktopTableScrollWidth - desktopTableClientWidth, 1);
  const desktopBottomThumbLeft = desktopTableHasOverflow
    ? Math.min(
      desktopBottomThumbTravel,
      Math.max(0, (desktopTableScrollLeft / desktopBottomScrollableDistance) * desktopBottomThumbTravel)
    )
    : 0;

  const handleDesktopBottomTrackPointerDown = (event) => {
    const container = desktopTableContainerRef.current;
    const track = desktopTableBottomTrackRef.current;
    if (!container || !track || !desktopTableHasOverflow) return;
    const rect = track.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const targetThumbLeft = Math.max(0, Math.min(desktopBottomThumbTravel, clickX - (desktopBottomThumbWidth / 2)));
    const targetRatio = desktopBottomThumbTravel > 0 ? (targetThumbLeft / desktopBottomThumbTravel) : 0;
    container.scrollLeft = targetRatio * desktopBottomScrollableDistance;
    event.preventDefault();
  };

  const handleDesktopBottomThumbPointerDown = (event) => {
    if (!desktopTableHasOverflow) return;
    desktopTableBottomThumbDragRef.current = {
      isDragging: true,
      startX: event.clientX,
      startScrollLeft: desktopTableScrollLeft,
      pointerId: event.pointerId ?? null
    };
    setIsDesktopBottomThumbDragging(true);
    if (desktopTableBottomThumbRef.current?.setPointerCapture && event.pointerId !== undefined) {
      try {
        desktopTableBottomThumbRef.current.setPointerCapture(event.pointerId);
      } catch {
        // Ignore pointer capture failures.
      }
    }
    event.stopPropagation();
    event.preventDefault();
  };

  const handleDesktopBottomThumbPointerMove = (event) => {
    const dragState = desktopTableBottomThumbDragRef.current;
    const container = desktopTableContainerRef.current;
    if (!dragState.isDragging || !container) return;
    const deltaX = event.clientX - dragState.startX;
    const ratio = desktopBottomThumbTravel > 0 ? (deltaX / desktopBottomThumbTravel) : 0;
    const nextScrollLeft = dragState.startScrollLeft + (ratio * desktopBottomScrollableDistance);
    container.scrollLeft = Math.max(0, Math.min(desktopBottomScrollableDistance, nextScrollLeft));
    event.preventDefault();
  };

  const handleDesktopBottomThumbPointerUp = () => {
    const dragState = desktopTableBottomThumbDragRef.current;
    if (!dragState.isDragging) return;
    if (desktopTableBottomThumbRef.current?.releasePointerCapture && dragState.pointerId !== null) {
      try {
        desktopTableBottomThumbRef.current.releasePointerCapture(dragState.pointerId);
      } catch {
        // Ignore pointer release failures.
      }
    }
    desktopTableBottomThumbDragRef.current = {
      isDragging: false,
      startX: 0,
      startScrollLeft: 0,
      pointerId: null
    };
    setIsDesktopBottomThumbDragging(false);
  };

  const handleToggleUserActive = async (user) => {
    if (user.is_active) {
      // Deactivating - check for subordinates
      const subordinates = userList.filter(u => u.manager_id === user.id && u.is_active);
      if (subordinates.length > 0) {
        setDeactivatingUser(user);
        setSubordinatesOfDeactivating(subordinates);
        setReassignToUserId('');
        setShowReassignModal(true);
        return;
      }
      if (!window.confirm(`Are you sure you want to deactivate ${user.full_name}?`)) return;
      try {
        await userAPI.updateUser(user.id, { is_active: false });
        loadUsers();
      } catch (error) {
        console.error('Failed to deactivate user:', error);
        alert(error.message || 'Failed to deactivate user.');
      }
    } else {
      // Activating
      if (!window.confirm(`Are you sure you want to activate ${user.full_name}?`)) return;
      try {
        await userAPI.updateUser(user.id, { is_active: true });
        loadUsers();
      } catch (error) {
        console.error('Failed to activate user:', error);
        alert(error.message || 'Failed to activate user.');
      }
    }
  };

  const handleUnlinkTelegram = async (user) => {
    if (!user || !user.id) return;
    if (!window.confirm(`Unlink Telegram for ${user.full_name}?`)) return;
    try {
      const response = await userAPI.unlinkTelegram(user.id);
      const removed = response?.removed_links || 0;
      alert(removed > 0 ? 'Telegram unlinked successfully.' : 'No Telegram link found for this user.');
      await loadUsers();
    } catch (error) {
      console.error('Failed to unlink Telegram:', error);
      alert(error.message || 'Failed to unlink Telegram.');
    }
  };

  const getPrimaryTelegramId = (user) => {
    const raw = String(user?.telegram_user_ids || '').trim();
    if (!raw) return '';
    const first = raw.split(',')[0]?.trim();
    return first || '';
  };

  const handleConfirmDeactivation = async () => {
    if (!reassignToUserId) {
      alert('Please select a person to reassign the subordinates to.');
      return;
    }

    try {
      // Reassign all subordinates to the new manager
      for (const sub of subordinatesOfDeactivating) {
        await userAPI.updateUser(sub.id, { manager_id: parseInt(reassignToUserId) });
      }
      // Now deactivate the user
      await userAPI.updateUser(deactivatingUser.id, { is_active: false });
      setShowReassignModal(false);
      setDeactivatingUser(null);
      loadUsers();
    } catch (error) {
      console.error('Failed to deactivate user:', error);
      alert(error.message || 'Failed to deactivate user.');
    }
  };

  const loadClaimDetails = async (claim) => {
    try {
      // Fetch full claim details from backend
      const response = await claimAPI.getClaimDetails(claim.claimId);
      const claimData = response.claim;

      // Transform and set selected claim with items
      const detailedClaim = {
        ...claim,
        items: claimData.items || [],
        remarks: claimData.remarks || claim.remarks
      };

      setSelectedClaim(detailedClaim);
    } catch (error) {
      console.error('Failed to load claim details:', error);
      // Fallback to showing claim without detailed items
      setSelectedClaim(claim);
    }
  };

  // OTP sending is handled entirely by the backend via Mandrill SMTP
  // Frontend only calls authAPI.requestOTP() which triggers the backend email service

  const handleRequestOTP = async (method) => {
    if (!email || !email.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }

    const isResend = authState === 'otp';

    if (!isResend) {
      cancelSessionRestoreRef.current = true;
      localStorage.removeItem('tmg_token');
      setCurrentUser(null);
      setClaims([]);
      setPendingApprovals([]);
    }

    if (isResend && otpResendCooldown > 0) {
      return;
    }

    try {
      if (isResend) {
        setOtpResendState('sending');
      }
      await authAPI.requestOTP(email, method);
      setOtpMethod(method);
      setAuthState('otp');
      if (isResend) {
        setOtpHasResent(true);
        setOtpResendState('success');
        setOtpResendCooldown(90);
      } else {
        setOtpHasResent(false);
        setOtpResendState('idle');
        setOtpResendCooldown(0);
      }
    } catch (error) {
      console.error('Failed to send OTP:', error);
      setOtpResendState('idle');
      alert(error.message || 'Failed to send OTP. Please try again.');
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) value = value[0];
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleVerifyOTP();
    }
  };

  const handleOtpPaste = (index, e) => {
    e.preventDefault();
    const pastedText = e.clipboardData?.getData('text') || '';
    const digits = pastedText.replace(/\D/g, '');
    if (!digits) return;

    const newOtp = [...otp];
    for (let i = 0; i < digits.length && index + i < 6; i += 1) {
      newOtp[index + i] = digits[i];
    }
    setOtp(newOtp);

    const nextIndex = Math.min(index + digits.length, 5);
    const nextInput = document.getElementById(`otp-${nextIndex}`);
    if (nextInput) nextInput.focus();
  };

  const handleVerifyOTP = async () => {
    if (isVerifyingOtp) return;

    const otpString = otp.join('');

    if (otpString.length !== 6) {
      alert('Please enter a 6-digit OTP');
      return;
    }

    try {
      setIsVerifyingOtp(true);
      const data = await authAPI.verifyOTP(email, otpString);

      // Get current user details
      await refreshCurrentUser();

      setAuthState('authenticated');
    } catch (error) {
      console.error('OTP verification failed:', error);
      alert(error.message || 'Invalid OTP. Please try again.');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleLogout = async () => {
    cancelSessionRestoreRef.current = true;
    localStorage.removeItem('tmg_token');

    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }

    setAuthState('login');
    setEmail('');
    setOtp(['', '', '', '', '', '']);
    setOtpMethod('');
    setCurrentUser(null);
    setClaims([]);
    setPendingApprovals([]);
    setDashboardTab('returned');
    setActiveView('dashboard');
    setApprovalTab('pending');
    window.history.replaceState({}, '', '/');
    deepLinkProcessedRef.current = false;
  };

  const mapClaimDetailToCard = (claim) => {
    const legacyManagerReturn = isLegacyManagerReturn(claim);
    const financeApprovalDateRaw = claim.finance_approval_date ||
      ((claim.finance_approval_status && claim.finance_approval_status !== 'pending') ? claim.last_modified_date : null);
    const financeView = resolveFinanceApprovalView(claim, legacyManagerReturn, financeApprovalDateRaw);
    return ({
      claimId: claim.id,
      userId: claim.user_id,
      id: claim.claim_number,
      monthYear: claim.month_year,
      entity: claim.entity,
      department: claim.department,
      amount: resolveClaimAmount(claim),
      manager: resolveManagerName(claim),
      managerApproval: mapManagerApproval(claim.manager_approval_status, claim.finance_approval_status, claim.status, claim),
      managerApprovalDate: (claim.manager_approval_date || (legacyManagerReturn ? claim.finance_approval_date : null)) ? new Date(claim.manager_approval_date || (legacyManagerReturn ? claim.finance_approval_date : null)).toLocaleString('en-MY', { timeZone: 'Asia/Kuala_Lumpur', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : null,
      managerRejectionReason: claim.manager_rejection_reason || '',
      managerReturnReason: claim.status === 'returned' ? (claim.manager_rejection_reason || (legacyManagerReturn ? (claim.finance_action_reason || '') : '')) : '',
      financeApproval: financeView.approval,
      financeApprovalDate: financeView.approvalDate,
      financeApprover: financeView.approver,
      financeReturnReason: financeView.returnReason,
      financeRejectionReason: financeView.rejectionReason,
      submissionDate: claim.submission_date ? new Date(claim.submission_date).toLocaleString('en-MY', { timeZone: 'Asia/Kuala_Lumpur', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : null,
      lastActionDate: (claim.last_action_at || claim.last_modified_date) ? new Date(claim.last_action_at || claim.last_modified_date).toLocaleString('en-MY', { timeZone: 'Asia/Kuala_Lumpur', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : null,
      submissionDateRaw: claim.submission_date || null,
      lastActionDateRaw: claim.last_action_at || claim.last_modified_date || null,
      manager_id_snapshot: claim.manager_id_snapshot ?? null,
      status: mapClaimStatus(claim.status, claim.submission_date),
      rawStatus: claim.status || 'draft',
      claimant: claim.requestor_name || claim.requestor || currentUser?.full_name || currentUser?.name || 'Unknown',
      requestor: claim.requestor_name || claim.requestor || currentUser?.full_name || currentUser?.name || 'Unknown',
      submitter: claim.requestor_name || claim.requestor || currentUser?.full_name || currentUser?.name || 'Unknown',
      lastActor: resolveLastActor(claim, currentUser?.full_name || currentUser?.name || 'Unknown'),
      remarks: claim.remarks || '',
      items: claim.items || []
    });
  };

  useEffect(() => {
    if (authState !== 'authenticated' || !currentUser || deepLinkProcessedRef.current) return;

    const params = new URLSearchParams(window.location.search);
    const claimIdParam = params.get('claimId');
    if (!claimIdParam) return;

    const claimId = Number(claimIdParam);
    if (!Number.isInteger(claimId) || claimId <= 0) return;

    deepLinkProcessedRef.current = true;

    const requestedView = params.get('view');
    const routePath = normalizePath(window.location.pathname);
    const hasPathDrivenView = routePath !== '/' && routePath !== '/dashboard';
    const allowedViews = ['dashboard', 'my-claims', 'approval', 'report'];
    if (!hasPathDrivenView && requestedView && allowedViews.includes(requestedView)) {
      if (requestedView === 'approval' && canAccessApprovals) {
        setActiveView('approval');
      } else if (requestedView !== 'approval') {
        setActiveView(requestedView);
      }
    }

    const openLinkedClaim = async () => {
      try {
        const response = await claimAPI.getClaimDetails(claimId);
        const detail = response?.claim;
        if (!detail) return;

        const mappedClaim = mapClaimDetailToCard(detail);
        if (isDraftClaim(mappedClaim)) return;

        if ((requestedView === 'my-claims' || !requestedView) && canEditClaim(mappedClaim)) {
          await loadEditClaim(mappedClaim);
          return;
        }

        await loadClaimDetails(mappedClaim);
      } catch (error) {
        console.error('Failed to open deep-linked claim:', error);
      }
    };

    openLinkedClaim();
  }, [authState, currentUser, canAccessApprovals]);

  // Load data when user logs in
  useEffect(() => {
    if (currentUser && canAccessApprovals) {
      loadPendingApprovals();
      const { view, tab } = parseRouteFromLocation(window.location);
      if (view === 'dashboard') {
        setDashboardTab(tab);
      }
    } else {
      setPendingApprovals([]);
      setDashboardTab('returned');
    }
  }, [currentUser, canAccessApprovals]);

  const addAuditLog = (action, description, userId) => {
    const log = {
      id: auditLogs.length + 1,
      timestamp: new Date().toLocaleString('en-MY', { timeZone: 'Asia/Kuala_Lumpur', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }),
      action,
      description,
      userId,
      userName: currentUser?.name || 'System'
    };
    setAuditLogs([log, ...auditLogs]);
  };

  const handleOpenAddClaim = async () => {
    const entity = currentUser?.entity || DEFAULT_ENTITY_NAME;
    await Promise.all([
      loadExpenses(),
      loadExpenseGlCodes()
    ]);
    setClaimForm(buildDefaultClaimForm(entity));
    setClaimValidationError(false);
    setClaimQuotaErrorRows([]);
    setShowAddClaimModal(true);
  };

  const handleAddClaimItem = () => {
    setClaimForm({
      ...claimForm,
      items: [
        ...claimForm.items,
        buildDefaultClaimItem(currentUser?.entity || DEFAULT_ENTITY_NAME)
      ]
    });
  };

  const handleRemoveClaimItem = (index) => {
    const newItems = claimForm.items.filter((_, i) => i !== index);
    setClaimForm({ ...claimForm, items: newItems });
  };

  const manageExpenseRows = useMemo(
    () => expenseItems.map((item) => ({ ...item, source: 'custom' })),
    [expenseItems]
  );

  const handleUpdateClaimItem = (index, field, value) => {
    const newItems = [...claimForm.items];
    newItems[index][field] = value;
    // Reset expense type when claim type changes
    if (field === 'claimType') {
      newItems[index].expensesType = getDefaultExpenseTypeForEntityClaim(currentUser?.entity, value);
    }
    if (claimQuotaErrorRows.length > 0) {
      setClaimQuotaErrorRows([]);
    }
    setClaimForm({ ...claimForm, items: newItems });
  };

  const handleFileInputClick = (event) => {
    // Allow selecting the same file again after user removes it.
    event.target.value = '';
  };

  const detectClaimTypeFromText = (text) => {
    const t = (text || '').toLowerCase();
    if (t.includes('dental') || t.includes('medical') || t.includes('optical') || t.includes('toll') || t.includes('parking')) {
      return 'HR and Others';
    }
    return 'Projects';
  };

  const detectExpenseTypeFromText = (text, claimType) => {
    const t = (text || '').toLowerCase();

    if (claimType === 'HR and Others') {
      if (t.includes('dental')) return 'DENTAL EXPENSES';
      if (t.includes('medical') || t.includes('clinic') || t.includes('hospital')) return 'MEDICAL EXPENSES';
      if (t.includes('optical') || t.includes('eye') || t.includes('vision')) return 'OPTICAL EXPENSES';
      if (t.includes('toll') || t.includes('parking')) return 'TOLL & PARKING';
      return 'MEDICAL EXPENSES';
    }

    if (t.includes('subscription') || t.includes('openai') || t.includes('chatgpt') || t.includes('adobe') || t.includes('zoom') || t.includes('dropbox')) {
      return 'SUBSCRIPTION FEE - DIGITISATION TOOLS';
    }
    if (t.includes('petrol') || t.includes('grab') || t.includes('transport') || t.includes('uber')) {
      return 'PETROL & TRANSPORTATION (e.g GRAB)';
    }
    if (t.includes('refreshment') || t.includes('lunch') || t.includes('dinner') || t.includes('meal')) {
      return 'STAFF REFRESHMENT';
    }
    if (t.includes('site')) return 'SITE EXPENSES';
    if (t.includes('delivery')) return 'DELIVERY CHARGES (FOR PROJECTS)';
    return 'SUBSCRIPTION FEE - DIGITISATION TOOLS';
  };

  const parseAmountFromText = (text) => {
    const normalizedAll = String(text || '').replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
    const isAdobeInvoice = /adobe/i.test(normalizedAll) && /invoice/i.test(normalizedAll);

    const collectDecimals = (value) =>
      [...String(value || '').matchAll(/\b(\d+\.\d{2})\b/g)]
        .map((m) => Number(m[1]))
        .filter((n) => Number.isFinite(n) && n > 0);

    // Strong pattern for merged-OCR invoice text: "GRAND TOTAL (MYR) 110.16"
    const mergedMyrGrandTotal = normalizedAll.match(/grand\s*total[^0-9]{0,40}(?:myr|rm)[^0-9]{0,40}(\d+(?:\.\d{1,2})?)/i);
    if (mergedMyrGrandTotal && mergedMyrGrandTotal[1]) {
      const value = Number(mergedMyrGrandTotal[1]);
      if (Number.isFinite(value) && value > 0) return value.toFixed(2);
    }
    const mergedMyrTotal = normalizedAll.match(/(?:total|amount\s*due)[^0-9]{0,40}(?:myr|rm)[^0-9]{0,40}(\d+(?:\.\d{1,2})?)/i);
    if (mergedMyrTotal && mergedMyrTotal[1]) {
      const value = Number(mergedMyrTotal[1]);
      if (Number.isFinite(value) && value > 0) return value.toFixed(2);
    }

    // Adobe invoices often OCR as merged blocks. Use a hardened fallback:
    // pick the largest realistic decimal from MYR / total context.
    if (isAdobeInvoice) {
      const myrContextMatch = normalizedAll.match(/(?:net amount\s*\(myr\)|grand total\s*\(myr\)|myr)[\s\S]{0,220}/i);
      const scoped = myrContextMatch ? myrContextMatch[0] : normalizedAll;
      const candidates = collectDecimals(scoped).filter((n) => n >= 50 && n < 100000);
      if (candidates.length > 0) return Math.max(...candidates).toFixed(2);

      // Recovery path: derive MYR from USD total and FX rate when MYR total OCR is missing.
      const usdGrand = normalizedAll.match(/grand\s*total[^0-9]{0,40}\(?usd\)?[^0-9]{0,40}(\d+(?:\.\d{1,4})?)/i);
      const fxRate = normalizedAll.match(/exchange\s*rate[^0-9]{0,60}(\d+(?:\.\d{1,6})?)/i);
      if (usdGrand && fxRate) {
        const usd = Number(usdGrand[1]);
        const fx = Number(fxRate[1]);
        if (Number.isFinite(usd) && Number.isFinite(fx) && usd > 0 && fx > 0 && fx < 1) {
          const derivedMyr = usd / fx;
          if (Number.isFinite(derivedMyr) && derivedMyr >= 50 && derivedMyr < 100000) {
            return derivedMyr.toFixed(2);
          }
        }
      }

      // Recovery path: MYR net + taxes (when both values are present).
      const myrNet = normalizedAll.match(/net\s*amount[^0-9]{0,40}\(?myr\)?[^0-9]{0,40}(\d+(?:\.\d{1,2})?)/i);
      const myrTax = normalizedAll.match(/taxes[^0-9]{0,80}(\d+(?:\.\d{1,2})?)/i);
      if (myrNet && myrTax) {
        const net = Number(myrNet[1]);
        const tax = Number(myrTax[1]);
        if (Number.isFinite(net) && Number.isFinite(tax) && net > 0 && tax >= 0) {
          const total = net + tax;
          if (Number.isFinite(total) && total >= 50 && total < 100000) return total.toFixed(2);
        }
      }
    }

    const lines = String(text || '')
      .split('\n')
      .map((line) => line.replace(/,/g, '').trim())
      .filter(Boolean);

    const extractLastAmountFromLine = (line) => {
      if (!line) return null;
      const matches = [...line.matchAll(/(\d+(?:\.\d{2})?)/g)];
      if (matches.length === 0) return null;
      const value = Number(matches[matches.length - 1][1]);
      return Number.isFinite(value) && value > 0 ? value : null;
    };

    const pickFirstAmountBy = (predicate) => {
      for (const line of lines) {
        if (!predicate(line)) continue;
        const amount = extractLastAmountFromLine(line);
        if (amount !== null) return amount.toFixed(2);
      }
      return '';
    };

    // Priority 1: MYR/RM grand total lines (most reliable for local claim amount).
    const myrGrandTotal = pickFirstAmountBy((line) =>
      /grand\s*total/i.test(line) && /(myr|rm)/i.test(line) && !/exchange\s*rate/i.test(line)
    );
    if (myrGrandTotal) return myrGrandTotal;

    // Priority 2: MYR/RM total/amount due.
    const myrTotal = pickFirstAmountBy((line) =>
      /(amount\s*due|total\s*amount|grand\s*total|total)/i.test(line) &&
      /(myr|rm)/i.test(line) &&
      !/exchange\s*rate|tax\s*rate/i.test(line)
    );
    if (myrTotal) return myrTotal;

    // Priority 3: Any grand total / amount due / total lines.
    const anyTotal = pickFirstAmountBy((line) =>
      /(amount\s*due|total\s*amount|grand\s*total|amount\s*paid|total)/i.test(line) &&
      !/exchange\s*rate|tax\s*rate/i.test(line)
    );
    if (anyTotal) return anyTotal;

    // OCR fallback for invoice-total blocks that may be collapsed into one line.
    if (/invoice\s*total|grand\s*total|adobe/i.test(normalizedAll)) {
      const decimals = collectDecimals(normalizedAll).filter((n) => n >= 50 && n < 100000);
      if (decimals.length > 0) {
        return Math.max(...decimals).toFixed(2);
      }
    }

    const normalized = String(text || '').replace(/,/g, '');
    const allAmounts = [...normalized.matchAll(/(?:usd|rm|myr|sgd|eur|\$)?\s*(\d+\.\d{2})/gi)]
      .map((m) => Number(m[1]))
      .filter((n) => Number.isFinite(n) && n > 0);

    if (allAmounts.length > 0) return Math.max(...allAmounts).toFixed(2);
    return '';
  };

  const parseInvoiceNoFromText = (text, fallbackFileName = '') => {
    const normalized = text || '';
    const normalizedFlat = String(text || '').replace(/\s+/g, ' ').trim();
    const looksLikeDateToken = (value) => {
      const v = String(value || '').trim();
      if (!v) return true;
      if (/^(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*[-\s]?\d{2,4}$/i.test(v)) return true;
      if (/^\d{1,2}[-\/]\d{2,4}$/.test(v)) return true;
      if (/^\d{2,4}[-\/]\d{1,2}$/.test(v)) return true;
      if (/^\d{1,2}[-\/][a-z]{3,9}[-\/]\d{2,4}$/i.test(v)) return true;
      return false;
    };
    const invoiceLabelPatterns = [
      /invoice\s*information[\s\S]{0,350}?invoice\s*number\s*[:.\-]?\s*([A-Z0-9][A-Z0-9\-]{5,})\b/i,
      /invoice\s*(?:no|number|#)\s*[:.\-]?\s*([A-Z0-9][A-Z0-9\-]{5,})\b/i,
      // OCR-tolerant: "invoice nurnber"/"invoice numher"/etc.
      /invoice\s*n[a-z]{3,8}\s*[:.\-]?\s*([A-Z0-9][A-Z0-9\-]{5,})\b/i,
      // OCR-tolerant with spaces in long numeric id
      /invoice\s*(?:no|number|#)\s*[:.\-]?\s*((?:\d[\s-]?){8,20})/i
    ];

    const isSstContext = (candidate) => {
      const escaped = String(candidate || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const contextRegex = new RegExp(`(?:sst\\s*no\\.?\\s*[:\\-]?\\s*)${escaped}`, 'i');
      return contextRegex.test(normalizedFlat);
    };

    for (const pattern of invoiceLabelPatterns) {
      const match = normalized.match(pattern);
      if (!match || !match[1]) continue;
      const value = match[1].replace(/\s+/g, '').trim();
      if (/^(invoice|number|receipt|bill)$/i.test(value)) continue;
      if (looksLikeDateToken(value)) continue;
      if (isSstContext(value)) continue;
      return value;
    }

    // Fallback: first long numeric token after "Invoice Information"
    const invoiceInfoLongNumeric = normalizedFlat.match(/invoice\s*information[\s\S]{0,260}?(\d{8,14})/i);
    if (invoiceInfoLongNumeric && invoiceInfoLongNumeric[1]) {
      const value = invoiceInfoLongNumeric[1].trim();
      if (!isSstContext(value)) return value;
    }

    // Scored fallback: choose the best long numeric candidate by local context.
    const candidates = [...normalizedFlat.matchAll(/\b(\d{8,14})\b/g)].map((m) => {
      const value = m[1];
      const idx = typeof m.index === 'number' ? m.index : 0;
      const context = normalizedFlat.slice(Math.max(0, idx - 40), Math.min(normalizedFlat.length, idx + value.length + 40));
      let score = 0;
      if (/invoice/i.test(context)) score += 5;
      if (/invoice\s*information/i.test(context)) score += 3;
      if (/number/i.test(context)) score += 1;
      if (/sst|customer|order|purchase|po\b|tax|vat|currency/i.test(context)) score -= 6;
      return { value, score };
    });
    if (candidates.length > 0) {
      candidates.sort((a, b) => b.score - a.score);
      const best = candidates[0];
      if (best.score > 0) return best.value;
    }

    const base = (fallbackFileName || '').replace(/\.[^.]+$/, '');
    const fallbackMatch = base.match(/[A-Z0-9-]{6,}/i);
    if (!fallbackMatch) return '';
    const fallbackValue = fallbackMatch[0];
    // Do not use filename-only words like "StephAdobe"; fallback ID must include digits.
    if (!/\d/.test(fallbackValue)) return '';
    if (/sst/i.test(fallbackValue)) return '';
    return looksLikeDateToken(fallbackValue) ? '' : fallbackValue;
  };

  const isGenericFileLabel = (value = '') => {
    const normalized = String(value || '')
      .replace(/\.[^.]+$/, '')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
    const compact = normalized.replace(/\s+/g, '');

    if (!normalized) return true;
    if (/^(invoice|receipt|bill|document|doc|scan|scanned|image|img|photo|file|attachment)(\s*\d+)?$/.test(normalized)) return true;
    if (/^(invoice|receipt|bill|document|doc|scan|scanned|image|img|photo|file|attachment)\d*$/.test(compact)) return true;
    if ((compact.includes('invoice') && compact.includes('receipt')) ||
        (compact.includes('bill') && compact.includes('receipt'))) return true;
    return false;
  };

  const parseBusinessNameFromText = (text, fallbackFileName = '') => {
    const sourceText = String(text || '');
    const normalizedSource = sourceText.replace(/\s+/g, ' ').trim();
    const lines = sourceText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    const billToIdx = lines.findIndex((line) => /^bill\s*to\b/i.test(line));
    const invoiceIdx = lines.findIndex((line) => /^invoice\b/i.test(line));
    const vendorUpperBound =
      billToIdx > 0 && invoiceIdx > 0
        ? Math.min(billToIdx, invoiceIdx)
        : (billToIdx > 0 ? billToIdx : (invoiceIdx > 0 ? invoiceIdx : Math.min(lines.length, 25)));
    const vendorBlock = lines.slice(0, vendorUpperBound);

    const companySuffixRegex = /\b(sdn\.?\s*bhd\.?|llc|ltd\.?|limited|inc\.?|corp\.?|corporation|company|co\.?|plc|pbc|enterprise|ventures|holdings|group|solutions?|technologies?|studio|services?)\b/i;
    const noiseRegex = /invoice|invoice information|original|receipt|bill to|ship to|description|qty|unit price|subtotal|total|amount due|date|payment address|pay online|currency|tax|po box|p\.?o\.?\s*box|street|road|avenue|lane|boulevard|selangor|malaysia|united states|california|klang|san francisco/i;

    const cleanBusinessCandidate = (value) =>
      String(value || '')
        .replace(/^[^A-Za-z]+/, '')
        .replace(/\s+(ORIGINAL|Invoice Information|Bill To|INVOICE)\b.*$/i, '')
        .replace(/\s{2,}/g, ' ')
        .trim();

    // Merged OCR case: capture vendor prefix before "ORIGINAL / Invoice Information / Bill To / INVOICE".
    const mergedVendorPrefix = normalizedSource.match(
      /^([A-Za-z0-9&.,'()\- ]{3,120}?\b(?:ltd\.?|limited|llc|inc\.?|corp\.?|corporation|sdn\.?\s*bhd\.?)\b)(?=\s+(?:original|invoice information|bill to|invoice)\b)/i
    );
    if (mergedVendorPrefix && mergedVendorPrefix[1]) {
      return cleanBusinessCandidate(mergedVendorPrefix[1]).slice(0, 120);
    }

    const isLikelyCompanyLine = (line) => {
      if (!line || line.length < 3 || line.length > 80) return false;
      if (/\d/.test(line)) return false;
      if (/@|:|https?:\/\//i.test(line)) return false;
      if (noiseRegex.test(line)) return false;
      const wordCount = line.split(/\s+/).length;
      if (wordCount > 8) return false;
      if (companySuffixRegex.test(line)) return true;
      // fallback: title-cased line near top that looks like a business name
      return /^[A-Z][A-Za-z0-9&.,'()\- ]+$/.test(line) && wordCount >= 1 && wordCount <= 5;
    };

    const strongMatch = vendorBlock.find((line) => companySuffixRegex.test(line) && isLikelyCompanyLine(line));
    if (strongMatch) return strongMatch.slice(0, 120);

    // OCR sometimes merges company + address into one line; extract company prefix before numeric address.
    const mergedLineMatch = vendorBlock.find((line) => companySuffixRegex.test(line));
    if (mergedLineMatch) {
      const extracted = mergedLineMatch.match(/^([A-Za-z0-9&.,'()\- ]{2,80}?\b(?:sdn\.?\s*bhd\.?|llc|ltd\.?|limited|inc\.?|corp\.?|corporation|company|co\.?|plc|pbc|enterprise|ventures|holdings|group|solutions?|technologies?|studio|services?)\b)/i);
      const candidate = extracted ? extracted[1].trim() : mergedLineMatch.trim();
      if (candidate && !isGenericFileLabel(candidate) && !noiseRegex.test(candidate)) {
        return candidate.slice(0, 120);
      }
    }

    // OCR sometimes keeps only email/contact lines; infer vendor from domain.
    const emailMatches = sourceText.match(/[A-Z0-9._%+-]+@([A-Z0-9.-]+\.[A-Z]{2,})/gi) || [];
    if (emailMatches.length > 0) {
      const blockedDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com'];
      for (const email of emailMatches) {
        const domain = String(email.split('@')[1] || '').toLowerCase().replace(/[^a-z0-9.-]/g, '');
        if (!domain || blockedDomains.includes(domain)) continue;
        const core = domain.split('.')[0] || '';
        if (!core || core.length < 3) continue;
        if (core.includes('openai')) return 'OpenAI OpCo, LLC';
        if (core.includes('anthropic')) return 'Anthropic, PBC';
        const pretty = core
          .replace(/[-_]+/g, ' ')
          .split(' ')
          .filter(Boolean)
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
          .join(' ');
        if (pretty && !isGenericFileLabel(pretty)) return pretty.slice(0, 120);
      }
    }

    // Last OCR fallback: detect known vendor keywords in noisy text.
    if (/openai/i.test(sourceText)) return 'OpenAI OpCo, LLC';
    if (/anthropic/i.test(sourceText)) return 'Anthropic, PBC';

    // Strong fallback for common software vendors seen in receipts.
    if (/adobe/i.test(sourceText)) {
      const adobeLine = lines.find((line) =>
        /adobe/i.test(line) &&
        /systems|software|ltd|limited|inc|llc/i.test(line)
      );
      if (adobeLine) return cleanBusinessCandidate(adobeLine).slice(0, 120);
      return 'Adobe';
    }

    const firstCandidate = vendorBlock.find(isLikelyCompanyLine);
    if (firstCandidate) return firstCandidate.slice(0, 120);

    return '';
  };

  const parseDescriptionFromText = (text, expensesType = '', fallbackBusinessName = '') => {
    const lines = (text || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    const normalizedAll = String(text || '').replace(/\s+/g, ' ').trim();

    if (/adobe/i.test(normalizedAll)) {
      const adobeItem = normalizedAll.match(/product\s*description\s+([A-Za-z][A-Za-z0-9 &.,'()\-\/]{2,60}?)\s+\d+\s+[A-Z]{1,4}\s+\d+(?:\.\d{1,2})/i);
      if (adobeItem && adobeItem[1]) {
        return adobeItem[1].trim().slice(0, 120);
      }
      if (/\billustrator\b/i.test(normalizedAll)) return 'Illustrator';
      if (/\bphotoshop\b/i.test(normalizedAll)) return 'Photoshop';
      if (/\bacrobat\b/i.test(normalizedAll)) return 'Acrobat';
      return 'Adobe Subscription';
    }

    const rowNoiseRegex = /^(description|qty|quantity|unit price|amount|subtotal|total|amount due|invoice|receipt|bill to|date|payment address|pay online|currency|item details|service term)$/i;
    const looksLikeAddressOrMeta = (line) =>
      /\d{3,}|@|street|road|avenue|lane|boulevard|p\.?o\.?\s*box|selangor|malaysia|united states|california|klang|san francisco/i.test(line);
    const cleanLineItemDescription = (line) => {
      let cleaned = String(line || '').replace(/\s+/g, ' ').trim();
      // First pass: regex cleanup for common row endings.
      cleaned = cleaned
        .replace(/^\d{4,}\s+/, '')
        .replace(/\s+\d+\s+(?:USD|MYR|RM|\$)?\s*\d+(?:\.\d{1,2})?(?:\s+\d{1,3}%?)?(?:\s+(?:USD|MYR|RM|\$)?\s*\d+(?:\.\d{1,2})?){0,2}\s*$/i, '')
        .replace(/\s+(?:x?\d+)\s+(?:USD|MYR|RM|\$)?\s*\d+(?:\.\d{1,2})?\s+(?:USD|MYR|RM|\$)?\s*\d+(?:\.\d{1,2})?$/i, '')
        .replace(/\s+(?:USD|MYR|RM|\$)\s*\d+(?:\.\d{1,2})?\s*$/i, '')
        .replace(/\s+\d{1,3}%\s*$/i, '')
        .trim();

      // Second pass: token-based tail stripping for noisy OCR variants.
      const numericToken = (token) =>
        /^(?:\$|rm|myr|usd)?\s*\d+(?:[.,]\d{1,2})?$/.test(token.toLowerCase()) ||
        /^\d{1,3}%$/.test(token) ||
        /^x?\d+$/.test(token.toLowerCase()) ||
        /^(?:usd|myr|rm|\$)$/.test(token.toLowerCase());

      const rawTokens = cleaned.split(' ').filter(Boolean);
      let end = rawTokens.length;
      let removed = 0;
      while (end > 2) {
        const token = rawTokens[end - 1].replace(/[()]/g, '');
        if (!numericToken(token)) break;
        end -= 1;
        removed += 1;
        if (removed >= 5) break;
      }

      if (removed > 0) {
        cleaned = rawTokens.slice(0, end).join(' ').trim();
      }

      return cleaned.replace(/\s{2,}/g, ' ').trim();
    };

    // Strong pattern for invoice table row when OCR merges columns into one line.
    // Example: "6518562 Illustrator 1 EA 102.00 102.00 8.00% 8.16 110.16"
    const mergedTableRowMatch = normalizedAll.match(
      /\b\d{5,}\s+([A-Za-z][A-Za-z0-9&.,'()\-\/ ]{2,80}?)\s+\d+\s+[A-Z]{1,4}\s+\d+(?:\.\d{1,2})\s+\d+(?:\.\d{1,2})/i
    );
    if (mergedTableRowMatch && mergedTableRowMatch[1]) {
      const cleaned = cleanLineItemDescription(mergedTableRowMatch[1]);
      if (cleaned && cleaned.length >= 2) return cleaned.slice(0, 120);
    }

    // Prefer invoice table item: line after a "Description ... Qty ... Unit price ... Amount" header.
    const tableHeaderIdx = lines.findIndex((line) =>
      /description/i.test(line) && /(qty|quantity)/i.test(line) && /(unit price|amount)/i.test(line)
    );
    if (tableHeaderIdx >= 0) {
      for (let i = tableHeaderIdx + 1; i < Math.min(lines.length, tableHeaderIdx + 8); i += 1) {
        const candidate = lines[i];
        if (!candidate) continue;
        if (rowNoiseRegex.test(candidate)) continue;
        if (looksLikeAddressOrMeta(candidate)) continue;
        if (candidate.length < 2 || candidate.length > 80) continue;
        const cleaned = cleanLineItemDescription(candidate);
        if (!cleaned || cleaned.length < 2) continue;
        return cleaned.slice(0, 120);
      }
    }

    // OCR fallback: a line that looks like item + numeric columns (e.g., "Illustrator 1 EA 102.00 ...").
    for (const line of lines) {
      if (!line || line.length < 3 || line.length > 140) continue;
      if (rowNoiseRegex.test(line)) continue;
      if (looksLikeAddressOrMeta(line)) continue;
      if (!/\d+(?:\.\d{1,2})/.test(line)) continue;
      const cleaned = cleanLineItemDescription(line);
      if (!cleaned || cleaned.length < 3) continue;
      if (/(invoice|bill to|ship to|invoice total|grand total)/i.test(cleaned)) continue;
      return cleaned.slice(0, 120);
    }

    // Fallback for subscription-like invoices.
    const keywordCandidate = lines.find((line) =>
      /(subscription|plan|pro|license|service|membership|monthly|annual|usage|credit|api)/i.test(line) &&
      !looksLikeAddressOrMeta(line) &&
      !rowNoiseRegex.test(line)
    );
    if (keywordCandidate) return cleanLineItemDescription(keywordCandidate).slice(0, 120);

    if (fallbackBusinessName && !isGenericFileLabel(fallbackBusinessName)) {
      return fallbackBusinessName.slice(0, 120);
    }
    return expensesType || 'Receipt';
  };

  const postProcessExtractedFields = ({ text, businessName, description, amount, receiptNumber, expensesType }) => {
    let nextDescription = description || '';
    let nextAmount = amount || '';
    let nextReceiptNumber = receiptNumber || '';

    const normalizedText = String(text || '').replace(/\s+/g, ' ').trim();
    const normalizedBusiness = String(businessName || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    const normalizedDesc = String(nextDescription || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

    // If description is basically vendor text, override with better item-level fallback.
    const descLooksLikeBusiness = normalizedBusiness && normalizedDesc &&
      (normalizedDesc.includes(normalizedBusiness.slice(0, Math.min(18, normalizedBusiness.length))) ||
       normalizedBusiness.includes(normalizedDesc.slice(0, Math.min(18, normalizedDesc.length))));
    if ((descLooksLikeBusiness || /^adobe systems software/i.test(nextDescription || '')) && normalizedText) {
      const parsedDescription = parseDescriptionFromText(text, expensesType, '');
      const parsedToken = normalizeAnalyzeToken(parsedDescription);
      const expenseToken = normalizeAnalyzeToken(expensesType);
      if (parsedDescription && parsedToken && parsedToken !== expenseToken && parsedToken !== 'RECEIPT') {
        nextDescription = parsedDescription;
      }
    }

    // Ensure receipt number is not blank/date-like when a numeric invoice ID exists.
    if (!nextReceiptNumber || /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i.test(nextReceiptNumber)) {
      nextReceiptNumber = parseInvoiceNoFromText(text, '');
    }

    // If amount is suspiciously small for invoice total block, re-pick max decimal in totals context.
    const numericAmount = Number(nextAmount || 0);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0 || (numericAmount < 50 && /invoice\s*total|grand\s*total|adobe/i.test(normalizedText))) {
      nextAmount = parseAmountFromText(text);
    }

    return {
      description: nextDescription,
      amount: nextAmount,
      receiptNumber: nextReceiptNumber
    };
  };

  const resetAnalyzeState = () => {
    setAnalyzeFiles([]);
    setIsAnalyzeDragActive(false);
    setAnalyzeFileStatuses([]);
    setAnalyzeStagedItems([]);
    setAnalyzeSummary({ created: 0, warningRows: 0, failed: 0 });
    setAnalyzeSuggestedMonthYear('');
    setAnalyzeSuggestedRemark('');
    setAnalyzeHasCompleted(false);
  };

  const normalizeAnalyzeToken = (value) =>
    String(value || '')
      .replace(/&/g, ' AND ')
      .replace(/[()]/g, ' ')
      .replace(/[^A-Za-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toUpperCase();

  const mapClaimTypeFromExtracted = (entity, rawClaimType) => {
    const options = getClaimTypeOptions(entity, '');
    if (!Array.isArray(options) || options.length === 0) {
      return {
        value: getDefaultClaimTypeForEntity(entity),
        warning: 'Claim type options unavailable; default selected.'
      };
    }

    const normalizedRaw = normalizeAnalyzeToken(rawClaimType);
    if (!normalizedRaw) {
      return {
        value: options[0],
        warning: 'Claim type missing from analyzer; default selected.'
      };
    }

    const claimAlias = {
      PROJECT: 'Projects',
      PROJECTS: 'Projects',
      HR: 'HR and Others',
      'HR AND OTHERS': 'HR and Others',
      'HR OTHERS': 'HR and Others'
    };

    if (claimAlias[normalizedRaw] && options.includes(claimAlias[normalizedRaw])) {
      return { value: claimAlias[normalizedRaw], warning: '' };
    }

    const direct = options.find((option) => normalizeAnalyzeToken(option) === normalizedRaw);
    if (direct) return { value: direct, warning: '' };

    const partial = options.find((option) => {
      const token = normalizeAnalyzeToken(option);
      return token.includes(normalizedRaw) || normalizedRaw.includes(token);
    });
    if (partial) return { value: partial, warning: '' };

    return {
      value: options[0],
      warning: `Could not map claim type "${rawClaimType}" automatically.`
    };
  };

  const mapExpenseTypeFromExtracted = (entity, claimType, rawExpenseType) => {
    const normalizedRaw = normalizeAnalyzeToken(rawExpenseType);
    const aliasRaw = normalizeExpenseAlias(rawExpenseType || '');
    const claimTypeOptions = getClaimTypeOptions(entity, claimType);
    const orderedClaimTypes = [claimType, ...claimTypeOptions.filter((item) => item !== claimType)];

    let bestMatch = null;
    for (const typeOption of orderedClaimTypes) {
      const options = getExpenseOptions(entity, typeOption, '', { preserveCurrent: false });
      if (!Array.isArray(options) || options.length === 0) continue;

      const byToken = options.find((option) => normalizeAnalyzeToken(option) === normalizedRaw);
      if (byToken) {
        bestMatch = { claimType: typeOption, expenseType: byToken, warning: '' };
        break;
      }

      const byAlias = options.find((option) => normalizeExpenseAlias(option) === aliasRaw);
      if (byAlias) {
        bestMatch = { claimType: typeOption, expenseType: byAlias, warning: '' };
        break;
      }

      const byPartial = options.find((option) => {
        const token = normalizeAnalyzeToken(option);
        return token.includes(normalizedRaw) || normalizedRaw.includes(token);
      });
      if (byPartial && !bestMatch) {
        bestMatch = { claimType: typeOption, expenseType: byPartial, warning: '' };
      }
    }

    if (bestMatch) return bestMatch;

    return {
      claimType,
      expenseType: '',
      warning: rawExpenseType
        ? `Could not map expense type "${rawExpenseType}" to active options.`
        : 'Expense type missing from analyzer.'
    };
  };

  const toMonthYearFromRaw = (rawValue) => {
    const value = String(rawValue || '').trim();
    if (!value) return '';
    if (/^\d{4}-(0[1-9]|1[0-2])$/.test(value)) return value;

    const dateMatch = value.match(/(\d{4})[-/](\d{1,2})/);
    if (dateMatch) {
      const year = dateMatch[1];
      const month = dateMatch[2].padStart(2, '0');
      if (/^(0[1-9]|1[0-2])$/.test(month)) return `${year}-${month}`;
    }

    const monthNameMatch = value.match(/(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*[\s,/-]*(\d{4})/i);
    if (monthNameMatch) {
      const monthLookup = {
        jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06',
        jul: '07', aug: '08', sep: '09', sept: '09', oct: '10', nov: '11', dec: '12'
      };
      const month = monthLookup[monthNameMatch[1].toLowerCase()] || '';
      if (month) return `${monthNameMatch[2]}-${month}`;
    }

    return '';
  };

  const parseAmountValue = (value) => {
    const normalized = String(value || '')
      .replace(/[^0-9.,-]/g, '')
      .replace(/,/g, '');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const getAnalyzeIdleStatus = (file) => ({
    fileName: file.name,
    fileType: file.type || inferMimeTypeFromFilename(file.name) || 'application/octet-stream',
    status: 'idle',
    message: '',
    warnings: []
  });

  const buildAnalyzeAggregate = (stagedItems, statuses) => {
    const completedStatuses = statuses.filter((item) => item.status !== 'idle' && item.status !== 'processing');
    const failed = completedStatuses.filter((item) => item.status === 'error').length;
    const warningRows = stagedItems.filter((item) => Array.isArray(item.analysisWarnings) && item.analysisWarnings.length > 0).length;
    const monthCandidates = stagedItems.map((item) => item.analysisMonthYear).filter(Boolean);
    const remarkCandidates = stagedItems.map((item) => item.description).filter(Boolean);
    const bestMonthYear = monthCandidates.length > 0
      ? monthCandidates.reduce((acc, monthYear) => {
        acc[monthYear] = (acc[monthYear] || 0) + 1;
        return acc;
      }, {})
      : null;
    const resolvedMonthYear = bestMonthYear
      ? Object.keys(bestMonthYear).sort((a, b) => bestMonthYear[b] - bestMonthYear[a])[0]
      : '';
    const suggestedRemark = remarkCandidates.length > 0
      ? `Auto-filled from receipts: ${remarkCandidates.slice(0, 2).join(' / ').slice(0, 120)}`
      : stagedItems.length > 0
        ? `Auto-filled from ${stagedItems.length} receipt(s)`
        : '';
    const hasCompleted = statuses.length > 0 && statuses.every((item) => item.status !== 'idle' && item.status !== 'processing');

    return {
      summary: {
        created: stagedItems.length,
        warningRows,
        failed
      },
      suggestedMonthYear: resolvedMonthYear,
      suggestedRemark,
      hasCompleted
    };
  };

  const handleAnalyzeReceipts = async () => {
    if (analyzeFiles.length === 0) {
      alert('Please upload at least one receipt to analyze');
      return;
    }

    const entity = currentUser?.entity || DEFAULT_ENTITY_NAME;
    const normalizedStatuses = analyzeFiles.map((file, index) => analyzeFileStatuses[index] || getAnalyzeIdleStatus(file));
    const pendingIndexes = normalizedStatuses
      .map((statusItem, index) => (statusItem.status === 'idle' ? index : -1))
      .filter((index) => index >= 0);

    if (pendingIndexes.length === 0) {
      alert('There are no new files to analyze.');
      return;
    }

    const pendingIndexSet = new Set(pendingIndexes);
    const processingStatuses = normalizedStatuses.map((statusItem, index) =>
      pendingIndexSet.has(index)
        ? {
          fileName: analyzeFiles[index].name,
          fileType: analyzeFiles[index].type || inferMimeTypeFromFilename(analyzeFiles[index].name) || 'application/octet-stream',
          status: 'processing',
          message: 'Processing...',
          warnings: []
        }
        : statusItem
    );
    setAnalyzeFileStatuses(processingStatuses);
    setAnalyzeHasCompleted(false);
    setIsAnalyzing(true);
    const stagedItems = analyzeStagedItems.filter((item) => !pendingIndexSet.has(item.sourceFileIndex));
    let nextStatuses = [...processingStatuses];

    try {
      for (let batchStart = 0; batchStart < pendingIndexes.length; batchStart += ANALYZE_UPLOAD_BATCH_SIZE) {
        const batchIndexes = pendingIndexes.slice(batchStart, batchStart + ANALYZE_UPLOAD_BATCH_SIZE);
        const batchFiles = batchIndexes.map((index) => analyzeFiles[index]);
        const analysis = await fileAPI.analyzeReceiptsViaN8n(batchFiles);
        const analysisFiles = Array.isArray(analysis?.files) ? analysis.files : [];
        const resultQueues = new Map();
        analysisFiles.forEach((result) => {
          const fileName = String(result?.file_name || '').trim();
          if (!fileName) return;
          if (!resultQueues.has(fileName)) resultQueues.set(fileName, []);
          resultQueues.get(fileName).push(result);
        });

        for (const fileIndex of batchIndexes) {
          const file = analyzeFiles[fileIndex];
          const queue = resultQueues.get(file.name) || [];
          const result = queue.length > 0 ? queue.shift() : null;

          if (!result) {
            nextStatuses[fileIndex] = {
              fileName: file.name,
              fileType: file.type || inferMimeTypeFromFilename(file.name) || 'application/octet-stream',
              status: 'error',
              message: 'No analyzer response for this file.',
              warnings: ['No result returned from n8n analyzer.']
            };
            continue;
          }

        if (String(result?.status || '').toLowerCase() !== 'ok') {
          const reason = String(result?.reason || 'Analyzer could not read this file.');
          nextStatuses[fileIndex] = {
            fileName: file.name,
            fileType: file.type || inferMimeTypeFromFilename(file.name) || 'application/octet-stream',
            status: 'error',
            message: reason,
            warnings: [reason]
          };
          continue;
        }

        const rawText = String(result?.raw_text || result?.text || '').trim();
        const rawClaimType = String(result?.claim_type || '').trim();
        const rawExpenseType = String(result?.expense_type || '').trim();

        const claimMatch = mapClaimTypeFromExtracted(entity, rawClaimType);
        const expenseMatch = mapExpenseTypeFromExtracted(entity, claimMatch.value, rawExpenseType);
        const chosenClaimType = expenseMatch.claimType || claimMatch.value || getDefaultClaimTypeForEntity(entity);
        const chosenExpenseType = expenseMatch.expenseType || '';

        const rawBusinessName = String(result?.business_name || result?.merchant || '').trim();
        const rawDescription = String(result?.description || '').trim();
        const rawReceipt = String(result?.receipt_number || result?.invoice_number || '').trim();
        const rawAmount = String(result?.amount || '').trim();

        const fallbackBusiness = rawBusinessName || parseBusinessNameFromText(rawText, file.name);
        const fallbackDescription = rawDescription || parseDescriptionFromText(rawText, chosenExpenseType, fallbackBusiness);
        const fallbackReceipt = rawReceipt || parseInvoiceNoFromText(rawText, file.name);
        const fallbackAmount = rawAmount || parseAmountFromText(rawText);

        const processed = postProcessExtractedFields({
          text: rawText,
          businessName: fallbackBusiness,
          description: fallbackDescription,
          amount: fallbackAmount,
          receiptNumber: fallbackReceipt,
          expensesType: chosenExpenseType
        });

        const numericAmount = parseAmountValue(processed.amount || fallbackAmount);
        const warnings = [];
        if (claimMatch.warning) warnings.push(claimMatch.warning);
        if (expenseMatch.warning) warnings.push(expenseMatch.warning);
        if (!processed.description) warnings.push('Description was not confidently extracted.');
        if (!fallbackBusiness) warnings.push('Business name was not confidently extracted.');
        if (!processed.receiptNumber) warnings.push('Receipt/Invoice number was not confidently extracted.');
        if (!Number.isFinite(numericAmount) || numericAmount <= 0) warnings.push('Amount was not confidently extracted.');

        if (!chosenExpenseType) {
          const critical = [];
          if (!chosenExpenseType) critical.push('expense type');
          nextStatuses[fileIndex] = {
            fileName: file.name,
            fileType: file.type || inferMimeTypeFromFilename(file.name) || 'application/octet-stream',
            status: 'error',
            message: `Missing critical field(s): ${critical.join(', ')}`,
            warnings: warnings.length > 0 ? warnings : [`Missing critical field(s): ${critical.join(', ')}`]
          };
          continue;
        }

        const monthCandidate = toMonthYearFromRaw(
          result?.month_year || result?.claim_month_year || result?.invoice_date || result?.date
        );

        const item = {
          claimType: chosenClaimType,
          expensesType: chosenExpenseType,
          description: String(processed.description || '').trim().slice(0, 180),
          amount: Number.isFinite(numericAmount) && numericAmount > 0 ? numericAmount.toFixed(2) : '',
          businessName: String(fallbackBusiness || '').trim().slice(0, 180),
          receiptNumber: String(processed.receiptNumber || '').trim().slice(0, 80),
          file,
          analysisWarnings: warnings,
          analysisMonthYear: monthCandidate,
          sourceFileIndex: fileIndex
        };

        stagedItems.push(item);
        nextStatuses[fileIndex] = {
          fileName: file.name,
          fileType: file.type || inferMimeTypeFromFilename(file.name) || 'application/octet-stream',
          status: 'done',
          message: warnings.length > 0 ? 'Created with warnings' : 'Done',
          warnings
        };
        }

        setAnalyzeFileStatuses([...nextStatuses]);
      }

      const aggregate = buildAnalyzeAggregate(stagedItems, nextStatuses);

      setAnalyzeFileStatuses(nextStatuses);
      setAnalyzeStagedItems(stagedItems);
      setAnalyzeSummary(aggregate.summary);
      setAnalyzeSuggestedMonthYear(aggregate.suggestedMonthYear);
      setAnalyzeSuggestedRemark(aggregate.suggestedRemark);
      setAnalyzeHasCompleted(aggregate.hasCompleted);
    } catch (error) {
      console.error('Receipt analyze failed:', error);
      const reason = error?.message ? String(error.message) : 'Failed to analyze receipts.';
      nextStatuses = nextStatuses.map((item, index) =>
        pendingIndexSet.has(index) && item.status === 'processing'
          ? {
            ...item,
            status: 'error',
            message: reason,
            warnings: [reason]
          }
          : item
      );
      const aggregate = buildAnalyzeAggregate(stagedItems, nextStatuses);
      setAnalyzeFileStatuses(nextStatuses);
      setAnalyzeSummary(aggregate.summary);
      setAnalyzeSuggestedMonthYear(aggregate.suggestedMonthYear);
      setAnalyzeSuggestedRemark(aggregate.suggestedRemark);
      setAnalyzeHasCompleted(aggregate.hasCompleted);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleApplyAnalyzedReceipts = () => {
    if (!analyzeHasCompleted || analyzeStagedItems.length === 0) {
      setShowAnalyzeModal(false);
      resetAnalyzeState();
      return;
    }

    const firstItem = claimForm.items[0];
    const isFirstItemEmpty = !firstItem.description && !firstItem.amount && !firstItem.file;
    const finalizedItems = [...analyzeStagedItems]
      .sort((a, b) => (a.sourceFileIndex ?? 0) - (b.sourceFileIndex ?? 0))
      .map(({ sourceFileIndex, analysisMonthYear, ...item }) => item);
    const nextItems = isFirstItemEmpty
      ? [finalizedItems[0], ...finalizedItems.slice(1), ...claimForm.items.slice(1)]
      : [...claimForm.items, ...finalizedItems];

    setClaimForm((prev) => ({
      ...prev,
      monthYear: analyzeSuggestedMonthYear || prev.monthYear,
      remarks: prev.remarks || analyzeSuggestedRemark || prev.remarks,
      items: nextItems
    }));

    setShowAnalyzeModal(false);
    resetAnalyzeState();
  };

  const appendAnalyzeFiles = (incomingFiles) => {
    if (isAnalyzing) return;

    const files = Array.from(incomingFiles || []);
    const validMimeFiles = files.filter(file => {
      const isImage = file.type.startsWith('image/');
      const isPDF = file.type === 'application/pdf';
      return isImage || isPDF;
    });

    const oversizedFiles = validMimeFiles.filter((file) => file.size > MAX_ANALYZE_FILE_SIZE_BYTES);
    const validFiles = validMimeFiles.filter((file) => file.size <= MAX_ANALYZE_FILE_SIZE_BYTES);

    if (validMimeFiles.length !== files.length) {
      alert('Only image and PDF files are allowed');
    }
    if (oversizedFiles.length > 0) {
      alert(
        `Upload failed. The file size exceeds the ${MAX_ANALYZE_FILE_SIZE_MB}MB limit.\n\nToo large:\n- ${oversizedFiles
          .map((file) => file.name)
          .join('\n- ')}`
      );
    }

    if (validFiles.length === 0) return;

    const nextStatuses = [
      ...analyzeFiles.map((file, index) => analyzeFileStatuses[index] || getAnalyzeIdleStatus(file)),
      ...validFiles.map(getAnalyzeIdleStatus)
    ];
    const aggregate = buildAnalyzeAggregate(analyzeStagedItems, nextStatuses);

    setAnalyzeFiles(prev => [...prev, ...validFiles]);
    setAnalyzeFileStatuses(nextStatuses);
    setAnalyzeSummary(aggregate.summary);
    setAnalyzeSuggestedMonthYear(aggregate.suggestedMonthYear);
    setAnalyzeSuggestedRemark(aggregate.suggestedRemark);
    setAnalyzeHasCompleted(false);
  };

  const handleAnalyzeFileUpload = (e) => {
    appendAnalyzeFiles(e.target.files);
    e.target.value = '';
  };

  const handleAnalyzeDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isAnalyzing) return;
    setIsAnalyzeDragActive(true);
  };

  const handleAnalyzeDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsAnalyzeDragActive(false);
  };

  const handleAnalyzeDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsAnalyzeDragActive(false);
    if (isAnalyzing) return;
    appendAnalyzeFiles(e.dataTransfer.files);
  };

  const handleRemoveAnalyzeFile = (index) => {
    const nextFiles = analyzeFiles.filter((_, i) => i !== index);
    const nextStatuses = analyzeFileStatuses
      .filter((_, i) => i !== index)
      .slice(0, nextFiles.length);
    const nextStagedItems = analyzeStagedItems
      .filter((item) => item.sourceFileIndex !== index)
      .map((item) => ({
        ...item,
        sourceFileIndex: item.sourceFileIndex > index ? item.sourceFileIndex - 1 : item.sourceFileIndex
      }));
    const aggregate = buildAnalyzeAggregate(nextStagedItems, nextStatuses);

    setAnalyzeFiles(nextFiles);
    setAnalyzeFileStatuses(nextStatuses);
    setAnalyzeStagedItems(nextStagedItems);
    setAnalyzeSummary(aggregate.summary);
    setAnalyzeSuggestedMonthYear(aggregate.suggestedMonthYear);
    setAnalyzeSuggestedRemark(aggregate.suggestedRemark);
    setAnalyzeHasCompleted(aggregate.hasCompleted);
  };

  const handleSubmitClaim = async () => {
    if (submitClaimInFlightRef.current) return;
    submitClaimInFlightRef.current = true;
    setIsSubmittingClaim(true);

    try {
      setClaimQuotaErrorRows([]);
      if (!isMonthInAllowedWindow(claimForm.monthYear)) {
        alert(`Claim Month must be between ${minAllowedMonth} and ${todayMonth}.`);
        return;
      }
      const invalidAmount = claimForm.items.some((item) => Number(item.amount) <= 0);
      if (invalidAmount) {
        alert('Each item amount must be greater than 0.');
        return;
      }
      const missingAttachment = claimForm.items.some((item) => !item.file);
      if (missingAttachment) {
        alert('Each claim item must include an attachment before submitting.');
        return;
      }
      const missingProjectDescription = claimForm.items.some((item) =>
        requiresDescriptionField(item.claimType) && !String(item.description || '').trim()
      );
      if (missingProjectDescription) {
        setClaimValidationError(true);
        alert('Desc./Project Name is required when Claim Type is Projects or HR and Others.');
        return;
      }
      const missingProjectBusinessName = claimForm.items.some((item) =>
        requiresBusinessNameField(item.claimType) && !String(item.businessName || '').trim()
      );
      if (missingProjectBusinessName) {
        setClaimValidationError(true);
        alert('Business Name is required when Claim Type is Projects.');
        return;
      }
      const missingProjectReceiptNumber = claimForm.items.some((item) =>
        requiresReceiptNumberField(item.claimType) && !String(item.receiptNumber || '').trim()
      );
      if (missingProjectReceiptNumber) {
        setClaimValidationError(true);
        alert('Receipt / Invoice No. is required when Claim Type is Projects.');
        return;
      }
      setClaimValidationError(false);

      // Prepare claim data for API
      const claimData = {
        entity: currentUser.entity,
        month_year: claimForm.monthYear,
        remarks: claimForm.remarks,
        items: claimForm.items.map(item => ({
          claim_type: item.claimType,
          expense_type: item.expensesType,
          description: item.description,
          receipt_amount: parseFloat(item.amount || 0),
          business_name: item.businessName || '',
          receipt_number: item.receiptNumber || ''
        }))
      };

      // Debug: Log the data being sent
      console.log('Submitting claim data:', claimData);
      console.log('Current user:', currentUser);

      // Create draft claim
      const createdClaim = await claimAPI.createDraftClaim(claimData);

      // Upload files to OSS by matching claim items by index
      const createdDetailsResponse = await claimAPI.getClaimDetails(createdClaim.claimId);
      const createdItems = createdDetailsResponse?.claim?.items || [];
      for (let idx = 0; idx < claimForm.items.length; idx += 1) {
        const localItem = claimForm.items[idx];
        const createdItem = createdItems[idx];
        if (localItem?.file && createdItem?.id) {
          await fileAPI.uploadClaimItemFile(createdClaim.claimId, createdItem.id, localItem.file);
        }
      }

      // Submit the claim immediately
      await claimAPI.submitClaim(createdClaim.claimId);

      try {
        const submittedDetailsResponse = await claimAPI.getClaimDetails(createdClaim.claimId);
        await notifyManagerClaimWebhook({
          action: 'submitted',
          claimId: createdClaim.claimId,
          claimData,
          claimDetails: submittedDetailsResponse
        });
      } catch (webhookError) {
        console.warn('Claim submitted, but webhook notification failed:', webhookError);
      }

      // Reload claims list
      await loadUserData();

      // Reset form
      setClaimForm({
        monthYear: todayMonth,
        remarks: '',
        items: [buildDefaultClaimItem(currentUser?.entity || DEFAULT_ENTITY_NAME)]
      });

      setShowAddClaimModal(false);
      alert('Claim submitted successfully!');
    } catch (error) {
      console.error('Failed to submit claim:', error);
      const quotaRows = resolveQuotaErrorRows(error.message, claimForm.items);
      if (quotaRows.length > 0) {
        setClaimQuotaErrorRows(quotaRows);
      }
      alert(error.message || 'Failed to submit claim. Please try again.');
    } finally {
      submitClaimInFlightRef.current = false;
      setIsSubmittingClaim(false);
    }
  };

  // Check if a claim can be edited
  const canEditClaim = (claim) => {
    if (!claim || !currentUser) return false;
    if (String(claim.userId ?? '') !== String(currentUser.id ?? '')) return false;
    const rawStatus = String(claim.rawStatus || '').toLowerCase();
    // Rejected claims are not editable.
    if (
      rawStatus === 'rejected' ||
      claim.status === 'Rejected' ||
      claim.financeApproval === 'Rejected' ||
      claim.managerApproval === 'Rejected'
    ) return false;
    // Returned claims are editable.
    if (rawStatus === 'returned' || claim.financeApproval === 'Returned' || claim.status === 'Returned') return true;
    // Can edit while waiting for first approval and no approver action has been taken yet.
    if (
      (rawStatus === 'pending_manager' || rawStatus === 'pending') &&
      (claim.managerApproval === 'Pending' || claim.managerApproval === '-') &&
      claim.financeApproval === 'Pending'
    ) return true;
    // Backward compatibility for records without reliable raw status mapping.
    if (claim.managerApproval === 'Pending' && claim.financeApproval === 'Pending') return true;
    // Cannot edit if manager has approved/rejected
    return false;
  };

  const canTakeApprovalAction = (claim) => {
    if (!claim || !currentUser) return false;
    if (!canPerformApprovalActions) return false;
    if (!claim.submissionDateRaw && !claim.submissionDate) return false;
    return canCurrentUserActOnClaim(claim, currentUser, accessProfile);
  };

  const loadEditClaim = async (claim) => {
    try {
      const response = await claimAPI.getClaimDetails(claim.claimId);
      const claimData = response.claim;

      const detailedClaim = {
        ...claim,
        items: claimData.items || [],
        remarks: claimData.remarks || claim.remarks
      };

      setEditClaimData(detailedClaim);
      setEditClaimOriginalData({
        entity: claimData.entity || claim.entity || currentUser?.entity || '',
        department: claimData.department || claim.department || currentUser?.department || '',
        month_year: claimData.month_year || claim.monthYear || '',
        remarks: claimData.remarks || claim.remarks || '',
        items: (claimData.items || []).map((item) => ({
          claim_type: item.claim_type || item.claimType || 'Projects',
          expense_type: item.expense_type || item.expensesType || 'DELIVERY CHARGES (FOR PROJECTS)',
          description: item.description || '',
          receipt_amount: Number(resolveItemAmount(item) || 0),
          business_name: item.business_name || '',
          receipt_number: item.receipt_number || ''
        }))
      });
      setEditClaimForm({
        monthYear: claimData.month_year || claim.monthYear || '',
        remarks: claimData.remarks || claim.remarks || '',
        items: (claimData.items || []).map(item => ({
          claimType: item.claim_type || item.claimType || 'Projects',
          expensesType: item.expense_type || item.expensesType || 'DELIVERY CHARGES (FOR PROJECTS)',
          description: item.description || '',
          amount: resolveItemAmount(item).toString(),
          businessName: item.business_name || '',
          receiptNumber: item.receipt_number || '',
          file: null,
          existingFile: item.file_url || (typeof item.file_path === 'string' && /^https?:\/\//i.test(item.file_path) ? item.file_path : null),
          fileName: item.file_name || null
        }))
      });
      setEditValidationError(false);
      setShowEditClaimModal(true);
    } catch (error) {
      console.error('Failed to load claim for editing:', error);
      alert('Failed to load claim details for editing.');
    }
  };

  const handleEditUpdateItem = (index, field, value) => {
    const newItems = [...editClaimForm.items];
    newItems[index][field] = value;
    if (field === 'claimType') {
      newItems[index].expensesType = getDefaultExpenseTypeForEntityClaim(editClaimData?.entity || currentUser?.entity, value);
    }
    if (editQuotaErrorRows.length > 0) {
      setEditQuotaErrorRows([]);
    }
    setEditClaimForm({ ...editClaimForm, items: newItems });
  };

  const handleEditRemoveItem = (index) => {
    if (editClaimForm.items.length <= 1) return;
    const newItems = editClaimForm.items.filter((_, i) => i !== index);
    setEditClaimForm({ ...editClaimForm, items: newItems });
  };

  const handleEditAddItem = () => {
    const editEntity = editClaimData?.entity || currentUser?.entity;
    const defaultClaimType = getDefaultClaimTypeForEntity(editEntity);
    setEditClaimForm({
      ...editClaimForm,
      items: [
        ...editClaimForm.items,
        {
          claimType: defaultClaimType,
          expensesType: getDefaultExpenseTypeForEntityClaim(editEntity, defaultClaimType),
          description: '',
          amount: '',
          businessName: '',
          receiptNumber: '',
          file: null,
          existingFile: null,
          fileName: null
        }
      ]
    });
  };

  const handleResubmitClaim = async () => {
    if (resubmitClaimInFlightRef.current) return;
    resubmitClaimInFlightRef.current = true;
    setIsResubmittingClaim(true);

    const isDraftEdit = String(editClaimData?.rawStatus || '').toLowerCase() === 'draft' ||
      String(editClaimData?.status || '').toLowerCase() === 'draft';

    // Validate mandatory fields
    const hasErrors = editClaimForm.items.some(item =>
      !item.claimType || !item.expensesType || !item.amount
    );
    if (!editClaimForm.monthYear || hasErrors) {
      setEditValidationError(true);
      return;
    }
    if (!isMonthInAllowedWindow(editClaimForm.monthYear)) {
      alert(`Claim Month must be between ${minAllowedMonth} and ${todayMonth}.`);
      return;
    }
    const invalidAmount = editClaimForm.items.some((item) => Number(item.amount) <= 0);
    if (invalidAmount) {
      alert('Each item amount must be greater than 0.');
      return;
    }
    const missingAttachment = editClaimForm.items.some((item) => !item.file && !item.existingFile);
    if (missingAttachment) {
      alert(`Each claim item must include an attachment before ${isDraftEdit ? 'submitting' : 'resubmitting'}.`);
      return;
    }
    const missingProjectDescription = editClaimForm.items.some((item) =>
      requiresDescriptionField(item.claimType) && !String(item.description || '').trim()
    );
    if (missingProjectDescription) {
      setEditValidationError(true);
      alert('Desc./Project Name is required when Claim Type is Projects or HR and Others.');
      return;
    }
    const missingProjectBusinessName = editClaimForm.items.some((item) =>
      requiresBusinessNameField(item.claimType) && !String(item.businessName || '').trim()
    );
    if (missingProjectBusinessName) {
      setEditValidationError(true);
      alert('Business Name is required when Claim Type is Projects.');
      return;
    }
    const missingProjectReceiptNumber = editClaimForm.items.some((item) =>
      requiresReceiptNumberField(item.claimType) && !String(item.receiptNumber || '').trim()
    );
    if (missingProjectReceiptNumber) {
      setEditValidationError(true);
      alert('Receipt / Invoice No. is required when Claim Type is Projects.');
      return;
    }

    let claimUpdated = false;
    try {
      setEditQuotaErrorRows([]);
      // Prepare updated claim data
      const claimData = {
        entity: currentUser.entity,
        department: editClaimData?.department || currentUser?.department || '',
        month_year: editClaimForm.monthYear,
        remarks: editClaimForm.remarks,
        items: editClaimForm.items.map(item => ({
          claim_type: item.claimType,
          expense_type: item.expensesType,
          description: item.description,
          receipt_amount: parseFloat(item.amount || 0),
          business_name: item.businessName || '',
          receipt_number: item.receiptNumber || ''
        }))
      };

      // Update claim content first
      await claimAPI.updateClaim(editClaimData.claimId, claimData);
      claimUpdated = true;

      // Upload new files (if any) to OSS by matching updated items by index
      const updatedDetailsResponse = await claimAPI.getClaimDetails(editClaimData.claimId);
      const updatedItems = updatedDetailsResponse?.claim?.items || [];
      for (let idx = 0; idx < editClaimForm.items.length; idx += 1) {
        const localItem = editClaimForm.items[idx];
        const updatedItem = updatedItems[idx];
        if (localItem?.file && updatedItem?.id) {
          await fileAPI.uploadClaimItemFile(editClaimData.claimId, updatedItem.id, localItem.file);
        }
      }

      if (isDraftEdit) {
        await claimAPI.submitClaim(editClaimData.claimId);
      } else {
        await claimAPI.resubmitClaim(editClaimData.claimId);
      }

      try {
        const submittedDetailsResponse = await claimAPI.getClaimDetails(editClaimData.claimId);
        await notifyManagerClaimWebhook({
          action: isDraftEdit ? 'submitted' : 'resubmitted',
          claimId: editClaimData.claimId,
          claimData,
          claimDetails: submittedDetailsResponse
        });
      } catch (webhookError) {
        console.warn('Claim updated, but webhook notification failed:', webhookError);
      }

      await loadUserData();
      setShowEditClaimModal(false);
      setEditClaimData(null);
      setEditClaimOriginalData(null);
      alert(`Claim ${isDraftEdit ? 'submitted' : 'resubmitted'} successfully!`);
    } catch (error) {
      console.error(`Failed to ${isDraftEdit ? 'submit' : 'resubmit'} claim:`, error);
      if (claimUpdated && editClaimOriginalData) {
        try {
          await claimAPI.updateClaim(editClaimData.claimId, editClaimOriginalData);
          await loadUserData();
        } catch (rollbackError) {
          console.error('Failed to rollback claim after unsuccessful submit/resubmit:', rollbackError);
        }
      }
      const quotaRows = resolveQuotaErrorRows(error.message, editClaimForm.items);
      if (quotaRows.length > 0) {
        setEditQuotaErrorRows(quotaRows);
      }
      alert(error.message || `Failed to ${isDraftEdit ? 'submit' : 'resubmit'} claim. Please try again.`);
    } finally {
      resubmitClaimInFlightRef.current = false;
      setIsResubmittingClaim(false);
    }
  };

  const handleApproveClaim = async (claimId, approverType, decision) => {
    try {
      if (decision === 'Approved') {
        await claimAPI.approveClaim(claimId, approverType);
      } else if (decision === 'Rejected') {
        const reason = rejectReason || prompt('Please provide a reason for rejection:');
        if (!reason) return;
        await claimAPI.rejectClaim(claimId, approverType, reason);
      }

      // Reload both user claims and pending approvals
      await loadUserData();
      if (canPerformApprovalActions) {
        await loadPendingApprovals();
      }

      alert(`Claim ${decision.toLowerCase()} successfully!`);
    } catch (error) {
      console.error('Failed to update claim:', error);
      alert(error.message || 'Failed to update claim. Please try again.');
    }
  };

  const exportToExcel = async () => {
    const anyStatusSelected = Object.values(exportFilters.statusFilters || {}).some(Boolean);
    if (!anyStatusSelected) {
      alert('Please select at least one claim status.');
      return;
    }
    const selectedEntities = Object.entries(exportFilters.entityFilters || {})
      .filter(([, selected]) => Boolean(selected))
      .map(([entity]) => entity);

    const dateType = exportFilters.dateType || 'last_action';
    const startValue = dateType === 'submission' ? exportFilters.submissionStart : exportFilters.lastActionStart;
    const endValue = dateType === 'submission' ? exportFilters.submissionEnd : exportFilters.lastActionEnd;

    if (!startValue || !endValue) {
      alert('Please select a start and end date.');
      return;
    }

    const startDate = parseDateForFilter(startValue);
    if (!startDate) {
      alert('Invalid start date.');
      return;
    }
    startDate.setHours(0, 0, 0, 0);
    const endDate = parseDateForFilter(endValue);
    if (!endDate) {
      alert('Invalid end date.');
      return;
    }
    endDate.setHours(23, 59, 59, 999);

    const reportSource = canAccessReports
      ? reportClaims
      : claims;

    let filteredClaims = reportSource.filter(c => {
      if (c.status === 'Draft') return false;
      if (selectedEntities.length > 0 && !selectedEntities.includes(c.entity)) return false;
      const filterDateValue = dateType === 'submission'
        ? getSubmissionDateForFilter(c)
        : getLastActionDateForFilter(c);
      const actionDate = parseDateForFilter(filterDateValue);
      if (actionDate && (actionDate < startDate || actionDate > endDate)) {
        return false;
      }

      const status = c.status;
      if (status === 'Approved' && !exportFilters.statusFilters.approved) return false;
      if (status === 'Pending' && !exportFilters.statusFilters.pending) return false;
      if (status === 'Returned' && !exportFilters.statusFilters.returned) return false;
      if (status === 'Rejected' && !exportFilters.statusFilters.rejected) return false;
      return true;
    });

    if (filteredClaims.length === 0) {
      alert('No claims found for the selected export filters.');
      return;
    }

    // Ensure item-level data exists for export
    const enrichedClaims = await Promise.all(
      filteredClaims.map(async (claim) => {
        if (claim.items && claim.items.length > 0) return claim;
        try {
          const response = await claimAPI.getClaimDetails(claim.claimId);
          return { ...claim, items: response?.claim?.items || [] };
        } catch (error) {
          console.error('Failed to load claim items for export:', error);
          return claim;
        }
      })
    );

    const exportHeaders = [
      'DocNo', 'DocDate', 'DocType', 'DealWith', 'Description', 'CurrencyCode', 'CurrencyRate', 'ToTaxCurrencyRate',
      'InclusiveTax', 'AccNo', 'ToAccountRate', 'ProjNo', 'DeptNo', 'TaxType', 'DetailDescription',
      'FurtherDescription', 'SalesAgent', 'Amount', 'TaxableAmt', 'RCHQAmount', 'PaymentMethod', 'ChequeNo',
      'PaymentAmt', 'BankCharge', 'ToBankRate', 'BankChargeTaxType', 'BankChargeTaxRefNo', 'BankChargeProjNo',
      'BankChargeDeptNo', 'PaymentBy', 'FloatDay', 'IsRCHQ', 'RCHQDate', 'TaxBName', 'TaxBRNo',
      'TaxRegisterNo', 'TaxRefNo', 'TaxBillDate'
    ];

    const rows = [];

    enrichedClaims.forEach((c) => {
      const items = c.items && c.items.length > 0 ? c.items : [null];
      const docDateRaw = c.lastActionDateRaw || c.lastActionDate || c.submissionDateRaw || c.submissionDate;
      const docDate = formatDocDate(docDateRaw);
      const baseDescription = formatClaimMonthLabel(c.monthYear);
      const dealWith = c.claimant || c.requestor || currentUser?.name || '';
      const deptNo = c.department || '';
      const claimMonthMmYy = formatClaimMonthMmYy(c.monthYear);

      items.forEach((item, idx) => {
        const claimType = item ? (item.claim_type || item.claimType || '') : '';
        const expenseType = item ? (item.expense_type || item.expensesType || item.expenseType || '') : '';
        const accNo = resolveGlCode(c.entity, expenseType);
        const projectName = item ? (item.description || item.desc || item.project_name || '') : '';
        const businessName = item ? (item.business_name || item.businessName || '') : '';
        const receiptNumber = item ? (item.receipt_number || item.receiptNumber || '') : '';
        const claimantName = c.claimant || c.requestor || currentUser?.name || '';
        const detailDescription = item
          ? (
            isProjectsClaimType(claimType)
              ? `${businessName || '-'} @ ${projectName || '-'} (${receiptNumber || '-'})`
              : `${expenseType || '-'} ${claimMonthMmYy || ''}${claimMonthMmYy ? ' ' : ''}(${claimantName || '-'})`
          )
          : '';
        const amountValue = item ? resolveItemAmount(item) : c.amount || 0;
        const docNo = c.id || '';

        const row = new Array(exportHeaders.length).fill('');
        if (idx === 0) {
          row[0] = docNo;
          row[1] = docDate;
          row[2] = 'PV';
          row[3] = dealWith;
          row[4] = baseDescription;
          if (docNo) {
            row[20] = 'BANK - CIMB';
            row[21] = 'GIRO';
          }
          row[22] = c.amount ? Number(c.amount).toFixed(2) : '';
        }
        row[9] = accNo;
        row[11] = '';
        row[12] = deptNo;
        row[14] = detailDescription;
        row[17] = amountValue ? Number(amountValue).toFixed(2) : '';
        rows.push(row);
      });
    });

    const ws = XLSX.utils.aoa_to_sheet([exportHeaders, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Claims Report');

    // Set column widths
    const colWidths = [
      { wch: 14 },  // DocNo
      { wch: 12 },  // DocDate
      { wch: 8 },   // DocType
      { wch: 26 },  // DealWith
      { wch: 20 },  // Description
      { wch: 12 },  // CurrencyCode
      { wch: 12 },  // CurrencyRate
      { wch: 16 },  // ToTaxCurrencyRate
      { wch: 12 },  // InclusiveTax
      { wch: 12 },  // AccNo
      { wch: 12 },  // ToAccountRate
      { wch: 12 },  // ProjNo
      { wch: 16 },  // DeptNo
      { wch: 12 },  // TaxType
      { wch: 40 },  // DetailDescription
      { wch: 24 },  // FurtherDescription
      { wch: 16 },  // SalesAgent
      { wch: 12 },  // Amount
      { wch: 12 },  // TaxableAmt
      { wch: 12 },  // RCHQAmount
      { wch: 16 },  // PaymentMethod
      { wch: 12 },  // ChequeNo
      { wch: 12 },  // PaymentAmt
      { wch: 12 },  // BankCharge
      { wch: 12 },  // ToBankRate
      { wch: 16 },  // BankChargeTaxType
      { wch: 18 },  // BankChargeTaxRefNo
      { wch: 16 },  // BankChargeProjNo
      { wch: 16 },  // BankChargeDeptNo
      { wch: 16 },  // PaymentBy
      { wch: 10 },  // FloatDay
      { wch: 10 },  // IsRCHQ
      { wch: 12 },  // RCHQDate
      { wch: 18 },  // TaxBName
      { wch: 16 },  // TaxBRNo
      { wch: 18 },  // TaxRegisterNo
      { wch: 16 },  // TaxRefNo
      { wch: 12 }   // TaxBillDate
    ];
    ws['!cols'] = colWidths;

    // Generate file
    XLSX.writeFile(wb, `claims_report_${startValue}_to_${endValue}.xlsx`);

    addAuditLog('export_report', `Report exported from ${startValue} to ${endValue}`, currentUser.id);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Returned': return 'text-cyan-600 bg-cyan-50 border border-cyan-200';
      case 'Pending': return 'text-gray-600 bg-gray-100 border border-gray-200';
      case 'Approved': return 'text-green-600 bg-green-100 border border-green-200';
      case 'Rejected': return 'text-red-600 bg-red-100 border border-red-200';
      case 'Draft': return 'text-amber-600 bg-amber-50 border border-amber-200';
      default: return 'text-gray-600 bg-gray-50 border border-gray-200';
    }
  };

  // Login Screen
  if (authState === 'login' && !isPublicActivityMode) {
    return (
      <div className="min-h-screen bg-gray-600 flex flex-col">
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
          * { font-family: 'Inter', sans-serif; }
        `}</style>

        <div className="flex-1 flex items-center justify-center p-4 pb-20">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-8">
            {/* Logo */}
            <div className="flex items-center justify-center mb-8">
              <img src="/logo.png" alt="Mogster Claims" className="h-10 w-auto" />
            </div>

            {DEV_MODE && (
              <div className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded text-center">
                <p className="text-xs text-blue-600">DEV MODE: Use OTP 000000</p>
              </div>
            )}

            <div className="space-y-4">
              {/* Email Input */}
              <div>
                <label className="block text-sm text-gray-700 mb-2">
                  Webmail
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleRequestOTP('webmail');
                    }
                  }}
                  placeholder="Please Fill In"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
                />
              </div>

              {/* Request OTP Buttons */}
              <div>
                <label className="block text-sm text-grey-700 mb-3">
                  Request OTP via
                </label>
                <div className="space-y-3">
                  <button
                    onClick={() => handleRequestOTP('webmail')}
                    className="w-full bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-4 py-3 rounded-md transition-colors text-base font-semibold leading-none"
                  >
                    Webmail
                  </button>
                  <button
                    disabled
                    className="w-full bg-gray-300 text-gray-500 px-4 py-3 rounded-md text-base font-semibold leading-none cursor-not-allowed"
                  >
                    Telegram (Coming Soon)
                  </button>
                </div>
              </div>

              {/* Mandatory Fields Note */}
              <p className="text-xs text-red-500 text-center italic mt-4">
                *Please fill in all mandatory fields
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gray-900 py-4">
          <p className="text-center text-gray-400 text-xs px-4">Copyright © The Makeover Guys Sdn Bhd (1125623-P). All rights reserved</p>
        </div>
      </div>
    );
  }

  // OTP Screen
  if (authState === 'otp' && !isPublicActivityMode) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#4b5563' }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&display=swap');
          .otp-screen * { font-family: 'DM Sans', sans-serif; }
        `}</style>

        <div className="otp-screen flex-1 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-md w-full max-w-[410px] p-8">
            <button
              onClick={() => {
                cancelSessionRestoreRef.current = true;
                localStorage.removeItem('tmg_token');
                setCurrentUser(null);
                setAuthState('login');
                setOtpResendCooldown(0);
                setOtpResendState('idle');
                setOtpHasResent(false);
              }}
              className="flex items-center gap-2 text-gray-900 hover:text-gray-700 mb-10 text-sm font-medium"
            >
              <ArrowLeft className="w-4 h-4 stroke-[2.5]" />
              Back
            </button>

            <div className="mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-1">Enter OTP Code</h2>
              <p className="text-sm text-gray-400">
                The verification code is sent to your {otpMethod === 'webmail' ? 'webmail' : 'Telegram'}
              </p>
              {DEV_MODE && <p className="text-xs text-blue-600 mt-2">DEV MODE: Enter 000000</p>}
            </div>

            <div>
              <label className="block text-sm text-gray-700 mb-3">
                Verification Code<span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    maxLength="1"
                    inputMode="numeric"
                    autoComplete={index === 0 ? 'one-time-code' : 'off'}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    onPaste={(e) => handleOtpPaste(index, e)}
                    className="w-[48px] h-[48px] text-center text-xl font-bold border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  />
                ))}
              </div>

              <button
                onClick={handleVerifyOTP}
                disabled={isVerifyingOtp}
                className={`w-full font-semibold px-4.5 rounded-full transition-colors text-sm mt-10 ${
                  isVerifyingOtp
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-yellow-400 text-gray-900 hover:bg-yellow-500'
                }`}
              >
                {isVerifyingOtp ? 'Verifying...' : 'Verify'}
              </button>

              <div className="text-center mt-6">
                <span className="text-sm text-gray-500">Didn't receive code?</span>
                {'  '}
                <button
                  onClick={() => handleRequestOTP(otpMethod)}
                  disabled={(otpHasResent && otpResendCooldown > 0) || otpResendState === 'sending'}
                  className={`text-sm font-medium transition-colors ${
                    (otpHasResent && otpResendCooldown > 0) || otpResendState === 'sending'
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-red-500 hover:text-red-600'
                  }`}
                >
                  {otpResendState === 'sending'
                    ? 'Sending...'
                    : otpHasResent && otpResendCooldown > 0
                      ? `Resend successful (${otpResendCooldown}s)`
                      : 'Resend'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="py-4">
          <p className="text-center text-gray-400 text-xs px-4">Copyright © The Makeover Guys Sdn Bhd (1125623-P) All rights reserved</p>
        </div>
      </div>
    );
  }

  // Main Application - Using simplified responsive approach
  // Due to file size constraints, the main app section would continue here with:
  // - Responsive header with mobile menu
  // - Card-based layout for mobile
  // - Table layout for desktop
  // - All three views (My Claims, Approval, Report)
  // - Add Claims modal
  
  const desktopTableDataSource = (() => {
    let dataSource = [];

    if (activeView === 'dashboard') {
      if (dashboardTab === 'returned') {
        // Returned claims on Dashboard must only show returned claims.
        dataSource = claims.filter(c => c.status === 'Returned');
      } else {
        dataSource = canPerformApprovalActions
          ? pendingApprovals.filter(c => c.status === 'Pending')
          : [];
      }
      if (searchQuery && searchQuery.trim()) {
        const query = searchQuery.trim().toLowerCase();
        dataSource = dataSource.filter(c => c.id && c.id.toLowerCase().includes(query));
      }
    } else if (activeView === 'approval') {
      dataSource = canPerformApprovalActions
        ? pendingApprovals
        : [];
      if (fromDate || toDate) {
        dataSource = dataSource.filter(c =>
          isDateWithinRange(
            c.lastActionDateRaw || c.lastActionDate || c.submissionDateRaw || c.submissionDate,
            fromDate,
            toDate
          )
        );
      }
      if (searchQuery && searchQuery.trim()) {
        const query = searchQuery.trim().toLowerCase();
        dataSource = dataSource.filter(c => c.id && c.id.toLowerCase().includes(query));
      }
    } else if (activeView === 'report') {
      const reportSource = canAccessReports
        ? reportClaims
        : claims;
      dataSource = reportSource.filter(c => {
        if (c.status === 'Draft') return false;
        if (fromDate || toDate) {
          return isDateWithinRange(getLastActionDateForFilter(c), fromDate, toDate);
        }
        return true;
      });
      const anyFilterActive = reportStatusFilters.approved || reportStatusFilters.pending || reportStatusFilters.returned || reportStatusFilters.rejected;
      if (anyFilterActive) {
        dataSource = dataSource.filter(c => {
          if (reportStatusFilters.approved && c.status === 'Approved') return true;
          if (reportStatusFilters.pending && c.status === 'Pending') return true;
          if (reportStatusFilters.returned && c.status === 'Returned') return true;
          if (reportStatusFilters.rejected && c.status === 'Rejected') return true;
          return false;
        });
      }
      if (searchQuery && searchQuery.trim()) {
        const query = searchQuery.trim().toLowerCase();
        dataSource = dataSource.filter(c => c.id && c.id.toLowerCase().includes(query));
      }
    } else if (activeView === 'my-claims') {
      dataSource = claims.filter(c => {
        if (!fromDate && !toDate) return true;
        return isDateWithinRange(getSubmissionDateForFilter(c), fromDate, toDate);
      });
      if (searchQuery && searchQuery.trim()) {
        const query = searchQuery.trim().toLowerCase();
        dataSource = dataSource.filter(c => c.id && c.id.toLowerCase().includes(query));
      }
    }

    return dataSource;
  })();

  const hasDesktopRows = desktopTableDataSource.length > 0;
  const isDashboardClaimsView = activeView === 'dashboard';
  const isApprovalClaimsView = activeView === 'approval';
  const isReportClaimsView = activeView === 'report';
  const isMyClaimsView = activeView === 'my-claims';
  const showClaimantColumn = isApprovalClaimsView || isDashboardClaimsView || isReportClaimsView;
  const showClaimTypeAndDescColumns = isApprovalClaimsView || isDashboardClaimsView || isMyClaimsView || isReportClaimsView;
  const showDepartmentColumn = isApprovalClaimsView || isDashboardClaimsView || isReportClaimsView;
  const showApprovalMetaColumns = isApprovalClaimsView || isDashboardClaimsView || isMyClaimsView || isReportClaimsView;
  const desktopTableMinWidthPx = showApprovalMetaColumns ? 2100 : 1500;
  const desktopColumnCount =
    2 +
    (showClaimantColumn ? 1 : 0) +
    (showClaimTypeAndDescColumns ? 1 : 0) +
    (showClaimTypeAndDescColumns ? 1 : 0) +
    1 +
    (showDepartmentColumn ? 1 : 0) +
    (isReportClaimsView ? 1 : 0) +
    1 +
    (!isReportClaimsView ? 1 : 0) +
    (showApprovalMetaColumns ? 1 : 0) +
    (showApprovalMetaColumns ? 1 : 0) +
    (showApprovalMetaColumns ? 1 : 0) +
    (showApprovalMetaColumns ? 1 : 0) +
    1;
  const desktopEmptyState = !hasDesktopRows
    ? (() => {
        if (activeView === 'dashboard' && dashboardTab === 'returned') {
          return {
            icon: null,
            title: 'No returned claims',
            description: "Awesome! No returned claims here - let's keep it that way.",
          };
        }

        if (activeView === 'approval' || (activeView === 'dashboard' && dashboardTab === 'pending')) {
          return {
            title: 'No claims for approval',
            description: 'Everything is clear for now. New approval requests will appear here.',
          };
        }

        if (activeView === 'report') {
          return {
            title: 'No claims found',
            description: 'Try adjusting the date range or filters to see matching claims.',
          };
        }

        if (activeView === 'my-claims') {
          return {
            title: 'No claims found',
            description: 'Try changing the date or adding a new claim.',
          };
        }

        return null;
      })()
    : null;

  const renderApprovalStatusBlock = (approval, approver, approvalDate, reason) => {
    const normalized = String(approval || '').trim();
    const lower = normalized.toLowerCase();
    const isApproved = lower === 'approved';
    const isRejected = lower === 'rejected';
    const isReturned = lower === 'returned';
    const isPending = lower === 'pending';
    const hasStatus = normalized && normalized !== '-';
    const showDetails = (isApproved || isRejected || isReturned) && (approver || approvalDate);

    const statusClass = isApproved
      ? 'text-green-600 font-semibold'
      : isRejected
        ? 'text-red-500 font-semibold'
        : isReturned
          ? 'text-blue-600 italic font-semibold'
          : hasStatus
            ? 'text-[#4A5568] italic'
            : 'text-[#4A5568]';

    return (
      <div className="leading-6">
        <div className={statusClass}>{hasStatus ? normalized : '-'}</div>
        {!isPending && reason && (
          <div className={`mt-0.5 ${isRejected ? 'text-red-500 italic' : isReturned ? 'text-blue-600 italic' : 'text-[#6B7280] italic'}`}>
            {reason}
          </div>
        )}
        {showDetails && (
          <>
            {approver && <div className="mt-0.5 text-[#6B7280] italic">By {approver}:</div>}
            {approvalDate && <div className="text-[#6B7280] italic">{approvalDate}</div>}
          </>
        )}
      </div>
    );
  };

  const renderActorDateBlock = (actorName, dateValue) => {
    const hasDate = Boolean(dateValue && dateValue !== '-');
    const hasActor = Boolean(actorName && actorName !== '-');

    if (!hasDate && !hasActor) return '-';

    return (
      <div className="leading-6">
        {hasActor && <div className="mt-0.5 text-[#6B7280] italic">By {actorName}:</div>}
        {hasDate && <div className="text-[#1F2937]">{dateValue}</div>}
      </div>
    );
  };
  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        * { font-family: 'Inter', sans-serif; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        .desktop-table-scroll {
          -ms-overflow-style: none;
          scrollbar-width: none;
          cursor: grab;
          touch-action: pan-y;
          overscroll-behavior-x: contain;
        }
        .desktop-table-scroll::-webkit-scrollbar {
          display: none;
          height: 0;
        }
        .desktop-table-scroll,
        .desktop-table-scroll * {
          -webkit-user-drag: none;
        }
        .desktop-table-scroll.is-dragging,
        .desktop-table-bottom-scrollbar.is-dragging {
          cursor: grabbing;
        }
        .desktop-table-scroll.is-dragging,
        .desktop-table-scroll.is-dragging * {
          user-select: none !important;
          -webkit-user-select: none !important;
        }
        body.desktop-table-dragging,
        body.desktop-table-dragging * {
          user-select: none !important;
          -webkit-user-select: none !important;
        }
        .desktop-table-bottom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #a3a3a3 #e5e7eb;
          cursor: grab;
          touch-action: pan-x;
        }
        .desktop-table-bottom-track {
          position: relative;
          height: 12px;
          width: 100%;
          border-radius: 9999px;
          background: #e5e7eb;
          cursor: pointer;
          touch-action: pan-x;
        }
        .desktop-table-bottom-thumb {
          position: absolute;
          top: 0;
          left: 0;
          height: 12px;
          border-radius: 9999px;
          background: #9ca3af;
          cursor: grab;
          transition: background-color 0.15s ease;
        }
        .desktop-table-bottom-thumb:hover,
        .desktop-table-bottom-thumb.is-dragging {
          background: #6b7280;
        }
        .desktop-table-bottom-thumb.is-dragging {
          cursor: grabbing;
        }
        .desktop-table-bottom-scrollbar::-webkit-scrollbar {
          height: 12px;
        }
        .desktop-table-bottom-scrollbar::-webkit-scrollbar-track {
          background: #e5e7eb;
          border-radius: 9999px;
        }
        .desktop-table-bottom-scrollbar::-webkit-scrollbar-thumb {
          background: #a3a3a3;
          border-radius: 9999px;
        }
      `}</style>

      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-4">
              <img src="/logo.png" alt="Mogster Claims" className="h-8 sm:h-10 w-auto" />            </div>
            <button onClick={() => setShowMobileMenu(!showMobileMenu)} className="md:hidden p-2">
              <Menu className="w-6 h-6" />
            </button>
            <div className="hidden md:flex items-center gap-6">
              <button
                onClick={() => { setActiveView('dashboard'); setShowManageDropdown(false); }}
                className={`text-sm font-medium pb-1 transition-colors ${activeView === 'dashboard' ? 'text-yellow-600 border-b-2 border-yellow-500' : 'text-gray-600 hover:text-gray-900'}`}
              >
                Dashboard
              </button>
              <button
                onClick={() => { setActiveView('my-claims'); setShowManageDropdown(false); }}
                className={`text-sm font-medium pb-1 transition-colors ${activeView === 'my-claims' ? 'text-yellow-600 border-b-2 border-yellow-500' : 'text-gray-600 hover:text-gray-900'}`}
              >
                My Claims
              </button>
              {canAccessApprovals && (
                <button
                  onClick={() => { setActiveView('approval'); setShowManageDropdown(false); }}
                  className={`text-sm font-medium pb-1 transition-colors ${activeView === 'approval' ? 'text-yellow-600 border-b-2 border-yellow-500' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  Approvals
                </button>
              )}
              {canAccessReports && (
                <button
                  onClick={() => { setActiveView('report'); setShowManageDropdown(false); }}
                  className={`text-sm font-medium pb-1 transition-colors ${activeView === 'report' ? 'text-yellow-600 border-b-2 border-yellow-500' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  Reports
                </button>
              )}
              {canAccessManage && (
                <div className="relative manage-dropdown-container">
                  <button
                    onClick={() => setShowManageDropdown(!showManageDropdown)}
                    className={`text-sm font-medium pb-1 transition-colors flex items-center gap-1 ${
                      MANAGE_CORE_VIEWS.has(activeView) || MANAGE_FINANCE_ONLY_VIEWS.has(activeView)
                        ? 'text-yellow-600 border-b-2 border-yellow-500'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Manage
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </button>
                  {showManageDropdown && (
                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 min-w-[180px]">
                      {canAccessManageUsers && (
                        <button
                          onClick={() => { setActiveView('manage-users'); setShowManageDropdown(false); }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-yellow-50 transition-colors ${activeView === 'manage-users' ? 'text-yellow-700 font-medium bg-yellow-50' : 'text-gray-700'}`}
                        >
                          Manage User
                        </button>
                      )}
                      {canAccessManageCompany && (
                        <>
                          <button
                            onClick={() => { setActiveView('manage-companies'); setShowManageDropdown(false); }}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-yellow-50 transition-colors ${activeView === 'manage-companies' ? 'text-yellow-700 font-medium bg-yellow-50' : 'text-gray-700'}`}
                          >
                            Manage Company
                          </button>
                          <button
                            onClick={() => { setActiveView('manage-departments'); setShowManageDropdown(false); }}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-yellow-50 transition-colors ${activeView === 'manage-departments' ? 'text-yellow-700 font-medium bg-yellow-50' : 'text-gray-700'}`}
                          >
                            Manage Department
                          </button>
                          <button
                            onClick={() => { setActiveView('manage-webhooks'); setShowManageDropdown(false); }}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-yellow-50 transition-colors ${activeView === 'manage-webhooks' ? 'text-yellow-700 font-medium bg-yellow-50' : 'text-gray-700'}`}
                          >
                            Manage Webhook
                          </button>
                        </>
                      )}
                      {canAccessManageExpenseGlCode && (
                        <button
                          onClick={() => { setActiveView('manage-expense-gl-codes'); setShowManageDropdown(false); }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-yellow-50 transition-colors ${activeView === 'manage-expense-gl-codes' ? 'text-yellow-700 font-medium bg-yellow-50' : 'text-gray-700'}`}
                        >
                          Manage Expense GL Code
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div className="relative user-dropdown-container">
                <button
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className="p-1.5 transition-colors"
                >
                  <User className={`w-6 h-6 ${showUserDropdown ? 'text-yellow-600' : 'text-gray-400 hover:text-gray-600'}`} />
                </button>
                {showUserDropdown && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-50 min-w-[240px]">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm text-gray-500">{currentUser?.email || email}</p>
                    </div>
                    <button
                      onClick={() => { setShowUserDropdown(false); handleLogout(); }}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                    >
                      <LogOut className="w-4 h-4" />
                      Log Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          {showMobileMenu && (
            <div className="md:hidden mt-4 space-y-2">
              <button
                onClick={() => { setActiveView('dashboard'); setShowMobileMenu(false); }}
                className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${activeView === 'dashboard' ? 'bg-yellow-100 text-yellow-700 font-medium' : 'bg-gray-100 text-gray-700'}`}
              >
                Dashboard
              </button>
              <button
                onClick={() => { setActiveView('my-claims'); setShowMobileMenu(false); }}
                className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${activeView === 'my-claims' ? 'bg-yellow-100 text-yellow-700 font-medium' : 'bg-gray-100 text-gray-700'}`}
              >
                My Claims
              </button>
              {canAccessApprovals && (
                <button
                  onClick={() => { setActiveView('approval'); setShowMobileMenu(false); }}
                  className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${activeView === 'approval' ? 'bg-yellow-100 text-yellow-700 font-medium' : 'bg-gray-100 text-gray-700'}`}
                >
                  Approvals
                </button>
              )}
              {canAccessReports && (
                <button
                  onClick={() => { setActiveView('report'); setShowMobileMenu(false); }}
                  className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${activeView === 'report' ? 'bg-yellow-100 text-yellow-700 font-medium' : 'bg-gray-100 text-gray-700'}`}
                >
                  Reports
                </button>
              )}
              {canAccessManage && (
                <>
                  {canAccessManageUsers && (
                    <button
                      onClick={() => { setActiveView('manage-users'); setShowMobileMenu(false); }}
                      className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${activeView === 'manage-users' ? 'bg-yellow-100 text-yellow-700 font-medium' : 'bg-gray-100 text-gray-700'}`}
                    >
                      Manage User
                    </button>
                  )}
                  {canAccessManageCompany && (
                    <>
                      <button
                        onClick={() => { setActiveView('manage-companies'); setShowMobileMenu(false); }}
                        className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${activeView === 'manage-companies' ? 'bg-yellow-100 text-yellow-700 font-medium' : 'bg-gray-100 text-gray-700'}`}
                      >
                        Manage Company
                      </button>
                      <button
                        onClick={() => { setActiveView('manage-departments'); setShowMobileMenu(false); }}
                        className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${activeView === 'manage-departments' ? 'bg-yellow-100 text-yellow-700 font-medium' : 'bg-gray-100 text-gray-700'}`}
                      >
                        Manage Department
                      </button>
                      <button
                        onClick={() => { setActiveView('manage-webhooks'); setShowMobileMenu(false); }}
                        className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${activeView === 'manage-webhooks' ? 'bg-yellow-100 text-yellow-700 font-medium' : 'bg-gray-100 text-gray-700'}`}
                      >
                        Manage Webhook
                      </button>
                    </>
                  )}
                  {canAccessManageExpenseGlCode && (
                    <button
                      onClick={() => { setActiveView('manage-expense-gl-codes'); setShowMobileMenu(false); }}
                      className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${activeView === 'manage-expense-gl-codes' ? 'bg-yellow-100 text-yellow-700 font-medium' : 'bg-gray-100 text-gray-700'}`}
                    >
                      Manage Expense GL Code
                    </button>
                  )}
                </>
              )}
              <div className="border-t border-gray-200 mt-2 pt-2 px-4 pb-1">
                <p className="text-xs text-gray-500 mb-2">{currentUser?.email || email}</p>
              </div>
              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-2 rounded-lg bg-red-50 text-red-600 font-medium flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Log Out
              </button>
            </div>
          )}
        </div>
      </div>

      <div className={`${activeView === 'manage-users' || activeView === 'manage-expense-gl-codes' || activeView === 'manage-expenses' ? '' : 'flex-1 '}max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8 w-full`}>
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold">
              {activeView === 'dashboard' ? 'Dashboard' :
               activeView === 'my-claims' ? 'My Claims' :
               activeView === 'approval' ? 'Approvals' :
               activeView === 'system-activity' ? 'System Activity' :
               activeView === 'manage-users' ? 'Manage User' :
               activeView === 'manage-companies' ? 'Manage Company' :
               activeView === 'manage-departments' ? 'Manage Department' :
               activeView === 'manage-webhooks' ? 'Manage Webhook' :
               activeView === 'manage-expense-gl-codes' ? 'Manage Expense GL Code' : 'Reports'}
            </h2>
            {(activeView === 'my-claims' || activeView === 'dashboard') && (
              <button onClick={handleOpenAddClaim} className="bg-yellow-400 px-4 sm:px-6 py-2 rounded-lg font-semibold flex items-center gap-2 hover:bg-yellow-500 transition-colors">
                <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Add Claim</span>
              </button>
            )}
            {activeView === 'report' && (
              <button
                onClick={() => {
                  const hasReportStatus = Object.values(reportStatusFilters || {}).some(Boolean);
                  const nextStatusFilters = hasReportStatus
                    ? reportStatusFilters
                    : { approved: true, pending: true, returned: true, rejected: true };
                  setExportFilters((prev) => ({
                    ...prev,
                    dateType: 'last_action',
                    lastActionStart: fromDate || prev.lastActionStart,
                    lastActionEnd: toDate || prev.lastActionEnd,
                    statusFilters: { ...nextStatusFilters }
                  }));
                  setShowExportModal(true);
                }}
                className="bg-yellow-400 px-4 sm:px-6 py-2 rounded-lg font-semibold text-sm sm:text-base hover:bg-yellow-500 transition-colors"
              >
                Export in Excel
              </button>
            )}
            {activeView === 'manage-users' && canAccessManageUsers && (
              <div className="flex gap-2">
                <input
                  ref={userImportFileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={handleUserImportFileSelected}
                />
                <button
                  onClick={handleOpenUserImportPicker}
                  className="px-4 sm:px-6 py-2 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50 transition-colors"
                >
                  Import
                </button>
                <button onClick={() => handleOpenUserModal()} className="bg-yellow-400 px-4 sm:px-6 py-2 rounded-lg font-semibold flex items-center gap-2 hover:bg-yellow-500 transition-colors">
                  <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Add User</span>
                </button>
              </div>
            )}
            {activeView === 'manage-companies' && canAccessManageCompany && (
              <button onClick={() => handleOpenCompanyModal()} className="bg-yellow-400 px-4 sm:px-6 py-2 rounded-lg font-semibold flex items-center gap-2 hover:bg-yellow-500 transition-colors">
                <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Add Company</span>
              </button>
            )}
            {activeView === 'manage-departments' && canAccessManageCompany && (
              <button onClick={() => handleOpenDepartmentModal()} className="bg-yellow-400 px-4 sm:px-6 py-2 rounded-lg font-semibold flex items-center gap-2 hover:bg-yellow-500 transition-colors">
                <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Add Department</span>
              </button>
            )}
            {activeView === 'manage-webhooks' && canAccessManageCompany && (
              <div className="flex gap-2">
                <button
                  onClick={handleSyncExistingWebhooks}
                  className="px-4 sm:px-6 py-2 rounded-lg font-semibold text-sm sm:text-base border border-gray-300 hover:bg-gray-50 transition-colors"
                  disabled={webhookLoading}
                >
                  Sync Existing
                </button>
                <button
                  onClick={handleGenerateWebhookUrl}
                  className="bg-yellow-400 px-4 sm:px-6 py-2 rounded-lg font-semibold text-sm sm:text-base hover:bg-yellow-500 transition-colors"
                  disabled={webhookLoading}
                >
                  {webhookLoading ? 'Generating...' : 'Generate Webhook URL'}
                </button>
              </div>
            )}
            {activeView === 'manage-expense-gl-codes' && canAccessManageExpenseGlCode && (
              <button onClick={() => handleOpenExpenseGlCodeModal()} className="bg-yellow-400 px-4 sm:px-6 py-2 rounded-lg font-semibold flex items-center gap-2 hover:bg-yellow-500 transition-colors">
                <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Add GL Code</span>
              </button>
            )}
            {activeView === 'manage-expenses' && canAccessManageExpenseGlCode && (
              <button onClick={() => handleOpenExpenseModal()} className="bg-yellow-400 px-4 sm:px-6 py-2 rounded-lg font-semibold flex items-center gap-2 hover:bg-yellow-500 transition-colors">
                <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Add Expense</span>
              </button>
            )}
          </div>

          {/* Dashboard Tabs and Search */}
          {activeView === 'dashboard' && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex gap-2">
                  <button
                    onClick={() => setDashboardTab('returned')}
                    className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors ${
                      dashboardTab === 'returned'
                        ? 'bg-white text-yellow-600 border-b-2 border-yellow-500'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Returned Claims
                  </button>
                  {canAccessApprovals && (
                    <button
                      onClick={() => setDashboardTab('pending')}
                      className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors ${
                        dashboardTab === 'pending'
                          ? 'bg-white text-yellow-600 border-b-2 border-yellow-500'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Pending Approvals
                    </button>
                  )}
                </div>
                <div className="relative w-full sm:w-64">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search Claim ID"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  />
                  <Eye className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                </div>
              </div>
            </div>
          )}

          {/* My Claims Date Filter and Search */}
          {activeView === 'my-claims' && (
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-gray-50 p-4 rounded-lg border">
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-gray-700">Submission Date</span>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                  <ArrowLeft className="w-4 h-4 text-gray-400 rotate-180" />
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
              </div>
              <div className="relative w-full sm:w-auto">
                <Eye className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search Claim ID"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full sm:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
            </div>
          )}

          {/* Approval Date Filter and Search */}
            {activeView === 'approval' && (
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-gray-50 p-4 rounded-lg border">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-gray-700">Last Action Date</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                  <ArrowLeft className="w-4 h-4 text-gray-400 rotate-180" />
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
              </div>
              <div className="relative w-full sm:w-auto">
                <Eye className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search Claim ID"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full sm:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
            </div>
          )}

          {/* Reports Date Filter and Search */}
          {activeView === 'report' && (
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-medium text-gray-700">Last Action Date</span>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                />
                <ArrowLeft className="w-4 h-4 text-gray-400 rotate-180" />
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                />
                <div className="relative report-filter-container">
                  <button
                    onClick={() => setShowReportFilter(!showReportFilter)}
                    className="flex items-center gap-1.5 px-4 py-1.5 bg-yellow-400 text-gray-900 text-sm font-semibold rounded-lg hover:bg-yellow-500 transition-colors"
                  >
                    Filter
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </button>
                  {showReportFilter && (
                    <div className="absolute left-0 top-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg py-4 px-5 z-50 min-w-[200px]">
                      <p className="text-sm font-semibold text-gray-700 mb-3">Status</p>
                      {[
                        { key: 'approved', label: 'Approved' },
                        { key: 'pending', label: 'Pending' },
                        { key: 'returned', label: 'Return' },
                        { key: 'rejected', label: 'Rejected' }
                      ].map(item => (
                        <label key={item.key} className="flex items-center justify-between py-2 cursor-pointer">
                          <span className="text-sm text-gray-700">{item.label}</span>
                          <div
                            onClick={() => setReportStatusFilters(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
                            className={`w-10 h-5 rounded-full transition-colors relative cursor-pointer ${reportStatusFilters[item.key] ? 'bg-yellow-400' : 'bg-gray-300'}`}
                          >
                            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${reportStatusFilters[item.key] ? 'translate-x-5' : 'translate-x-0.5'}`} />
                          </div>
                        </label>
                      ))}
                      <div className="flex justify-between mt-3 pt-3 border-t border-gray-100">
                        <button
                          onClick={() => setReportStatusFilters({ approved: true, pending: false, returned: false, rejected: false })}
                          className="text-sm text-yellow-600 font-medium hover:text-yellow-700"
                        >
                          Reset
                        </button>
                        <button
                          onClick={() => setShowReportFilter(false)}
                          className="px-4 py-1.5 bg-yellow-400 text-gray-900 text-sm font-semibold rounded-lg hover:bg-yellow-500 transition-colors"
                        >
                          Filter
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="relative w-full sm:w-64">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search Claim ID"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                />
                <Eye className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              </div>
            </div>
          )}
        </div>

        {activeView === 'system-activity' && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Activity Type</label>
                  <select
                    value={activityFilters.action}
                    onChange={(e) => setActivityFilters((prev) => ({ ...prev, action: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  >
                    <option value="">All Activities</option>
                    {[
                      'claim_created',
                      'claim_submitted',
                      'claim_edited',
                      'claim_approved',
                      'claim_rejected',
                      'claim_returned',
                      'claim_resubmitted'
                    ].map((action) => (
                      <option key={action} value={action}>{formatActivityActionLabel(action)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Claim No.</label>
                  <input
                    type="text"
                    value={activityFilters.claimId}
                    onChange={(e) => setActivityFilters((prev) => ({ ...prev, claimId: e.target.value }))}
                    placeholder="e.g. 202603-00039"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                  <input
                    type="date"
                    value={activityFilters.startDate}
                    onChange={(e) => setActivityFilters((prev) => ({ ...prev, startDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                  <input
                    type="date"
                    value={activityFilters.endDate}
                    onChange={(e) => setActivityFilters((prev) => ({ ...prev, endDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  />
                </div>
                <div className="flex items-end gap-3">
                  <button
                    onClick={loadActivityLogs}
                    className="w-full bg-yellow-400 px-4 py-2 rounded-lg font-semibold hover:bg-yellow-500 transition-colors"
                  >
                    Refresh
                  </button>
                </div>
              </div>
              <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-gray-500">
                <p>Auto refreshes every 10 seconds while this page is open.</p>
                <p>Last updated: {activityLastUpdated ? formatActivityTimestamp(activityLastUpdated) : '-'}</p>
              </div>
              {activityError && (
                <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {activityError}
                </div>
              )}
            </div>

            <div className="hidden lg:block bg-white border border-gray-300 overflow-x-auto overflow-y-auto max-h-[70vh]">
              <table className="min-w-[1400px] text-sm border-collapse w-full">
                <thead className="bg-gray-100 border-b border-gray-300">
                  <tr>
                    <th className="px-4 text-left text-xs font-semibold text-gray-600 border-b border-r border-gray-300 min-w-[180px]">Timestamp</th>
                    <th className="px-4 text-left text-xs font-semibold text-gray-600 border-b border-r border-gray-300 min-w-[180px]">Activity</th>
                    <th className="px-4 text-left text-xs font-semibold text-gray-600 border-b border-r border-gray-300 min-w-[180px]">User</th>
                    <th className="px-4 text-left text-xs font-semibold text-gray-600 border-b border-r border-gray-300 min-w-[140px]">Email</th>
                    <th className="px-4 text-left text-xs font-semibold text-gray-600 border-b border-r border-gray-300 min-w-[120px]">Claim No.</th>
                    <th className="px-4 text-left text-xs font-semibold text-gray-600 border-b border-r border-gray-300 min-w-[120px]">From</th>
                    <th className="px-4 text-left text-xs font-semibold text-gray-600 border-b border-r border-gray-300 min-w-[120px]">To</th>
                    <th className="px-4 text-left text-xs font-semibold text-gray-600 border-b border-r border-gray-300 min-w-[260px]">Reason</th>
                    <th className="px-4 text-left text-xs font-semibold text-gray-600 border-b border-gray-300 min-w-[320px]">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {activityLoading ? (
                    <tr>
                      <td colSpan="9" className="px-6 py-10 text-center text-gray-500">Loading activity...</td>
                    </tr>
                  ) : auditLogs.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="px-6 py-10 text-center text-gray-500">No activity found for the selected filters.</td>
                    </tr>
                  ) : auditLogs.map((log, idx) => (
                    <tr key={log.id || `${log.action}-${idx}`} className={`border-b border-gray-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                      <td className="px-4 py-3 align-top text-gray-700">{formatActivityTimestamp(log.created_at || log.timestamp)}</td>
                      <td className="px-4 py-3 align-top font-medium text-gray-900">{formatActivityActionLabel(log.action)}</td>
                      <td className="px-4 py-3 align-top text-gray-700">{log.user_name || '-'}</td>
                      <td className="px-4 py-3 align-top text-gray-700">{log.user_email || '-'}</td>
                      <td className="px-4 py-3 align-top text-gray-700">{log.claim_number || '-'}</td>
                      <td className="px-4 py-3 align-top text-gray-700">{log.old_status || '-'}</td>
                      <td className="px-4 py-3 align-top text-gray-700">{log.new_status || '-'}</td>
                      <td className="px-4 py-3 align-top text-gray-700 whitespace-pre-wrap break-words">{log.reason || '-'}</td>
                      <td className="px-4 py-3 align-top text-gray-700 whitespace-pre-wrap break-words">{formatActivityDetails(log)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="lg:hidden space-y-3">
              {activityLoading ? (
                <div className="bg-white rounded-xl border border-gray-200 p-4 text-sm text-gray-500">Loading activity...</div>
              ) : auditLogs.length === 0 ? (
                <div className="bg-white rounded-xl border border-gray-200 p-4 text-sm text-gray-500">No activity found for the selected filters.</div>
              ) : auditLogs.map((log, idx) => (
                <div key={log.id || `${log.action}-${idx}`} className="bg-white rounded-xl border border-gray-200 p-4 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm font-semibold text-gray-900">{formatActivityActionLabel(log.action)}</p>
                    <p className="text-xs text-gray-500">{formatActivityTimestamp(log.created_at || log.timestamp)}</p>
                  </div>
                  <p className="text-sm text-gray-700">User: {log.user_name || '-'}{log.user_email ? ` (${log.user_email})` : ''}</p>
                  <p className="text-sm text-gray-700">Claim: {log.claim_number || '-'}</p>
                  <p className="text-sm text-gray-700">From: {log.old_status || '-'} | To: {log.new_status || '-'}</p>
                  <p className="text-sm text-gray-700">Reason: {log.reason || '-'}</p>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">Details: {formatActivityDetails(log)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ==================== MANAGE WEBHOOK PAGE ==================== */}
        {activeView === 'manage-webhooks' && canAccessManageCompany && (
          <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-7 space-y-6">
            <div className="space-y-2">
              <h3 className="text-xl font-bold text-gray-900">Webhook URLs</h3>
              <p className="text-sm text-gray-600">
                Generate webhook URLs for integrations. Use Enable/Deactivate or Delete from the right side.
              </p>
            </div>

            <div className="space-y-4">
              {webhookList.map((webhook) => (
                <div key={webhook.id} className="border border-gray-200 rounded-xl p-4">
                  <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
                    <div className="flex-1 space-y-2">
                      <input
                        type="text"
                        readOnly
                        value={webhook.url || ''}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 focus:outline-none"
                      />
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${webhook.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'}`}>
                          {webhook.is_active ? 'Enabled' : 'Deactivated'}
                        </span>
                        {webhook.created_at && (
                          <span className="text-xs text-gray-500">
                            {new Date(webhook.created_at).toLocaleString('en-MY', { timeZone: 'Asia/Kuala_Lumpur' })}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 lg:justify-end">
                      <button
                        onClick={() => handleCopyWebhookUrl(webhook.url)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        Copy
                      </button>
                      <button
                        onClick={() => handleToggleWebhookStatus(webhook)}
                        disabled={webhookActionLoadingId === webhook.id}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          webhook.is_active ? 'bg-gray-200 text-gray-800 hover:bg-gray-300' : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        {webhookActionLoadingId === webhook.id ? 'Saving...' : webhook.is_active ? 'Deactivate' : 'Enable'}
                      </button>
                      <button
                        onClick={() => handleDeleteWebhook(webhook)}
                        disabled={webhookActionLoadingId === webhook.id}
                        className="px-3 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors"
                      >
                        {webhookActionLoadingId === webhook.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {webhookList.length === 0 && (
                <div className="text-center py-10 text-gray-500 border border-dashed border-gray-300 rounded-xl">
                  No webhook URL yet. Click "Generate Webhook URL".
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-xl p-4 bg-gray-50 space-y-3">
              <h4 className="text-sm font-semibold text-gray-900">Webhook API Guide</h4>
              <p className="text-xs text-gray-700">
                Use the generated URL token in external integrations. Each item in <code className="bg-white px-1 py-0.5 rounded">items[]</code> creates one claim row.
              </p>
              <div className="text-xs text-gray-800 space-y-1">
                <p><span className="font-semibold">GET (list webhooks):</span> <code className="bg-white px-1 py-0.5 rounded">GET /api/webhooks</code> (Manage Company enabled)</p>
                <p><span className="font-semibold">POST (generate webhook):</span> <code className="bg-white px-1 py-0.5 rounded">POST /api/webhooks</code> (Manage Company enabled)</p>
                <p><span className="font-semibold">POST (incoming claim):</span> <code className="bg-white px-1 py-0.5 rounded">POST /api/webhooks/incoming?token=&lt;webhook_token&gt;</code></p>
                <p><span className="font-semibold">GET (Telegram link status):</span> <code className="bg-white px-1 py-0.5 rounded">GET /api/webhooks/telegram/link-status?token=&lt;webhook_token&gt;&amp;telegram_user_id=&lt;id&gt;</code></p>
                <p><span className="font-semibold">POST (Telegram OTP verify):</span> <code className="bg-white px-1 py-0.5 rounded">POST /api/webhooks/telegram/verify-otp?token=&lt;webhook_token&gt;</code></p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-800 mb-1">Sample incoming JSON (ID-based dropdown)</p>
                <pre className="text-xs bg-white border border-gray-200 rounded-lg p-3 overflow-x-auto">
{`{
  "telegram_user_id": "tg_123456",
  "user_email": "user@company.com",
  "month_year": "2026-02",
  "remarks": "Telegram batch upload",
  "items": [
    {
      "claim_type_id": 1,
      "expense_type_id": 3,
      "amount": 15.5,
      "receipt_number": "R-001",
      "merchant": "Grab",
      "attachments": [
        {
          "file_name": "grab-receipt.jpg",
          "mime_type": "image/jpeg",
          "base64": "<base64-content>"
        }
      ]
    },
    {
      "claim_type_id": 2,
      "expense_type_id": 7,
      "amount": 8.0,
      "receipt_number": "R-002",
      "merchant": "Mall Parking",
      "attachment_url": "https://example.com/r-002.pdf"
    }
  ]
}`}
                </pre>
              </div>
              <p className="text-xs text-gray-700">
                Attachment fields supported per item: <code className="bg-white px-1 py-0.5 rounded">attachments[]</code>, <code className="bg-white px-1 py-0.5 rounded">attachment_url</code>, <code className="bg-white px-1 py-0.5 rounded">attachment_urls[]</code>, <code className="bg-white px-1 py-0.5 rounded">attachment_base64</code>.
              </p>
              <p className="text-xs text-gray-700">
                Telegram first-time flow: send payload with <code className="bg-white px-1 py-0.5 rounded">telegram_user_id</code> + user identifier; API returns auth required and sends OTP email. Then call verify endpoint with <code className="bg-white px-1 py-0.5 rounded">telegram_user_id</code>, <code className="bg-white px-1 py-0.5 rounded">otp</code>, and user identifier. Next webhook calls from same Telegram ID auto-map to that user.
              </p>
              <p className="text-xs text-amber-700">
                If <code className="bg-white px-1 py-0.5 rounded">month_year</code> is missing, API will return an error asking for month and year.
              </p>
            </div>
          </div>
        )}

        {/* ==================== MANAGE COMPANIES TABLE ==================== */}
        {activeView === 'manage-companies' && canAccessManageCompany && (
          <>
            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
              {companies.map(company => (
                <div key={company.id} className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-semibold text-gray-900">{company.full_name}</p>
                      <p className="text-sm text-gray-500">Code: {company.code}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${company.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {company.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleOpenCompanyModal(company)} className="flex-1 px-3 py-1.5 text-sm font-medium bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors">Edit</button>
                    <button onClick={() => handleToggleCompanyActive(company)} className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${company.is_active ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}>
                      {company.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </div>
              ))}
              {companies.length === 0 && <div className="text-center py-8 text-gray-500">No companies found.</div>}
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full table-fixed">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase w-1/2">Full Name</th>
                      <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase w-1/6">Code</th>
                      <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase w-1/6">Status</th>
                      <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase w-1/6">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companies.map(company => (
                      <tr key={company.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="px-4 text-sm font-medium text-gray-900">{company.full_name}</td>
                        <td className="px-4 text-sm text-gray-600 font-mono">{company.code}</td>
                        <td className="px-4 text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${company.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {company.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 text-sm">
                          <div className="flex gap-2">
                            <button onClick={() => handleOpenCompanyModal(company)} className="px-3 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors">Edit</button>
                            <button onClick={() => handleToggleCompanyActive(company)} className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${company.is_active ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}>
                              {company.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {companies.length === 0 && <div className="text-center py-8 text-gray-500">No companies found.</div>}
              </div>
            </div>
          </>
        )}

        {/* ==================== MANAGE DEPARTMENTS TABLE ==================== */}
        {activeView === 'manage-departments' && canAccessManageCompany && (
          <>
            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
              {departments.map(dept => (
                <div key={dept.id} className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex justify-between items-start mb-2">
                    <p className="font-semibold text-gray-900">{dept.name}</p>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${dept.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {dept.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleOpenDepartmentModal(dept)} className="flex-1 px-3 py-1.5 text-sm font-medium bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors">Edit</button>
                    <button onClick={() => handleToggleDepartmentActive(dept)} className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${dept.is_active ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}>
                      {dept.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </div>
              ))}
              {departments.length === 0 && <div className="text-center py-8 text-gray-500">No departments found.</div>}
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full table-fixed">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase w-1/2">Department Name</th>
                      <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase w-1/4">Status</th>
                      <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase w-1/4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {departments.map(dept => (
                      <tr key={dept.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="px-4 text-sm font-medium text-gray-900">{dept.name}</td>
                        <td className="px-4 text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${dept.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {dept.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 text-sm">
                          <div className="flex gap-2">
                            <button onClick={() => handleOpenDepartmentModal(dept)} className="px-3 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors">Edit</button>
                            <button onClick={() => handleToggleDepartmentActive(dept)} className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${dept.is_active ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}>
                              {dept.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {departments.length === 0 && <div className="text-center py-8 text-gray-500">No departments found.</div>}
              </div>
            </div>
          </>
        )}

                      {/* Claims Views */}
                      {activeView !== 'manage-users' && activeView !== 'manage-companies' && activeView !== 'manage-departments' && activeView !== 'manage-webhooks' && activeView !== 'manage-expense-gl-codes' && activeView !== 'manage-pending-approvals' && activeView !== 'manage-dashboard-returned' && activeView !== 'manage-reports' && activeView !== 'manage-entities' && activeView !== 'manage-allocations' && activeView !== 'manage-expense' && activeView !== 'manage-company-expenses' && activeView !== 'manage-manager-user' && activeView !== 'manage-finance-admin' && activeView !== 'manage-subdepartments' && activeView !== 'manage-approval-history' && activeView !== 'manage-corporate-card-history' && activeView !== 'manage-corporate-card' && activeView !== 'manage-corporate-card-report' && activeView !== 'manage-bills' && activeView !== 'manage-bills-report' && activeView !== 'manage-bills-history' && activeView !== 'manage-roles' && activeView !== 'manage-user-role-access' && activeView !== 'manage-department-allocations' && activeView !== 'manage-project-allocations' && activeView !== 'claim-approval' && activeView !== 'manage-claims-system' && activeView !== 'manage-claim-allocation' && activeView !== 'manage-reports-admin' && activeView !== 'manage-bills-admin' && activeView !== 'manage-corporate-card-admin' && activeView !== 'manage-budgets' && activeView !== 'manage-position' && activeView !== 'manage-webmail-templates' && activeView !== 'manage-submissions' && activeView !== 'manage-user-import' && activeView !== 'manage-telegram-link' && activeView !== 'claim-history' && activeView !== 'claim-history-view' && (
                        <>
                          <div className="lg:hidden space-y-4">
                            {hasDesktopRows ? (
                              desktopTableDataSource.map((claim, idx) => (
                                <div key={`${claim.id}-${idx}`} className="bg-white border border-[#D8DEEA] rounded-[20px] px-5 py-5 shadow-[0_8px_24px_rgba(15,23,42,0.04)] space-y-4">
                                  <div className="flex items-start justify-between gap-3">
                                    <span className={`inline-flex items-center justify-center rounded-full px-4 py-1.5 text-[14px] font-semibold ${getStatusColor(claim.status)}`}>
                                      {claim.status}
                                    </span>
                                    {activeView !== 'dashboard' && (
                                      <span className="text-[13px] font-medium text-[#8A94A6]">{claim.id}</span>
                                    )}
                                  </div>

                                  <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-[14px] text-[#111827]">
                                    <div>
                                      <div className="text-[12px] font-semibold tracking-[0.04em] uppercase text-[#8A94A6]">Claim ID</div>
                                      <div className="mt-1 font-semibold break-words">{claim.id}</div>
                                    </div>
                                    {activeView !== 'my-claims' && activeView !== 'report' && activeView !== 'dashboard' && activeView !== 'approval' ? null : null}
                                    {showClaimantColumn && (
                                      <>
                                        <div>
                                          <div className="text-[12px] font-semibold tracking-[0.04em] uppercase text-[#8A94A6]">Claimant</div>
                                          <div className="mt-1 break-words">{claim.claimant || '-'}</div>
                                        </div>
                                        <div>
                                          <div className="text-[12px] font-semibold tracking-[0.04em] uppercase text-[#8A94A6]">Claim Type</div>
                                          <div className="mt-1 break-words">{getListClaimType(claim)}</div>
                                        </div>
                                        <div className="col-span-2">
                                          <div className="text-[12px] font-semibold tracking-[0.04em] uppercase text-[#8A94A6]">Desc. / Project Name</div>
                                          <div className="mt-1 break-words">{getListProjectName(claim)}</div>
                                        </div>
                                      </>
                                    )}
                                    {isMyClaimsView && (
                                      <>
                                        <div>
                                          <div className="text-[12px] font-semibold tracking-[0.04em] uppercase text-[#8A94A6]">Claim Type</div>
                                          <div className="mt-1 break-words">{getListClaimType(claim)}</div>
                                        </div>
                                        <div>
                                          <div className="text-[12px] font-semibold tracking-[0.04em] uppercase text-[#8A94A6]">Desc. / Project Name</div>
                                          <div className="mt-1 break-words">{getListProjectName(claim)}</div>
                                        </div>
                                      </>
                                    )}
                                    {isReportClaimsView && (
                                      <>
                                        <div>
                                          <div className="text-[12px] font-semibold tracking-[0.04em] uppercase text-[#8A94A6]">Department</div>
                                          <div className="mt-1 break-words">{claim.department || '-'}</div>
                                        </div>
                                      </>
                                    )}
                                    <div>
                                      <div className="text-[12px] font-semibold tracking-[0.04em] uppercase text-[#8A94A6]">Claim Mth/Year</div>
                                      <div className="mt-1 break-words">{getListClaimMonth(claim)}</div>
                                    </div>
                                    <div>
                                      <div className="text-[12px] font-semibold tracking-[0.04em] uppercase text-[#8A94A6]">Total Amount</div>
                                      <div className="mt-1 font-semibold">RM {Number(claim.amount || 0).toFixed(2)}</div>
                                    </div>
                                    {!isReportClaimsView && (
                                      <div>
                                        <div className="text-[12px] font-semibold tracking-[0.04em] uppercase text-[#8A94A6]">Manager</div>
                                        <div className="mt-1 break-words">{claim.manager}</div>
                                      </div>
                                    )}
                                    {showApprovalMetaColumns && (
                                      <div>
                                        <div className="text-[12px] font-semibold tracking-[0.04em] uppercase text-[#8A94A6]">Manager Approval</div>
                                        <div className="mt-1">
                                          {renderApprovalStatusBlock(
                                            claim.managerApproval,
                                            claim.manager,
                                            claim.managerApprovalDate,
                                            claim.managerApproval === 'Returned' ? claim.managerReturnReason : (claim.managerApproval === 'Rejected' ? claim.managerRejectionReason : '')
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    {showApprovalMetaColumns && (
                                      <div>
                                        <div className="text-[12px] font-semibold tracking-[0.04em] uppercase text-[#8A94A6]">Finance Approval</div>
                                        <div className="mt-1">
                                          {renderApprovalStatusBlock(
                                            claim.financeApproval,
                                            claim.financeApprover,
                                            claim.financeApprovalDate,
                                            claim.financeApproval === 'Returned' ? claim.financeReturnReason : (claim.financeApproval === 'Rejected' ? claim.financeRejectionReason : '')
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    {showDepartmentColumn && (
                                      <div>
                                        <div className="text-[12px] font-semibold tracking-[0.04em] uppercase text-[#8A94A6]">Department</div>
                                        <div className="mt-1 break-words">{claim.department || '-'}</div>
                                      </div>
                                    )}
                                    {showApprovalMetaColumns && (
                                      <div>
                                        <div className="text-[12px] font-semibold tracking-[0.04em] uppercase text-[#8A94A6]">Submission Date</div>
                                        <div className="mt-1 break-words">{renderActorDateBlock(claim.submitter, claim.submissionDate)}</div>
                                      </div>
                                    )}
                                    {showApprovalMetaColumns && (
                                      <div>
                                        <div className="text-[12px] font-semibold tracking-[0.04em] uppercase text-[#8A94A6]">Last Action Date</div>
                                        <div className="mt-1 break-words">{renderActorDateBlock(claim.lastActor, claim.lastActionDate)}</div>
                                      </div>
                                    )}
                                  </div>

                                  <div className="pt-1">
                                    {(isApprovalClaimsView || (isDashboardClaimsView && dashboardTab === 'pending')) &&
                                      canCurrentUserActOnClaim(claim, currentUser, accessProfile) && (
                                      <div className="flex flex-col gap-2 min-w-[170px]">
                                        <div className="flex gap-2">
                                          <button
                                            type="button"
                                            className="h-9 px-4 rounded-md bg-[#F04457] text-white text-[15px] font-semibold hover:bg-[#dc3545] transition-colors"
                                            onClick={() => {
                                              setPendingAction({ claimId: claim.claimId, type: 'reject', approverType: resolveClaimApproverType(claim, currentUser, accessProfile) });
                                              setShowRejectModal(true);
                                            }}
                                          >
                                            Reject
                                          </button>
                                          <button
                                            type="button"
                                            className="h-9 px-4 rounded-md bg-[#16A34A] text-white text-[15px] font-semibold hover:bg-[#15803D] transition-colors"
                                            onClick={() => handleApproveClaim(claim.claimId, resolveClaimApproverType(claim, currentUser, accessProfile), 'Approved')}
                                          >
                                            Approve
                                          </button>
                                        </div>
                                        <button
                                          type="button"
                                          className="h-9 px-4 rounded-md border border-[#B8C0CC] bg-white text-[#334155] text-[15px] font-semibold hover:bg-[#F8FAFC] transition-colors"
                                            onClick={() => {
                                              setPendingAction({ claimId: claim.claimId, type: 'return' });
                                              setShowReturnModal(true);
                                            }}
                                        >
                                          Return
                                        </button>
                                      </div>
                                    )}
                                    <div className="flex justify-end">
                                      <button
                                        className="inline-flex items-center justify-center text-[#1F2937] transition-colors hover:text-[#0F172A]"
                                        onClick={() => (((activeView === 'dashboard' && dashboardTab === 'returned') || (activeView === 'my-claims' && canEditClaim(claim))) ? loadEditClaim(claim) : loadClaimDetails(claim))}
                                        aria-label={claim.status === 'Draft' || claim.status === 'Returned' ? 'Edit claim' : 'View claim'}
                                      >
                                        <ArrowRight size={22} />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))
                            ) : desktopEmptyState ? (
                              <div className="bg-white border border-[#D8DEEA] rounded-[24px] px-6 py-14 text-center shadow-[0_8px_24px_rgba(15,23,42,0.04)]">
                                {desktopEmptyState.icon && <div className="text-[42px] mb-4">{desktopEmptyState.icon}</div>}
                                <div className="text-[22px] font-semibold text-[#0F172A] mb-2">{desktopEmptyState.title}</div>
                                <div className="max-w-[420px] mx-auto text-[15px] leading-7 text-[#52607A]">{desktopEmptyState.description}</div>
                              </div>
                            ) : null}
                          </div>

                          <div className="hidden lg:block w-full min-w-0">
                            <div
                              ref={desktopTableContainerRef}
                              className={`desktop-table-scroll w-full overflow-x-auto overflow-y-hidden rounded-[24px] border border-[#D8DEEA] bg-white shadow-[0_10px_30px_rgba(15,23,42,0.05)] ${isDesktopTableDragging ? 'is-dragging' : ''}`}
                              style={{ WebkitOverflowScrolling: 'touch' }}
                            >
                              <table className="w-max border-collapse" style={{ minWidth: desktopTableMinWidthPx }}>
                                <thead className="bg-[#F8FAFC] text-[#374151]">
                                  <tr>
                                    <th className="border-b border-r border-[#D8DEEA] px-5 py-4 text-left text-[14px] font-semibold whitespace-nowrap min-w-[130px]">Claim Status</th>
                                    <th className="border-b border-r border-[#D8DEEA] px-5 py-4 text-left text-[14px] font-semibold whitespace-nowrap min-w-[130px]">Claim ID</th>
                                    {showClaimantColumn && (
                                      <th className="border-b border-r border-[#D8DEEA] px-5 py-4 text-left text-[14px] font-semibold whitespace-nowrap min-w-[150px]">Claimant</th>
                                    )}
                                    {showClaimTypeAndDescColumns && (
                                      <th className="border-b border-r border-[#D8DEEA] px-5 py-4 text-left text-[14px] font-semibold whitespace-nowrap min-w-[140px]">Claim Type</th>
                                    )}
                                    {showClaimTypeAndDescColumns && (
                                      <th className="border-b border-r border-[#D8DEEA] px-5 py-4 text-left text-[14px] font-semibold whitespace-nowrap min-w-[220px]">Desc. / Project Name</th>
                                    )}
                                    <th className="border-b border-r border-[#D8DEEA] px-5 py-4 text-left text-[14px] font-semibold whitespace-nowrap min-w-[150px]">Claim Mth/Year</th>
                                    {showDepartmentColumn && (
                                      <th className="border-b border-r border-[#D8DEEA] px-5 py-4 text-left text-[14px] font-semibold whitespace-nowrap min-w-[170px]">Department</th>
                                    )}
                                    <th className="border-b border-r border-[#D8DEEA] px-5 py-4 text-left text-[14px] font-semibold whitespace-nowrap min-w-[150px]">Total Amount</th>
                                    {!isReportClaimsView && (
                                      <th className="border-b border-r border-[#D8DEEA] px-5 py-4 text-left text-[14px] font-semibold whitespace-nowrap min-w-[150px]">Manager</th>
                                    )}
                                    {showApprovalMetaColumns && (
                                      <th className="border-b border-r border-[#D8DEEA] px-5 py-4 text-left text-[14px] font-semibold whitespace-nowrap min-w-[170px]">Manager Approval</th>
                                    )}
                                    {showApprovalMetaColumns && (
                                      <th className="border-b border-r border-[#D8DEEA] px-5 py-4 text-left text-[14px] font-semibold whitespace-nowrap min-w-[170px]">Finance Approval</th>
                                    )}
                                    {showApprovalMetaColumns && (
                                      <th className="border-b border-r border-[#D8DEEA] px-5 py-4 text-left text-[14px] font-semibold whitespace-nowrap min-w-[165px]">Submission Date</th>
                                    )}
                                    {showApprovalMetaColumns && (
                                      <th className="border-b border-r border-[#D8DEEA] px-5 py-4 text-left text-[14px] font-semibold whitespace-nowrap min-w-[170px]">Last Action Date</th>
                                    )}
                                    <th className="border-b border-[#D8DEEA] px-5 py-4 text-left text-[14px] font-semibold whitespace-nowrap min-w-[170px]">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {hasDesktopRows && desktopTableDataSource.map((claim, idx) => (
                                    <tr key={`${claim.id}-${idx}`} className="border-b border-[#E8EDF5] bg-white hover:bg-[#FBFCFE] transition-colors align-top">
                                      <td className="border-r border-[#E8EDF5] px-5 py-5 text-[15px] text-[#1F2937] align-top">
                                        <div className={`inline-flex items-center justify-center rounded-full px-4 py-1.5 text-[14px] font-semibold ${getStatusColor(claim.status)}`}>
                                          {claim.status}
                                        </div>
                                      </td>
                                      <td className="border-r border-[#E8EDF5] px-5 py-5 text-[16px] font-semibold text-[#1F2937] align-top whitespace-normal break-words">{claim.id}</td>
                                      {showClaimantColumn && (
                                        <td className="border-r border-[#E8EDF5] px-5 py-5 text-[15px] text-[#1F2937] align-top whitespace-normal break-words">{claim.claimant || '-'}</td>
                                      )}
                                      {showClaimTypeAndDescColumns && (
                                        <td className="border-r border-[#E8EDF5] px-5 py-5 text-[15px] text-[#1F2937] align-top whitespace-normal break-words">{getListClaimType(claim)}</td>
                                      )}
                                      {showClaimTypeAndDescColumns && (
                                        <td className="border-r border-[#E8EDF5] px-5 py-5 text-[15px] text-[#1F2937] align-top whitespace-normal break-words">{getListProjectName(claim)}</td>
                                      )}
                                      <td className="border-r border-[#E8EDF5] px-5 py-5 text-[15px] text-[#1F2937] align-top whitespace-normal break-words">{getListClaimMonth(claim)}</td>
                                      {showDepartmentColumn && (
                                        <td className="border-r border-[#E8EDF5] px-5 py-5 text-[15px] text-[#1F2937] align-top whitespace-normal break-words">{claim.department || '-'}</td>
                                      )}
                                      <td className="border-r border-[#E8EDF5] px-5 py-5 text-[16px] font-semibold text-[#0F172A] align-top whitespace-nowrap">RM {Number(claim.amount || 0).toFixed(2)}</td>
                                      {!isReportClaimsView && (
                                        <td className="border-r border-[#E8EDF5] px-5 py-5 text-[15px] text-[#1F2937] align-top whitespace-normal break-words">{claim.manager}</td>
                                      )}
                                      {showApprovalMetaColumns && (
                                        <td className="border-r border-[#E8EDF5] px-5 py-5 text-[15px] align-top whitespace-normal break-words">
                                          {renderApprovalStatusBlock(
                                            claim.managerApproval,
                                            claim.manager,
                                            claim.managerApprovalDate,
                                            claim.managerApproval === 'Returned' ? claim.managerReturnReason : (claim.managerApproval === 'Rejected' ? claim.managerRejectionReason : '')
                                          )}
                                        </td>
                                      )}
                                      {showApprovalMetaColumns && (
                                        <td className="border-r border-[#E8EDF5] px-5 py-5 text-[15px] align-top whitespace-normal break-words">
                                          {renderApprovalStatusBlock(
                                            claim.financeApproval,
                                            claim.financeApprover,
                                            claim.financeApprovalDate,
                                            claim.financeApproval === 'Returned' ? claim.financeReturnReason : (claim.financeApproval === 'Rejected' ? claim.financeRejectionReason : '')
                                          )}
                                        </td>
                                      )}
                                      {showApprovalMetaColumns && (
                                        <td className="border-r border-[#E8EDF5] px-5 py-5 text-[15px] text-[#1F2937] align-top whitespace-normal break-words">
                                          {renderActorDateBlock(claim.submitter, claim.submissionDate)}
                                        </td>
                                      )}
                                      {showApprovalMetaColumns && (
                                        <td className="border-r border-[#E8EDF5] px-5 py-5 text-[15px] text-[#1F2937] align-top whitespace-normal break-words">
                                          {renderActorDateBlock(claim.lastActor, claim.lastActionDate)}
                                        </td>
                                      )}
                                      <td className="px-5 py-5 align-top">
                                        <div className="flex items-start gap-4 flex-nowrap">
                                          <div className="flex flex-col">
                                            {(activeView === 'dashboard' && dashboardTab === 'returned') ||
                                            (activeView === 'my-claims' && canEditClaim(claim)) ? (
                                              <button
                                                className="inline-flex items-center justify-center text-[#1F2937] transition-colors hover:text-[#0F172A] mb-2"
                                                onClick={() => loadEditClaim(claim)}
                                                aria-label="Edit claim"
                                              >
                                                <Edit2 size={20} />
                                              </button>
                                            ) : null}
                                            {(isApprovalClaimsView || (isDashboardClaimsView && dashboardTab === 'pending')) &&
                                              canCurrentUserActOnClaim(claim, currentUser, accessProfile) && (
                                              <div className="flex flex-col gap-2 min-w-[170px]">
                                                <div className="flex gap-2">
                                                  <button
                                                    type="button"
                                                    className="h-9 px-4 rounded-md bg-[#F04457] text-white text-[15px] font-semibold hover:bg-[#dc3545] transition-colors"
                                                    onClick={() => {
                                                      setPendingAction({ claimId: claim.claimId, type: 'reject', approverType: resolveClaimApproverType(claim, currentUser, accessProfile) });
                                                      setShowRejectModal(true);
                                                    }}
                                                  >
                                                    Reject
                                                  </button>
                                                  <button
                                                    type="button"
                                                    className="h-9 px-4 rounded-md bg-[#16A34A] text-white text-[15px] font-semibold hover:bg-[#15803D] transition-colors"
                                                    onClick={() => handleApproveClaim(claim.claimId, resolveClaimApproverType(claim, currentUser, accessProfile), 'Approved')}
                                                  >
                                                    Approve
                                                  </button>
                                                </div>
                                                <button
                                                  type="button"
                                                  className="h-9 px-4 rounded-md border border-[#B8C0CC] bg-white text-[#334155] text-[15px] font-semibold hover:bg-[#F8FAFC] transition-colors"
                                                  onClick={() => {
                                                    setPendingAction({ claimId: claim.claimId, type: 'return' });
                                                    setShowReturnModal(true);
                                                  }}
                                                >
                                                  Return
                                                </button>
                                              </div>
                                            )}
                                          </div>
                                          <button
                                            className="inline-flex items-center justify-center text-[#1F2937] transition-colors hover:text-[#0F172A] mt-1 ml-auto"
                                            onClick={() => (((activeView === 'dashboard' && dashboardTab === 'returned') || (activeView === 'my-claims' && canEditClaim(claim))) ? loadEditClaim(claim) : loadClaimDetails(claim))}
                                            aria-label={claim.status === 'Draft' || claim.status === 'Returned' ? 'Edit claim' : 'View claim'}
                                          >
                                            <ArrowRight size={22} />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                  {!hasDesktopRows && desktopEmptyState && (
                                    <tr>
                                      <td colSpan={desktopColumnCount} className="px-8 py-10 align-top">
                                        <div className="min-h-[120px] flex items-center justify-start">
                                          <div className="max-w-[560px] text-left">
                                            {desktopEmptyState.icon && <div className="text-[52px] mb-4">{desktopEmptyState.icon}</div>}
                                            <div className="text-[28px] font-semibold text-[#0F172A] mb-3">{desktopEmptyState.title}</div>
                                            <div className="text-[17px] leading-8 text-[#52607A] break-words">{desktopEmptyState.description}</div>
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                            {desktopTableHasOverflow && (
                              <div className="mt-2 px-1">
                                <div
                                  ref={desktopTableBottomTrackRef}
                                  className="desktop-table-bottom-track"
                                  onPointerDown={handleDesktopBottomTrackPointerDown}
                                  aria-label="Table horizontal scrollbar"
                                >
                                  <div
                                    ref={desktopTableBottomThumbRef}
                                    className={`desktop-table-bottom-thumb ${isDesktopBottomThumbDragging ? 'is-dragging' : ''}`}
                                    style={{
                                      width: desktopBottomThumbWidth,
                                      transform: `translateX(${desktopBottomThumbLeft}px)`
                                    }}
                                    onPointerDown={handleDesktopBottomThumbPointerDown}
                                    onPointerMove={handleDesktopBottomThumbPointerMove}
                                    onPointerUp={handleDesktopBottomThumbPointerUp}
                                    onPointerCancel={handleDesktopBottomThumbPointerUp}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}
      </div>

      {/* Add Claims Modal */}
      {showAddClaimModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
              <h3 className="text-2xl font-bold text-gray-900">Submit Claim</h3>
              <button onClick={() => setShowAddClaimModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Status and Claim Info */}
              <div className="space-y-3">
                <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">Draft</span>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Claimant</label>
                    <p className="text-sm font-medium text-gray-900">{currentUser?.full_name}</p>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Entity</label>
                    <p className="text-sm font-medium text-gray-900">{currentUser?.entity}</p>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Department</label>
                    <p className="text-sm font-medium text-gray-900">{currentUser?.department || currentUser?.entity}</p>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Total Claim</label>
                    <p className="text-sm font-bold text-gray-900">
                      RM {claimForm.items.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Claim Month and Remark */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Claim Month / Year<span className="text-red-500">*</span></label>
                  <select
                    value={claimForm.monthYear}
                    onChange={(e) => setClaimForm({ ...claimForm, monthYear: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  >
                    {claimMonthOptions.map((monthOption) => (
                      <option key={monthOption.value} value={monthOption.value}>
                        {monthOption.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Remark</label>
                  <input
                    type="text"
                    value={claimForm.remarks}
                    onChange={(e) => setClaimForm({ ...claimForm, remarks: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                    placeholder="Optional remarks..."
                  />
                </div>
              </div>

              {/* Claim Items */}
              <div className="space-y-4">
                {claimForm.items.map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-sm font-medium text-gray-700">{String(index).padStart(2, '0')}</span>
                      {claimForm.items.length > 1 && (
                        <button onClick={() => handleRemoveClaimItem(index)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Claim Type<span className="text-red-500">*</span></label>
                        <select
                          value={item.claimType}
                          onChange={(e) => handleUpdateClaimItem(index, 'claimType', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 ${
                            claimQuotaErrorRows.includes(index)
                              ? 'border-red-400 focus:ring-red-200'
                              : 'border-gray-300 focus:ring-yellow-400'
                          }`}
                        >
                          {getClaimTypeOptions(currentUser?.entity || DEFAULT_ENTITY_NAME, item.claimType).map((claimTypeOption) => (
                            <option key={claimTypeOption} value={claimTypeOption}>{claimTypeOption}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Expenses Type<span className="text-red-500">*</span></label>
                        <select
                          value={item.expensesType}
                          onChange={(e) => handleUpdateClaimItem(index, 'expensesType', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 ${
                            claimQuotaErrorRows.includes(index)
                              ? 'border-red-400 focus:ring-red-200'
                              : 'border-gray-300 focus:ring-yellow-400'
                          }`}
                        >
                          {getExpenseOptions(currentUser?.entity || DEFAULT_ENTITY_NAME, item.claimType, item.expensesType, { preserveCurrent: false }).map(et => (
                            <option key={et} value={et}>{et}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Desc. / Project Name{requiresDescriptionField(item.claimType) && <span className="text-red-500">*</span>}
                        </label>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => handleUpdateClaimItem(index, 'description', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 ${
                            claimValidationError && requiresDescriptionField(item.claimType) && !String(item.description || '').trim()
                              ? 'border-red-400 focus:ring-red-200'
                              : 'border-gray-300 focus:ring-yellow-400'
                          }`}
                          placeholder="Description..."
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Amount<span className="text-red-500">*</span></label>
                        <input
                          type="number"
                          value={item.amount}
                          onChange={(e) => handleUpdateClaimItem(index, 'amount', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 ${
                            claimQuotaErrorRows.includes(index)
                              ? 'border-red-400 focus:ring-red-200'
                              : 'border-gray-300 focus:ring-yellow-400'
                          }`}
                          placeholder="RM 0.00"
                          step="0.01"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Business Name{requiresBusinessNameField(item.claimType) && <span className="text-red-500">*</span>}
                        </label>
                        <input
                          type="text"
                          value={item.businessName}
                          onChange={(e) => handleUpdateClaimItem(index, 'businessName', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 ${
                            claimValidationError && isProjectsClaimType(item.claimType) && !String(item.businessName || '').trim()
                              ? 'border-red-400 focus:ring-red-200'
                              : 'border-gray-300 focus:ring-yellow-400'
                          }`}
                          placeholder="Business name..."
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Receipt / Invoice No.{requiresReceiptNumberField(item.claimType) && <span className="text-red-500">*</span>}</label>
                        <input
                          type="text"
                          value={item.receiptNumber}
                          onChange={(e) => handleUpdateClaimItem(index, 'receiptNumber', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                          placeholder="Receipt number..."
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Attachment<span className="text-red-500">*</span></label>
                        <input
                          type="file"
                          onClick={handleFileInputClick}
                          onChange={(e) => handleUpdateClaimItem(index, 'file', e.target.files[0])}
                          className="hidden"
                          id={`file-${index}`}
                          accept=".pdf,.jpg,.jpeg,.png"
                        />
                        {item.file ? (
                          <div className="inline-flex items-center gap-2 pl-3 pr-2 py-1.5 border-2 border-gray-900 rounded-full bg-white max-w-full">
                            <FileText className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                            <button
                              type="button"
                              onClick={() => openLocalAttachmentPreview(item.file, item.file?.name)}
                              className="text-xs font-medium text-gray-900 truncate max-w-[180px] hover:underline text-left"
                              title={item.file?.name || 'Open attachment'}
                            >
                              {item.file.name}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleUpdateClaimItem(index, 'file', null)}
                              className="w-5 h-5 rounded-full bg-yellow-400 hover:bg-yellow-500 flex items-center justify-center flex-shrink-0 transition-colors"
                            >
                              <X className="w-3.5 h-3.5 text-gray-900" />
                            </button>
                          </div>
                        ) : (
                          <label
                            htmlFor={`file-${index}`}
                            className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm cursor-pointer hover:bg-gray-50"
                          >
                            <FileText className="w-4 h-4 text-yellow-600" />
                            <span className="text-xs text-gray-500">Choose file...</span>
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                <button
                  onClick={handleAddClaimItem}
                  className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:border-yellow-400 hover:text-yellow-600 transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Item
                </button>
              </div>

              {/* Error Messages */}
              {claimValidationError && (
                <div className="text-xs text-red-500 italic">
                  *Please fill in all mandatory fields
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-between items-center">
                <button
                  type="button"
                  onClick={() => {
                    if (!ANALYZE_RECEIPT_ENABLED) return;
                    setShowAnalyzeModal(true);
                  }}
                  disabled={!ANALYZE_RECEIPT_ENABLED}
                  className="px-6 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg bg-white hover:border-yellow-400 hover:text-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Analyze Receipt
                </button>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleAddClaimItem}
                    className="px-6 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-lg transition-colors"
                  >
                    Add Rows
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmitClaim}
                    disabled={isSubmittingClaim}
                    className="px-8 text-gray-900 font-bold rounded-lg transition-colors bg-yellow-400 hover:bg-yellow-500 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSubmittingClaim ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Claim Modal */}
      {showEditClaimModal && editClaimData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b px-8 py-5 flex justify-between items-center z-10">
              <h3 className="text-2xl font-bold text-gray-900">Edit Claim</h3>
              <button onClick={() => { setShowEditClaimModal(false); setEditClaimData(null); setEditClaimOriginalData(null); }} className="text-gray-400 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="px-8 py-6">
              {/* Status Badge */}
              <div className="mb-3">
                <span className={`inline-block px-4 py-1 rounded-full text-sm font-medium ${getStatusColor(editClaimData.status)}`}>
                  {editClaimData.status}
                </span>
              </div>

              {/* Claim ID */}
              <div className="mb-6">
                <h4 className="text-xl font-bold text-gray-900">{editClaimData.id}</h4>
                <p className="text-xs text-gray-400 italic">Claim ID</p>
              </div>

              {/* Separator */}
              <hr className="mb-6 border-gray-200" />

              {/* Read-only Info Row 1 */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-4 mb-4">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Claimant</p>
                  <p className="text-sm font-semibold text-gray-900">{editClaimData.requestor || editClaimData.claimant || currentUser?.full_name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Entity</p>
                  <p className="text-sm font-semibold text-gray-900">{editClaimData.entity}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Department</p>
                  <p className="text-sm font-semibold text-gray-900">{editClaimData.department}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Approver</p>
                  <p className="text-sm font-semibold text-gray-900">{editClaimData.manager}</p>
                </div>
              </div>

              {/* Editable Row 2: Total Claim (read-only), Claim Month (editable), Remark (editable) */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-4 mb-8">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Total Claim</p>
                  <p className="text-sm font-semibold text-gray-900">
                    RM {editClaimForm.items.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0).toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Claim Month<span className="text-red-500">*</span></p>
                  <select
                    value={editClaimForm.monthYear}
                    onChange={(e) => setEditClaimForm({ ...editClaimForm, monthYear: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  >
                    {claimMonthOptions.map((monthOption) => (
                      <option key={monthOption.value} value={monthOption.value}>
                        {monthOption.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-xs text-gray-400 mb-1">Remark</p>
                  <input
                    type="text"
                    value={editClaimForm.remarks}
                    onChange={(e) => setEditClaimForm({ ...editClaimForm, remarks: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  />
                </div>
              </div>

              {/* Editable Claim Items */}
              <div className="space-y-4">
                {editClaimForm.items.map((item, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-lg p-5">
                    {/* Item header with trash and number */}
                    <div className="flex items-start gap-4 mb-4">
                      <div className="flex items-center gap-2 min-w-[60px]">
                        <button
                          onClick={() => handleEditRemoveItem(idx)}
                          disabled={editClaimForm.items.length <= 1}
                          title={editClaimForm.items.length <= 1 ? 'At least one item is required' : 'Remove item'}
                          className={`${
                            editClaimForm.items.length <= 1
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-red-400 hover:text-red-600'
                          }`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <span className="text-lg font-bold text-gray-400">{String(idx + 1).padStart(2, '0')}</span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3 flex-1">
                        <div>
                          <p className="text-xs text-gray-400 mb-1">Claim Type<span className="text-red-500">*</span></p>
                          <select
                            value={item.claimType}
                            onChange={(e) => handleEditUpdateItem(idx, 'claimType', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 bg-white ${
                              editQuotaErrorRows.includes(idx)
                                ? 'border-red-400 focus:ring-red-200'
                                : 'border-gray-300 focus:ring-yellow-400'
                            }`}
                          >
                            {getClaimTypeOptions(editClaimData?.entity || currentUser?.entity || DEFAULT_ENTITY_NAME, item.claimType).map((claimTypeOption) => (
                              <option key={claimTypeOption} value={claimTypeOption}>{claimTypeOption}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-1">Expenses Type<span className="text-red-500">*</span></p>
                          <select
                            value={item.expensesType}
                            onChange={(e) => handleEditUpdateItem(idx, 'expensesType', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 bg-white ${
                              editQuotaErrorRows.includes(idx)
                                ? 'border-red-400 focus:ring-red-200'
                                : 'border-gray-300 focus:ring-yellow-400'
                            }`}
                          >
                          {getExpenseOptions(editClaimData?.entity || currentUser?.entity || DEFAULT_ENTITY_NAME, item.claimType, item.expensesType, { preserveCurrent: true }).map(et => (
                            <option key={et} value={et}>{et}</option>
                          ))}
                          </select>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-1">
                            Desc. / Project Name{requiresDescriptionField(item.claimType) && <span className="text-red-500">*</span>}
                          </p>
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => handleEditUpdateItem(idx, 'description', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 ${
                              editValidationError && requiresDescriptionField(item.claimType) && !String(item.description || '').trim()
                                ? 'border-red-400 focus:ring-red-200'
                                : 'border-gray-300 focus:ring-yellow-400'
                            }`}
                          />
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-1">Amount<span className="text-red-500">*</span></p>
                          <input
                            type="number"
                            value={item.amount}
                            onChange={(e) => handleEditUpdateItem(idx, 'amount', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 ${
                              editQuotaErrorRows.includes(idx)
                                ? 'border-red-400 focus:ring-red-200'
                                : 'border-gray-300 focus:ring-yellow-400'
                            }`}
                            placeholder="RM 0"
                            step="0.01"
                          />
                        </div>
                      </div>
                    </div>
                    {/* Row 2: Business Name, Receipt No, Attachment */}
                    <div className="flex items-start gap-4">
                      <span className="min-w-[60px]"></span>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 flex-1">
                        <div>
                          <p className="text-xs text-gray-400 mb-1">
                            Business Name{requiresBusinessNameField(item.claimType) && <span className="text-red-500">*</span>}
                          </p>
                          <input
                            type="text"
                            value={item.businessName}
                            onChange={(e) => handleEditUpdateItem(idx, 'businessName', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 ${
                              editValidationError && isProjectsClaimType(item.claimType) && !String(item.businessName || '').trim()
                                ? 'border-red-400 focus:ring-red-200'
                                : 'border-gray-300 focus:ring-yellow-400'
                            }`}
                          />
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-1">Receipt / Invoice No.{requiresReceiptNumberField(item.claimType) && <span className="text-red-500">*</span>}</p>
                          <input
                            type="text"
                            value={item.receiptNumber}
                            onChange={(e) => handleEditUpdateItem(idx, 'receiptNumber', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                          />
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-1">Attachment<span className="text-red-500">*</span></p>
                          <input
                            type="file"
                            onClick={handleFileInputClick}
                            onChange={(e) => handleEditUpdateItem(idx, 'file', e.target.files[0])}
                            className="hidden"
                            id={`edit-file-${idx}`}
                            accept=".pdf,.jpg,.jpeg,.png"
                          />
                          {item.file ? (
                            <div className="inline-flex items-center gap-2 pl-3 pr-2 py-1.5 border-2 border-gray-900 rounded-full bg-white max-w-full">
                              <FileText className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                              <button
                                type="button"
                                onClick={() => openLocalAttachmentPreview(item.file, item.file?.name)}
                                className="text-xs font-medium text-gray-900 truncate max-w-[150px] hover:underline text-left"
                                title={item.file?.name || 'Open attachment'}
                              >
                                {item.file.name}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleEditUpdateItem(idx, 'file', null)}
                                className="w-5 h-5 rounded-full bg-yellow-400 hover:bg-yellow-500 flex items-center justify-center flex-shrink-0"
                              >
                                <X className="w-3.5 h-3.5 text-gray-900" />
                              </button>
                            </div>
                          ) : item.existingFile ? (
                            <div className="inline-flex items-center gap-2 pl-3 pr-2 py-1.5 border-2 border-gray-900 rounded-full bg-white max-w-full">
                              <FileText className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                              <a
                                href={item.existingFile}
                                onClick={(e) => {
                                  e.preventDefault();
                                  openAttachmentPreview(
                                    item.existingFile,
                                    buildAttachmentDisplayName({
                                      claimId: editClaimData?.id,
                                      claimType: item.claimType,
                                      claimMonthYear: editClaimData?.monthYear,
                                      itemIndex: idx,
                                      totalItems: editClaimForm.items.length,
                                      originalFilename: item.fileName
                                    })
                                  );
                                }}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs font-medium text-gray-900 truncate max-w-[150px] hover:underline"
                                title={item.fileName || 'Open attachment'}
                              >
                                {buildAttachmentDisplayName({
                                  claimId: editClaimData?.id,
                                  claimType: item.claimType,
                                  claimMonthYear: editClaimData?.monthYear,
                                  itemIndex: idx,
                                  totalItems: editClaimForm.items.length,
                                  originalFilename: item.fileName
                                })}
                              </a>
                              <button
                                type="button"
                                onClick={() => { handleEditUpdateItem(idx, 'existingFile', null); handleEditUpdateItem(idx, 'fileName', null); }}
                                className="w-5 h-5 rounded-full bg-yellow-400 hover:bg-yellow-500 flex items-center justify-center flex-shrink-0"
                              >
                                <X className="w-3.5 h-3.5 text-gray-900" />
                              </button>
                            </div>
                          ) : (
                            <label
                              htmlFor={`edit-file-${idx}`}
                              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm cursor-pointer hover:bg-gray-100"
                            >
                              <FileText className="w-4 h-4 text-yellow-600" />
                              <span className="text-xs text-gray-500">Choose file...</span>
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Validation Error */}
              {editValidationError && (
                <p className="text-xs text-red-500 italic mt-4">*Please fill up mandatory fields</p>
              )}

              {/* Action Buttons */}
              <div className="flex justify-between items-center gap-3 mt-6">
                <button
                  type="button"
                  onClick={handleEditAddItem}
                  className="px-6 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-lg transition-colors"
                >
                  Add Rows
                </button>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => { setShowEditClaimModal(false); setEditClaimData(null); setEditClaimOriginalData(null); }}
                    className="px-8 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Quit Edit
                  </button>
                  <button
                    type="button"
                    onClick={handleResubmitClaim}
                    disabled={isResubmittingClaim}
                    className="px-8 text-gray-900 font-bold rounded-lg transition-colors bg-yellow-400 hover:bg-yellow-500 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isResubmittingClaim
                      ? ((String(editClaimData?.rawStatus || '').toLowerCase() === 'draft' || String(editClaimData?.status || '').toLowerCase() === 'draft')
                        ? 'Submitting...'
                        : 'Resubmitting...')
                      : ((String(editClaimData?.rawStatus || '').toLowerCase() === 'draft' || String(editClaimData?.status || '').toLowerCase() === 'draft')
                        ? 'Submit Changes'
                        : 'Resubmit Changes')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Claim Details Modal */}
      {selectedClaim && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b px-8 py-5 flex justify-between items-center z-10">
              <h3 className="text-2xl font-bold text-gray-900">View Claim</h3>
              <button onClick={() => setSelectedClaim(null)} className="text-gray-400 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="px-8 py-6">
              {/* Status Badge */}
              <div className="mb-3">
                <span className={`inline-block px-4 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedClaim.status)}`}>
                  {selectedClaim.status}
                </span>
              </div>

              {/* Claim ID */}
              <div className="mb-6">
                <h4 className="text-xl font-bold text-gray-900">{selectedClaim.id}</h4>
                <p className="text-xs text-gray-400 italic">Claim ID</p>
              </div>

              {/* Claim Info Grid - Row 1 */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-4 mb-4">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Claimant</p>
                  <p className="text-sm font-semibold text-gray-900">{selectedClaim.requestor || selectedClaim.claimant}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Entity</p>
                  <p className="text-sm font-semibold text-gray-900">{selectedClaim.entity}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Department</p>
                  <p className="text-sm font-semibold text-gray-900">{selectedClaim.department}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Approver</p>
                  <p className="text-sm font-semibold text-gray-900">{selectedClaim.manager}</p>
                </div>
              </div>

              {/* Claim Info Grid - Row 2 */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-4 mb-8">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Total Claim</p>
                  <p className="text-sm font-semibold text-gray-900">RM {Number(selectedClaim.amount || 0).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Claim Month</p>
                  <p className="text-sm font-semibold text-gray-900">{formatMonthYearLabel(selectedClaim.monthYear)}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-xs text-gray-400 mb-1">Remark</p>
                  <p className="text-sm font-semibold text-gray-900">{selectedClaim.remarks || '-'}</p>
                </div>
              </div>

              {/* Claim Items */}
              <div className="space-y-4">
                {selectedClaim.items && selectedClaim.items.length > 0 ? (
                  selectedClaim.items.map((item, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-lg p-5">
                      {/* Item number */}
                      <div className="flex items-start gap-4 mb-4">
                        <span className="text-lg font-bold text-gray-400 min-w-[30px]">{String(idx + 1).padStart(2, '0')}</span>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4 flex-1">
                          <div className="min-w-0">
                            <p className="text-xs text-gray-400 mb-1">Claim Type</p>
                            <p className="text-sm font-semibold text-gray-900 break-words">{item.claim_type || item.claimType}</p>
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs text-gray-400 mb-1">Expenses Type</p>
                            <p className="text-sm font-semibold text-gray-900 uppercase break-words">{item.expense_type || item.expensesType}</p>
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs text-gray-400 mb-1">Desc. / Project Name</p>
                            <p className="text-sm font-semibold text-gray-900 break-words">{item.description || '-'}</p>
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs text-gray-400 mb-1">Amount</p>
                            <p className="text-sm font-semibold text-gray-900">RM {resolveItemAmount(item).toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <span className="min-w-[30px]"></span>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4 flex-1">
                          <div className="min-w-0">
                            <p className="text-xs text-gray-400 mb-1">Business Name</p>
                            <p className="text-sm font-semibold text-gray-900 break-words">{item.business_name || '-'}</p>
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs text-gray-400 mb-1">Receipt / Invoice No.</p>
                            <p className="text-sm font-semibold text-gray-900 break-words">{item.receipt_number || '-'}</p>
                          </div>
                          <div className="min-w-0 lg:col-span-2">
                            <p className="text-xs text-gray-400 mb-1">Attachment</p>
                            {(item.file_url || (typeof item.file_path === 'string' && /^https?:\/\//i.test(item.file_path)) || item.file) ? (
                              <a
                                href={item.file_url || (typeof item.file_path === 'string' && /^https?:\/\//i.test(item.file_path) ? item.file_path : '#')}
                                onClick={(e) => {
                                  e.preventDefault();
                                  openAttachmentPreview(
                                    item.file_url || (typeof item.file_path === 'string' && /^https?:\/\//i.test(item.file_path) ? item.file_path : ''),
                                    buildAttachmentDisplayName({
                                      claimId: selectedClaim?.id,
                                      claimType: item.claim_type,
                                      claimMonthYear: selectedClaim?.monthYear,
                                      itemIndex: idx,
                                      totalItems: selectedClaim?.items?.length || 1,
                                      originalFilename: item.file_name
                                    })
                                  );
                                }}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 cursor-pointer hover:bg-gray-100"
                              >
                                <FileText className="w-4 h-4 text-gray-500" />
                                <span className="truncate max-w-[200px]">
                                  {buildAttachmentDisplayName({
                                    claimId: selectedClaim?.id,
                                    claimType: item.claim_type,
                                    claimMonthYear: selectedClaim?.monthYear,
                                    itemIndex: idx,
                                    totalItems: selectedClaim?.items?.length || 1,
                                    originalFilename: item.file_name
                                  })}
                                </span>
                              </a>
                            ) : (
                              <p className="text-sm font-semibold text-gray-900">-</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="bg-gray-50 rounded-lg p-6 text-center text-gray-500">
                    No claim items found
                  </div>
                )}
              </div>

              {/* Approval Actions */}
              {(activeView === 'my-claims' || activeView === 'dashboard') && canEditClaim(selectedClaim) && (
                <div className="border-t mt-6 pt-4 flex justify-end">
                  <button
                    onClick={() => {
                      const claimToEdit = selectedClaim;
                      setSelectedClaim(null);
                      loadEditClaim(claimToEdit);
                    }}
                    className="px-6 py-2 border-2 border-gray-900 text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    Edit Claim
                  </button>
                </div>
              )}

              {/* Approval Actions */}
              {(activeView === 'approval' || (activeView === 'dashboard' && dashboardTab === 'pending') || activeView === 'report') &&
               canTakeApprovalAction(selectedClaim) && (
                <div className="border-t mt-6 pt-5">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
                    <button
                      onClick={() => {
                        setPendingAction({ claimId: selectedClaim.claimId, type: 'return' });
                        setShowReturnModal(true);
                        setSelectedClaim(null);
                      }}
                      className="w-full sm:w-52 bg-white border border-gray-400 text-gray-800 px-4 py-2.5 rounded font-semibold hover:bg-gray-50 transition-colors"
                    >
                      Return
                    </button>
                    <button
                      onClick={() => {
                        setPendingAction({ claimId: selectedClaim.claimId, type: 'reject', approverType: resolveClaimApproverType(selectedClaim, currentUser, accessProfile) });
                        setShowRejectModal(true);
                        setSelectedClaim(null);
                      }}
                      style={{ backgroundColor: '#EE4056' }}
                      className="w-full sm:w-52 text-white px-4 py-2.5 rounded font-semibold transition-colors hover:opacity-90"
                    >
                      Reject
                    </button>
                    <button
                      onClick={() => {
                        handleApproveClaim(selectedClaim.claimId, resolveClaimApproverType(selectedClaim, currentUser, accessProfile), 'Approved');
                        setSelectedClaim(null);
                      }}
                      className="w-full sm:w-52 bg-green-600 text-white px-4 py-2.5 rounded font-semibold hover:bg-green-700 transition-colors"
                    >
                      Approve
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject Confirmation Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex justify-end mb-4">
              <button onClick={() => { setShowRejectModal(false); setRejectReason(''); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <img src="/error-icon.png" alt="Error" className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Are you sure you want to</h3>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">reject this claim?</h3>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Reason of Rejection</label>
              <input
                type="text"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder="Please fill in the reason"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => { setShowRejectModal(false); setRejectReason(''); }}
                className="px-4 py-2 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (rejectReason && pendingAction) {
                    setShowRejectModal(false);
                    try {
                      await handleApproveClaim(pendingAction.claimId, pendingAction.approverType, 'Rejected');
                      setRejectReason('');
                      setPendingAction(null);
                    } catch (error) {
                      console.error('Failed to reject claim:', error);
                    }
                  } else {
                    alert('Please provide a reason for rejection');
                  }
                }}
                style={{ backgroundColor: '#EE4056' }}
                className="px-4 py-2 text-white font-semibold rounded-lg transition-colors hover:opacity-90"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Return Confirmation Modal */}
      {showReturnModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex justify-end mb-4">
              <button onClick={() => { setShowReturnModal(false); setReturnReason(''); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <img src="/reply-icon.png" alt="Return" className="w-8 h-8" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-4">
                By returning this to claimant, claimant need to edit &amp; resubmit this claim. Are you sure?
              </h3>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Reason of Return</label>
              <input
                type="text"
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
                placeholder="Please fill in the reason"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => { setShowReturnModal(false); setReturnReason(''); }}
                className="px-4 py-2 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (returnReason && pendingAction) {
                    setShowReturnModal(false);
                    try {
                      await claimAPI.returnClaim(pendingAction.claimId, returnReason);
                      await loadPendingApprovals();
                      setReturnReason('');
                      setPendingAction(null);
                      alert('Claim returned successfully!');
                    } catch (error) {
                      console.error('Failed to return claim:', error);
                      alert(error.message || 'Failed to return claim. Please try again.');
                    }
                  } else {
                    alert('Please provide a reason for return');
                  }
                }}
                className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-lg transition-colors"
              >
                Return
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Excel Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Export Excel Sheet</h3>
              <button onClick={() => setShowExportModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-6">Export datas with the filter below:</p>

            {/* Date Filter */}
            <div className="mb-6 space-y-4">
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-2">
                  <input
                    type="radio"
                    name="export-date-type"
                    checked={exportFilters.dateType === 'last_action'}
                    onChange={() => setExportFilters({ ...exportFilters, dateType: 'last_action' })}
                  />
                  Last Action Date
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="date"
                    value={exportFilters.lastActionStart}
                    onChange={(e) => setExportFilters({ ...exportFilters, lastActionStart: e.target.value })}
                    disabled={exportFilters.dateType !== 'last_action'}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 disabled:bg-gray-100 disabled:text-gray-400"
                  />
                  <ArrowLeft className="w-5 h-5 text-gray-400 rotate-180" />
                  <input
                    type="date"
                    value={exportFilters.lastActionEnd}
                    onChange={(e) => setExportFilters({ ...exportFilters, lastActionEnd: e.target.value })}
                    disabled={exportFilters.dateType !== 'last_action'}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 disabled:bg-gray-100 disabled:text-gray-400"
                  />
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-2">
                  <input
                    type="radio"
                    name="export-date-type"
                    checked={exportFilters.dateType === 'submission'}
                    onChange={() => setExportFilters({ ...exportFilters, dateType: 'submission' })}
                  />
                  Submission Date
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="date"
                    value={exportFilters.submissionStart}
                    onChange={(e) => setExportFilters({ ...exportFilters, submissionStart: e.target.value })}
                    disabled={exportFilters.dateType !== 'submission'}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 disabled:bg-gray-100 disabled:text-gray-400"
                  />
                  <ArrowLeft className="w-5 h-5 text-gray-400 rotate-180" />
                  <input
                    type="date"
                    value={exportFilters.submissionEnd}
                    onChange={(e) => setExportFilters({ ...exportFilters, submissionEnd: e.target.value })}
                    disabled={exportFilters.dateType !== 'submission'}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 disabled:bg-gray-100 disabled:text-gray-400"
                  />
                </div>
              </div>
            </div>

            {/* Entity Filter */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-900 mb-3">Entity</label>
              <div className="space-y-3 max-h-40 overflow-y-auto pr-1">
                {companies
                  .filter((company) => company.is_active)
                  .sort((a, b) => String(a.full_name || '').localeCompare(String(b.full_name || '')))
                  .map((company) => {
                    const checked = Boolean(exportFilters.entityFilters?.[company.full_name]);
                    return (
                      <label key={company.id} className="flex items-center gap-3 cursor-pointer">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                          checked
                            ? 'bg-yellow-400 border-yellow-400'
                            : 'border-gray-300'
                        }`}>
                          {checked && (
                            <CheckCircle className="w-4 h-4 text-gray-900" />
                          )}
                        </div>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => setExportFilters({
                            ...exportFilters,
                            entityFilters: {
                              ...exportFilters.entityFilters,
                              [company.full_name]: e.target.checked
                            }
                          })}
                          className="hidden"
                        />
                        <span className="text-sm text-gray-900">{company.full_name}</span>
                      </label>
                    );
                  })}
              </div>
            </div>

            {/* Status Filter */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-900 mb-3">Claim Status</label>
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                    exportFilters.statusFilters.approved
                      ? 'bg-yellow-400 border-yellow-400'
                      : 'border-gray-300'
                  }`}>
                    {exportFilters.statusFilters.approved && (
                      <CheckCircle className="w-4 h-4 text-gray-900" />
                    )}
                  </div>
                  <input
                    type="checkbox"
                    checked={exportFilters.statusFilters.approved}
                    onChange={(e) => setExportFilters({
                      ...exportFilters,
                      statusFilters: { ...exportFilters.statusFilters, approved: e.target.checked }
                    })}
                    className="hidden"
                  />
                  <span className="text-sm text-gray-900">Approved</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                    exportFilters.statusFilters.pending
                      ? 'bg-yellow-400 border-yellow-400'
                      : 'border-gray-300'
                  }`}>
                    {exportFilters.statusFilters.pending && (
                      <CheckCircle className="w-4 h-4 text-gray-900" />
                    )}
                  </div>
                  <input
                    type="checkbox"
                    checked={exportFilters.statusFilters.pending}
                    onChange={(e) => setExportFilters({
                      ...exportFilters,
                      statusFilters: { ...exportFilters.statusFilters, pending: e.target.checked }
                    })}
                    className="hidden"
                  />
                  <span className="text-sm text-gray-900">Pending</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                    exportFilters.statusFilters.returned
                      ? 'bg-yellow-400 border-yellow-400'
                      : 'border-gray-300'
                  }`}>
                    {exportFilters.statusFilters.returned && (
                      <CheckCircle className="w-4 h-4 text-gray-900" />
                    )}
                  </div>
                  <input
                    type="checkbox"
                    checked={exportFilters.statusFilters.returned}
                    onChange={(e) => setExportFilters({
                      ...exportFilters,
                      statusFilters: { ...exportFilters.statusFilters, returned: e.target.checked }
                    })}
                    className="hidden"
                  />
                  <span className="text-sm text-gray-900">Return</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                    exportFilters.statusFilters.rejected
                      ? 'bg-yellow-400 border-yellow-400'
                      : 'border-gray-300'
                  }`}>
                    {exportFilters.statusFilters.rejected && (
                      <CheckCircle className="w-4 h-4 text-gray-900" />
                    )}
                  </div>
                  <input
                    type="checkbox"
                    checked={exportFilters.statusFilters.rejected}
                    onChange={(e) => setExportFilters({
                      ...exportFilters,
                      statusFilters: { ...exportFilters.statusFilters, rejected: e.target.checked }
                    })}
                    className="hidden"
                  />
                  <span className="text-sm text-gray-900">Rejected</span>
                </label>
              </div>
            </div>

            {/* Export Button */}
            <div className="flex justify-end">
              <button
                  onClick={async () => {
                    await exportToExcel();
                    setShowExportModal(false);
                  }}
                className="px-8 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold rounded-lg transition-colors"
              >
                Export
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Analyze Receipt Modal */}
      {ANALYZE_RECEIPT_ENABLED && showAnalyzeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Analyze Receipt</h3>
              <button
                disabled={isAnalyzing}
                onClick={() => {
                  if (isAnalyzing) return;
                  setShowAnalyzeModal(false);
                  resetAnalyzeState();
                }}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-3">
              Attach receipts to auto-fill claim rows. Each attachment maps to exactly one claim item.
            </p>
            <p className="text-xs text-gray-500 mb-6">
              Supported: PDF, JPG, PNG. Maximum {MAX_ANALYZE_FILE_SIZE_MB}MB per attachment.
            </p>

            <div
              className="mb-4"
              onDragOver={handleAnalyzeDragOver}
              onDragEnter={handleAnalyzeDragOver}
              onDragLeave={handleAnalyzeDragLeave}
              onDrop={handleAnalyzeDrop}
            >
              <input
                type="file"
                multiple
                accept="image/*,.pdf"
                onClick={handleFileInputClick}
                onChange={handleAnalyzeFileUpload}
                className="hidden"
                id="analyze-file-upload"
                disabled={isAnalyzing}
              />

              {analyzeFiles.length === 0 ? (
                <label
                  htmlFor="analyze-file-upload"
                  className={`flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                    isAnalyzeDragActive
                      ? 'border-yellow-500 bg-yellow-50'
                      : 'border-gray-300 hover:border-yellow-400 bg-gray-50'
                  }`}
                >
                  <FileText className="w-10 h-10 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">Click to upload or drag and drop files here</span>
                  <span className="text-xs text-gray-500 mt-1">1 file = 1 item</span>
                </label>
              ) : (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="grid grid-cols-12 bg-gray-50 border-b border-gray-200 px-4 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    <div className="col-span-2">Status</div>
                    <div className="col-span-7">Attachment</div>
                    <div className="col-span-3 text-right">Action</div>
                  </div>
                  <div className="max-h-72 overflow-y-auto divide-y divide-gray-100">
                    {analyzeFiles.map((file, index) => {
                      const statusItem = analyzeFileStatuses[index] || {
                        status: 'idle',
                        message: '',
                        warnings: []
                      };
                      const fileType = (file.type || inferMimeTypeFromFilename(file.name) || 'file').replace('application/', '').replace('image/', '').toUpperCase();
                      const isDone = statusItem.status === 'done';
                      const isError = statusItem.status === 'error';
                      const isProcessing = statusItem.status === 'processing' || (isAnalyzing && statusItem.status === 'idle');

                      return (
                        <div key={`${file.name}-${index}`} className="px-4 py-3">
                          <div className="grid grid-cols-12 items-start gap-2">
                            <div className="col-span-2">
                              <div className="inline-flex items-center gap-2 px-2 py-1 rounded border border-gray-200 text-xs">
                                {isProcessing && <div className="w-4 h-4 border-2 border-gray-300 border-t-yellow-500 rounded-full animate-spin" />}
                                {isDone && <CheckCircle className="w-4 h-4 text-green-600" />}
                                {isError && <X className="w-4 h-4 text-red-600" />}
                                {!isProcessing && !isDone && !isError && <span className="w-4 h-4 inline-block rounded-full bg-gray-300" />}
                              </div>
                            </div>
                            <div className="col-span-7 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                              <p className="text-xs text-gray-500">{fileType}</p>
                              {statusItem.message && (
                                <p className={`text-xs mt-1 ${isError ? 'text-red-600' : isDone ? 'text-green-600' : 'text-gray-500'}`}>
                                  {statusItem.message}
                                </p>
                              )}
                              {Array.isArray(statusItem.warnings) && statusItem.warnings.length > 0 && (
                                <ul className="mt-1 space-y-1">
                                  {statusItem.warnings.slice(0, 2).map((warning, warningIndex) => (
                                    <li key={warningIndex} className="text-xs text-amber-700">
                                      {warning}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                            <div className="col-span-3 text-right">
                              <button
                                onClick={() => handleRemoveAnalyzeFile(index)}
                                disabled={isAnalyzing}
                                className="text-xs px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="px-4 py-3 border-t border-gray-200 bg-white flex items-center justify-between">
                    <label
                      htmlFor={isAnalyzing ? undefined : 'analyze-file-upload'}
                      aria-disabled={isAnalyzing}
                      className={`text-sm font-semibold ${
                        isAnalyzing
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-yellow-700 cursor-pointer hover:text-yellow-800'
                      }`}
                    >
                      + Add more files
                    </label>
                    <div className="text-xs text-gray-600">
                      {analyzeFiles.length} file(s)
                    </div>
                  </div>
                </div>
              )}
            </div>

            {analyzeHasCompleted && (
              <div className="mb-5 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                <p className="text-sm font-semibold text-gray-900 mb-1">Analyze Summary</p>
                <p className="text-xs text-gray-700">
                  {analyzeSummary.created} created, {analyzeSummary.warningRows} created with warnings, {analyzeSummary.failed} failed
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-xs text-gray-700 max-w-sm italic" style={{ fontSize: '0.65rem' }}>
                Results may not always be accurate. Please verify all details before submitting.
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => {
                    if (isAnalyzing) return;
                    setShowAnalyzeModal(false);
                    resetAnalyzeState();
                  }}
                  disabled={isAnalyzing}
                  className="px-5 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAnalyzeReceipts}
                  disabled={isAnalyzing || analyzeFiles.length === 0}
                  className={`px-6 py-2 rounded-lg text-sm font-bold transition-colors ${
                    isAnalyzing || analyzeFiles.length === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-yellow-400 hover:bg-yellow-500 text-gray-900'
                  }`}
                >
                  {isAnalyzing ? 'Processing...' : 'Analyze'}
                </button>
                <button
                  onClick={handleApplyAnalyzedReceipts}
                  disabled={!analyzeHasCompleted}
                  className={`px-6 py-2 rounded-lg text-sm font-bold transition-colors ${
                    analyzeHasCompleted
                      ? 'bg-gray-900 text-white hover:bg-gray-800'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Done
                </button>
              </div>
            </div>
              </div>
        </div>
      )}

      {/* ==================== MANAGE USERS VIEW ==================== */}
      {/* ==================== MANAGE EXPENSE GL CODES VIEW ==================== */}
      {activeView === 'manage-expense-gl-codes' && canAccessManageExpenseGlCode && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8">
          {expenseGlCodeLoadError && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {expenseGlCodeLoadError}
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <input
                type="text"
                value={expenseGlCodeSearchQuery}
                onChange={(e) => setExpenseGlCodeSearchQuery(e.target.value)}
                placeholder="Search expense type or GL code..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
              />
              <Eye className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            </div>
            <select
              value={expenseGlCodeEntityFilter}
              onChange={(e) => setExpenseGlCodeEntityFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
            >
              <option value="">All Entities</option>
              {companies.filter(c => c.is_active).map(c => (
                <option key={c.id} value={c.full_name}>{c.full_name}</option>
              ))}
            </select>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {expenseGlCodes
              .filter(item => {
                const matchesSearch = !expenseGlCodeSearchQuery ||
                  (item.expense_type || '').toLowerCase().includes(expenseGlCodeSearchQuery.toLowerCase()) ||
                  (item.gl_code || '').toLowerCase().includes(expenseGlCodeSearchQuery.toLowerCase());
                const matchesEntity = matchesExpenseGlEntityFilter(item.entity, expenseGlCodeEntityFilter);
                return matchesSearch && matchesEntity;
              })
              .map(item => (
                <div key={item.id} className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-semibold text-gray-900">{item.expense_type}</p>
                      <p className="text-sm text-gray-500">{item.entity}</p>
                      <p className="text-xs text-gray-500 mt-1">Claim Type: {item.claim_type || '-'}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {item.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                    <div><span className="text-gray-500">GL Code:</span> {item.gl_code}</div>
                    <div><span className="text-gray-500">Expense:</span> {item.expense_type}</div>
                    <div className="col-span-2"><span className="text-gray-500">Entitlement:</span> {getEntitlementTypeLabel(getDisplayedEntitlementType({ claimType: item.claim_type, expenseType: item.expense_type, storedValue: item.entitlement_type }))}</div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleOpenExpenseGlCodeModal(item)}
                      className="flex-1 px-3 py-1.5 text-sm font-medium bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleToggleExpenseGlCodeActive(item)}
                      className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${item.is_active ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                    >
                      {item.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </div>
              ))}
            {expenseGlCodes.filter(item => {
              const matchesSearch = !expenseGlCodeSearchQuery ||
                (item.expense_type || '').toLowerCase().includes(expenseGlCodeSearchQuery.toLowerCase()) ||
                (item.gl_code || '').toLowerCase().includes(expenseGlCodeSearchQuery.toLowerCase());
              const matchesEntity = matchesExpenseGlEntityFilter(item.entity, expenseGlCodeEntityFilter);
              return matchesSearch && matchesEntity;
            }).length === 0 && (
              <div className="text-center py-8 text-gray-500">No GL codes found.</div>
            )}
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase">Entity</th>
                    <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase">Claim Type</th>
                    <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase">Expense Type</th>
                    <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase">Entitlement</th>
                    <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase">GL Code</th>
                    <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {expenseGlCodes
                    .filter(item => {
                      const matchesSearch = !expenseGlCodeSearchQuery ||
                        (item.expense_type || '').toLowerCase().includes(expenseGlCodeSearchQuery.toLowerCase()) ||
                        (item.gl_code || '').toLowerCase().includes(expenseGlCodeSearchQuery.toLowerCase());
                      const matchesEntity = matchesExpenseGlEntityFilter(item.entity, expenseGlCodeEntityFilter);
                      return matchesSearch && matchesEntity;
                    })
                    .map(item => (
                      <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="px-4 text-sm text-gray-700">{item.entity}</td>
                        <td className="px-4 text-sm text-gray-700">{item.claim_type || '-'}</td>
                        <td className="px-4 text-sm font-medium text-gray-900">{item.expense_type}</td>
                        <td className="px-4 text-sm text-gray-700">{getEntitlementTypeLabel(getDisplayedEntitlementType({ claimType: item.claim_type, expenseType: item.expense_type, storedValue: item.entitlement_type }))}</td>
                        <td className="px-4 text-sm text-gray-700">{item.gl_code}</td>
                        <td className="px-4 text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {item.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 text-sm">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleOpenExpenseGlCodeModal(item)}
                              className="px-3 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleToggleExpenseGlCodeActive(item)}
                              className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${item.is_active ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                            >
                              {item.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              {expenseGlCodes.filter(item => {
                const matchesSearch = !expenseGlCodeSearchQuery ||
                  (item.expense_type || '').toLowerCase().includes(expenseGlCodeSearchQuery.toLowerCase()) ||
                  (item.gl_code || '').toLowerCase().includes(expenseGlCodeSearchQuery.toLowerCase());
                const matchesEntity = matchesExpenseGlEntityFilter(item.entity, expenseGlCodeEntityFilter);
                return matchesSearch && matchesEntity;
              }).length === 0 && (
                <div className="text-center py-8 text-gray-500">No GL codes found.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ==================== MANAGE EXPENSES VIEW ==================== */}
      {activeView === 'manage-expenses' && canAccessManageExpenseGlCode && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8">
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <input
                type="text"
                value={expenseSearchQuery}
                onChange={(e) => setExpenseSearchQuery(e.target.value)}
                placeholder="Search expense type..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
              />
              <Eye className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            </div>
            <select
              value={expenseEntityFilter}
              onChange={(e) => setExpenseEntityFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
            >
              <option value="">All Entities</option>
              {companies.filter(c => c.is_active).map(c => (
                <option key={c.id} value={c.full_name}>{c.full_name}</option>
              ))}
            </select>
            <select
              value={expenseClaimTypeFilter}
              onChange={(e) => setExpenseClaimTypeFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
            >
              <option value="">All Claim Types</option>
              <option value="Projects">Projects</option>
              <option value="HR and Others">HR and Others</option>
            </select>
          </div>

          <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase">Entity</th>
                    <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase">Claim Type</th>
                    <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase">Expense Type</th>
                    <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {manageExpenseRows
                    .filter(item => {
                      const matchesSearch = !expenseSearchQuery ||
                        (item.expense_type || '').toLowerCase().includes(expenseSearchQuery.toLowerCase());
                      const matchesEntity = !expenseEntityFilter || item.entity === expenseEntityFilter;
                      const matchesClaimType = !expenseClaimTypeFilter || item.claim_type === expenseClaimTypeFilter;
                      return matchesSearch && matchesEntity && matchesClaimType;
                    })
                    .map(item => (
                      <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="px-4 text-sm text-gray-700">{item.entity}</td>
                        <td className="px-4 text-sm text-gray-700">{item.claim_type}</td>
                        <td className="px-4 text-sm font-medium text-gray-900">{item.expense_type}</td>
                        <td className="px-4 text-sm">
                          {item.source === 'default' ? (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">Default</span>
                          ) : (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {item.is_active ? 'Active' : 'Inactive'}
                            </span>
                          )}
                        </td>
                        <td className="px-4 text-sm">
                          {item.source === 'default' ? (
                            <span className="text-xs text-gray-500">Use "+ Add Expense" to override</span>
                          ) : (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleOpenExpenseModal(item)}
                                className="px-3 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleToggleExpenseActive(item)}
                                className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${item.is_active ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                              >
                                {item.is_active ? 'Deactivate' : 'Activate'}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              {manageExpenseRows.filter(item => {
                const matchesSearch = !expenseSearchQuery ||
                  (item.expense_type || '').toLowerCase().includes(expenseSearchQuery.toLowerCase());
                const matchesEntity = !expenseEntityFilter || item.entity === expenseEntityFilter;
                const matchesClaimType = !expenseClaimTypeFilter || item.claim_type === expenseClaimTypeFilter;
                return matchesSearch && matchesEntity && matchesClaimType;
              }).length === 0 && (
                <div className="text-center py-8 text-gray-500">No expenses found.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeView === 'manage-users' && canAccessManageUsers && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8">
          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <input
                type="text"
                value={userSearchQuery}
                onChange={(e) => {
                  setUserSearchQuery(e.target.value);
                  setUserCurrentPage(1);
                }}
                placeholder="Search by name or email..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
              />
              <Eye className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            </div>
            <select
              value={userEntityFilter}
              onChange={(e) => {
                setUserEntityFilter(e.target.value);
                setUserCurrentPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
            >
              <option value="">All Entities</option>
              {companies.filter(c => c.is_active).map(c => (
                <option key={c.id} value={c.full_name}>{c.full_name}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-between mb-4 text-sm text-gray-500">
            <span>{userIsLoading ? 'Loading users...' : `${userTotalCount} user${userTotalCount === 1 ? '' : 's'} found`}</span>
            <span>Page {userCurrentPage} of {userTotalPages}</span>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {userList.map(user => (
                <div key={user.id} className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-semibold text-gray-900">{user.full_name}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                    <div><span className="text-gray-500">Phone:</span> {user.phone}</div>
                    <div><span className="text-gray-500">Role:</span> {getRoleLabel(user.role)}</div>
                    <div><span className="text-gray-500">Entity:</span> {user.entity}</div>
                    <div><span className="text-gray-500">Dept:</span> {user.department || '-'}</div>
                    <div><span className="text-gray-500">Position:</span> {user.position || '-'}</div>
                    <div className="col-span-2"><span className="text-gray-500">Report To:</span> {user.manager_name || '-'}</div>
                    <div className="col-span-2">
                      <span className="text-gray-500">Telegram:</span>{' '}
                      {user.telegram_linked ? (
                        <span className="text-green-700 font-medium">Linked</span>
                      ) : (
                        <span className="text-gray-500">Not Linked</span>
                      )}
                      {getPrimaryTelegramId(user) && (
                        <span className="text-gray-700"> ({getPrimaryTelegramId(user)})</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleOpenUserModal(user)}
                      className="flex-1 px-3 py-1.5 text-sm font-medium bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleToggleUserActive(user)}
                      className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${user.is_active ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                    >
                      {user.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    {Boolean(user.telegram_linked) && (
                      <button
                        onClick={() => handleUnlinkTelegram(user)}
                        className="flex-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors bg-blue-100 text-blue-700 hover:bg-blue-200"
                      >
                        Unlink Telegram
                      </button>
                    )}
                  </div>
                </div>
              ))}
            {!userIsLoading && userList.length === 0 && (
              <div className="text-center py-8 text-gray-500">No users found.</div>
            )}
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase">Name</th>
                    <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase">Email</th>
                    <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase">Phone</th>
                    <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase">Entity</th>
                    <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase">Dept</th>
                    <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase">Position</th>
                    <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase">Role</th>
                    <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase">Report To</th>
                    <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase">Telegram</th>
                    <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="text-left px-4 text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {userList.map(user => (
                      <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="px-4 text-sm font-medium text-gray-900">{user.full_name}</td>
                        <td className="px-4 text-sm text-gray-600">{user.email}</td>
                        <td className="px-4 text-sm text-gray-600">{user.phone}</td>
                        <td className="px-4 text-sm text-gray-600">{user.entity}</td>
                        <td className="px-4 text-sm text-gray-600">{user.department || '-'}</td>
                        <td className="px-4 text-sm text-gray-600">{user.position || '-'}</td>
                        <td className="px-4 text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeClass(user.role)}`}>
                            {getRoleLabel(user.role)}
                          </span>
                        </td>
                        <td className="px-4 text-sm text-gray-600">{user.manager_name || '-'}</td>
                        <td className="px-4 text-sm text-gray-600">
                          <div className="flex flex-col">
                            <span className={user.telegram_linked ? 'text-green-700 font-medium' : 'text-gray-500'}>
                              {user.telegram_linked ? 'Linked' : 'Not Linked'}
                            </span>
                            {getPrimaryTelegramId(user) && (
                              <span className="text-xs text-gray-500">{getPrimaryTelegramId(user)}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 text-sm">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleOpenUserModal(user)}
                              className="px-3 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleToggleUserActive(user)}
                              className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${user.is_active ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                            >
                              {user.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                            {Boolean(user.telegram_linked) && (
                              <button
                                onClick={() => handleUnlinkTelegram(user)}
                                className="px-3 py-1 text-xs font-medium rounded-lg transition-colors bg-blue-100 text-blue-700 hover:bg-blue-200"
                              >
                                Unlink Telegram
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              {!userIsLoading && userList.length === 0 && (
                <div className="text-center py-8 text-gray-500">No users found.</div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between mt-4">
            <button
              onClick={() => setUserCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={userCurrentPage <= 1 || userIsLoading}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              Previous
            </button>
            <div className="flex items-center gap-2">
              {Array.from({ length: userTotalPages }, (_, index) => index + 1)
                .filter((page) => Math.abs(page - userCurrentPage) <= 2 || page === 1 || page === userTotalPages)
                .filter((page, index, arr) => arr.indexOf(page) === index)
                .map((page, index, arr) => (
                  <React.Fragment key={page}>
                    {index > 0 && arr[index - 1] !== page - 1 && (
                      <span className="px-1 text-sm text-gray-400">...</span>
                    )}
                    <button
                      onClick={() => setUserCurrentPage(page)}
                      disabled={userIsLoading}
                      className={`min-w-[40px] px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        page === userCurrentPage
                          ? 'bg-yellow-400 text-gray-900'
                          : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                      } disabled:opacity-50`}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                ))}
            </div>
            <button
              onClick={() => setUserCurrentPage((prev) => Math.min(prev + 1, userTotalPages))}
              disabled={userCurrentPage >= userTotalPages || userIsLoading}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}


      {/* ==================== CREATE/EDIT COMPANY MODAL ==================== */}
      {/* ==================== CREATE/EDIT EXPENSE MODAL ==================== */}
      {showExpenseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-bold">{editingExpense ? 'Edit Expense' : 'Add Expense'}</h3>
              <button onClick={() => setShowExpenseModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Entity *</label>
                <select
                  value={expenseFormData.entity}
                  onChange={(e) => setExpenseFormData({ ...expenseFormData, entity: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                >
                  <option value="">Select Entity</option>
                  {companies.filter(c => c.is_active).map(c => (
                    <option key={c.id} value={c.full_name}>{c.full_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Claim Type *</label>
                <select
                  value={expenseFormData.claim_type}
                  onChange={(e) => setExpenseFormData({ ...expenseFormData, claim_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                >
                  <option value="Projects">Projects</option>
                  <option value="HR and Others">HR and Others</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expense Type *</label>
                <input
                  type="text"
                  value={expenseFormData.expense_type}
                  onChange={(e) => setExpenseFormData({ ...expenseFormData, expense_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  placeholder="e.g. SUBSCRIPTION FEE - DIGITISATION TOOLS"
                />
              </div>
              {editingExpense && (
                <div className="flex items-center justify-between py-2">
                  <label className="text-sm font-medium text-gray-700">Active Status</label>
                  <button
                    onClick={() => setExpenseFormData({ ...expenseFormData, is_active: !expenseFormData.is_active })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${expenseFormData.is_active ? 'bg-green-500' : 'bg-gray-300'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${expenseFormData.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
              )}
            </div>
            <div className="flex gap-3 p-6 border-t">
              <button onClick={() => setShowExpenseModal(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors">Cancel</button>
              <button onClick={handleSaveExpense} className="flex-1 px-4 py-2 bg-yellow-400 rounded-lg font-semibold hover:bg-yellow-500 transition-colors">
                {editingExpense ? 'Save Changes' : 'Create Expense'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== CREATE/EDIT EXPENSE GL CODE MODAL ==================== */}
      {showExpenseGlCodeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-bold">{editingExpenseGlCode ? 'Edit Expense GL Code' : 'Add Expense GL Code'}</h3>
              <button onClick={() => { setShowInlineExpenseTypeModal(false); setShowExpenseGlCodeModal(false); }} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Entity *</label>
                <select
                  value={expenseGlCodeFormData.entity}
                  onChange={(e) => {
                    const nextEntity = e.target.value;
                    const nextClaimType = expenseGlCodeFormData.claim_type || 'Projects';
                    setExpenseGlCodeFormData((prev) => ({
                      ...prev,
                      entity: nextEntity,
                      claim_type: nextClaimType,
                      entitlement_type: getDisplayedEntitlementType({
                        claimType: nextClaimType,
                        expenseType: prev.expense_type,
                        storedValue: prev.entitlement_type
                      })
                    }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                >
                  <option value="">Select Entity</option>
                  {getExpenseGlEntityOptions(expenseGlCodeFormData.entity).map((entityName) => (
                    <option key={entityName} value={entityName}>{entityName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Claim Type *</label>
                <select
                  value={expenseGlCodeFormData.claim_type}
                  onChange={(e) => {
                    const nextClaimType = e.target.value;
                    setExpenseGlCodeFormData((prev) => ({
                      ...prev,
                      claim_type: nextClaimType,
                      entitlement_type: getDisplayedEntitlementType({
                        claimType: nextClaimType,
                        expenseType: prev.expense_type,
                        storedValue: prev.entitlement_type
                      })
                    }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                >
                  <option value="Projects">Projects</option>
                  <option value="HR and Others">HR and Others</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expense Type *</label>
                <select
                  value={expenseGlCodeFormData.expense_type}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    if (nextValue === '__create_new_expense_type__') {
                      handleOpenInlineExpenseTypeModal();
                      return;
                    }
                    const resolvedClaimType = expenseGlCodeFormData.claim_type || inferExpenseClaimType(expenseGlCodeFormData.entity, nextValue);
                    setExpenseGlCodeFormData((prev) => ({
                      ...prev,
                      claim_type: resolvedClaimType,
                      expense_type: nextValue,
                      entitlement_type: getDisplayedEntitlementType({
                        claimType: resolvedClaimType,
                        expenseType: nextValue,
                        storedValue: prev.entitlement_type
                      })
                    }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                >
                  <option value="">Select Expense Type</option>
                  {getManageExpenseTypeOptions(expenseGlCodeFormData.expense_type).map((expenseType) => (
                    <option key={expenseType} value={expenseType}>
                      {expenseType === '__create_new_expense_type__' ? '+ Create New Expense Type' : expenseType}
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-xs text-gray-500">
                  Pick an existing expense type or create a new one without leaving this screen.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Entitlement Used *</label>
                {(() => {
                  const resolvedClaimType =
                    expenseGlCodeFormData.claim_type ||
                    inferExpenseClaimType(
                      expenseGlCodeFormData.entity,
                      expenseGlCodeFormData.expense_type
                    );
                  const entitlementOptions = getEntitlementTypeOptions({
                    claimType: resolvedClaimType
                  });

                  return (
                <select
                  value={expenseGlCodeFormData.entitlement_type}
                  onChange={(e) => setExpenseGlCodeFormData({ ...expenseGlCodeFormData, entitlement_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  disabled={isProjectsClaimType(resolvedClaimType)}
                >
                  {entitlementOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                  );
                })()}
                <p className="mt-2 text-xs text-gray-500">
                  {isProjectsClaimType(inferExpenseClaimType(expenseGlCodeFormData.entity, expenseGlCodeFormData.expense_type))
                    ? 'Projects expenses always use No Limit.'
                    : 'For HR and Others, you can choose Monthly, Yearly, or No Limit.'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">GL Code *</label>
                <input
                  type="text"
                  value={expenseGlCodeFormData.gl_code}
                  onChange={(e) => setExpenseGlCodeFormData({ ...expenseGlCodeFormData, gl_code: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  placeholder="e.g. 908-P003"
                />
              </div>
              {editingExpenseGlCode && (
                <div className="flex items-center justify-between py-2">
                  <label className="text-sm font-medium text-gray-700">Active Status</label>
                  <button
                    onClick={() => setExpenseGlCodeFormData({ ...expenseGlCodeFormData, is_active: !expenseGlCodeFormData.is_active })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${expenseGlCodeFormData.is_active ? 'bg-green-500' : 'bg-gray-300'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${expenseGlCodeFormData.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
              )}
            </div>
            <div className="flex gap-3 p-6 border-t">
              <button onClick={() => { setShowInlineExpenseTypeModal(false); setShowExpenseGlCodeModal(false); }} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors">Cancel</button>
              <button onClick={handleSaveExpenseGlCode} className="flex-1 px-4 py-2 bg-yellow-400 rounded-lg font-semibold hover:bg-yellow-500 transition-colors">
                {editingExpenseGlCode ? 'Save Changes' : 'Create GL Code'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showInlineExpenseTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-bold">Create New Expense Type</h3>
              <button
                onClick={() => setShowInlineExpenseTypeModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Entity *</label>
                <select
                  value={inlineExpenseTypeFormData.entity}
                  onChange={(e) => setInlineExpenseTypeFormData({ ...inlineExpenseTypeFormData, entity: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                >
                  <option value="">Select Entity</option>
                  {companies.filter(c => c.is_active).map(c => (
                    <option key={c.id} value={c.full_name}>{c.full_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Claim Type *</label>
                <select
                  value={inlineExpenseTypeFormData.claim_type}
                  onChange={(e) => setInlineExpenseTypeFormData({ ...inlineExpenseTypeFormData, claim_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                >
                  <option value="Projects">Projects</option>
                  <option value="HR and Others">HR and Others</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expense Type *</label>
                <input
                  type="text"
                  value={inlineExpenseTypeFormData.expense_type}
                  onChange={(e) => setInlineExpenseTypeFormData({ ...inlineExpenseTypeFormData, expense_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  placeholder="e.g. OFFICE SUBSCRIPTION"
                />
              </div>
              <p className="text-xs text-gray-500">
                We still keep claim type here so the new expense type appears correctly in claim forms later.
              </p>
            </div>
            <div className="flex gap-3 p-6 border-t">
              <button
                onClick={() => setShowInlineExpenseTypeModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveInlineExpenseType}
                className="flex-1 px-4 py-2 bg-yellow-400 rounded-lg font-semibold hover:bg-yellow-500 transition-colors"
              >
                Create Expense Type
              </button>
            </div>
          </div>
        </div>
      )}

      {showCompanyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-bold">{editingCompany ? 'Edit Company' : 'Add New Company'}</h3>
              <button onClick={() => setShowCompanyModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                <input
                  type="text"
                  value={companyFormData.full_name}
                  onChange={(e) => setCompanyFormData({...companyFormData, full_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  placeholder="e.g. The Makeover Guys Sdn. Bhd"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code (3-4 characters) *</label>
                <input
                  type="text"
                  value={companyFormData.code}
                  onChange={(e) => setCompanyFormData({...companyFormData, code: e.target.value.toUpperCase().slice(0, 4)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  placeholder="e.g. TMG or MOHP"
                  maxLength={4}
                />
                <p className="text-xs text-gray-500 mt-1">Used as prefix for Claim IDs (e.g. TMG-202602-00001 or MOHP-202602-00001)</p>
              </div>
            </div>
            <div className="flex gap-3 p-6 border-t">
              <button onClick={() => setShowCompanyModal(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors">Cancel</button>
              <button onClick={handleSaveCompany} className="flex-1 px-4 py-2 bg-yellow-400 rounded-lg font-semibold hover:bg-yellow-500 transition-colors">
                {editingCompany ? 'Save Changes' : 'Create Company'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== CREATE/EDIT DEPARTMENT MODAL ==================== */}
      {showDepartmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-bold">{editingDepartment ? 'Edit Department' : 'Add New Department'}</h3>
              <button onClick={() => setShowDepartmentModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department Name *</label>
                <input
                  type="text"
                  value={departmentFormData.name}
                  onChange={(e) => setDepartmentFormData({...departmentFormData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  placeholder="e.g. Operations"
                />
              </div>
            </div>
            <div className="flex gap-3 p-6 border-t">
              <button onClick={() => setShowDepartmentModal(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors">Cancel</button>
              <button onClick={handleSaveDepartment} className="flex-1 px-4 py-2 bg-yellow-400 rounded-lg font-semibold hover:bg-yellow-500 transition-colors">
                {editingDepartment ? 'Save Changes' : 'Create Department'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showUserImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <div>
                <h3 className="text-lg font-bold">Import Users</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Step {userImportStep === 'mapping' ? '1' : userImportStep === 'progress' ? '2' : '3'} of 3
                </p>
              </div>
              <button onClick={resetUserImportState} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 pt-6">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className={`rounded-xl border px-4 py-3 ${userImportStep === 'mapping' ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="font-semibold">1. Column Linking</div>
                  <div className="text-xs text-gray-500 mt-1">Match Excel columns to user fields.</div>
                </div>
                <div className={`rounded-xl border px-4 py-3 ${userImportStep === 'progress' ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="font-semibold">2. Import Progress</div>
                  <div className="text-xs text-gray-500 mt-1">Create or update users from the file.</div>
                </div>
                <div className={`rounded-xl border px-4 py-3 ${userImportStep === 'result' ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="font-semibold">3. Import Result</div>
                  <div className="text-xs text-gray-500 mt-1">See created, updated, and skipped rows.</div>
                </div>
              </div>
            </div>

            {userImportStep === 'mapping' && (
              <div className="p-6 space-y-5">
                <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm">
                  <div className="font-medium text-gray-800">Selected file</div>
                  <div className="text-gray-600 mt-1">{userImportFileName || 'No file selected yet'}</div>
                  {userImportRows.length > 0 && (
                    <div className="text-xs text-gray-500 mt-1">{userImportRows.length} rows ready to import.</div>
                  )}
                </div>

                {userImportError && (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {userImportError}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {USER_IMPORT_FIELD_DEFINITIONS.map((field) => (
                    <div key={field.key}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field.label}{field.required ? ' *' : ''}
                      </label>
                      <select
                        value={userImportMappings[field.key] || ''}
                        onChange={(e) => setUserImportMappings((prev) => ({ ...prev, [field.key]: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      >
                        <option value="">Do not import this field</option>
                        {userImportSelectableColumns.map((column) => (
                          <option key={column} value={column}>{column}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>

                <div className="rounded-xl border border-gray-200 px-4 py-3">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-800">Preview</h4>
                      <p className="text-xs text-gray-500">First 3 rows from the Excel file.</p>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-gray-600">
                          {userImportPreviewColumns.map((column) => (
                            <th key={column} className="px-3 py-2 text-left font-medium whitespace-nowrap">{column}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {userImportRows.slice(0, 3).map((row) => (
                          <tr key={row.__rowNumber} className="border-t border-gray-100">
                            {userImportPreviewColumns.map((column) => (
                              <td key={`${row.__rowNumber}-${column}`} className="px-3 py-2 align-top text-gray-700">
                                {String(row[column] ?? '') || '-'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {userImportStep === 'progress' && (
              <div className="p-6 space-y-4">
                <div className="rounded-2xl border border-gray-200 p-6 text-center">
                  <div className="text-lg font-semibold text-gray-800">Processing import</div>
                  <div className="text-sm text-gray-500 mt-2">{userImportStatus}</div>
                  <div className="mt-6 h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div className="h-full w-3/4 bg-yellow-400 animate-pulse" />
                  </div>
                  <div className="text-xs text-gray-500 mt-3">This may take a moment depending on the number of rows.</div>
                </div>
              </div>
            )}

            {userImportStep === 'result' && (
              <div className="p-6 space-y-5">
                {userImportError ? (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {userImportError}
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <div className="rounded-xl border border-gray-200 px-4 py-3"><div className="text-xs text-gray-500">Total</div><div className="text-xl font-semibold">{userImportResult?.summary?.total ?? 0}</div></div>
                      <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3"><div className="text-xs text-green-700">Created</div><div className="text-xl font-semibold text-green-700">{userImportResult?.summary?.created ?? 0}</div></div>
                      <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3"><div className="text-xs text-blue-700">Updated</div><div className="text-xl font-semibold text-blue-700">{userImportResult?.summary?.updated ?? 0}</div></div>
                      <div className="rounded-xl border border-yellow-200 bg-yellow-50 px-4 py-3"><div className="text-xs text-yellow-700">Skipped</div><div className="text-xl font-semibold text-yellow-700">{userImportResult?.summary?.skipped ?? 0}</div></div>
                      <div className="rounded-xl border border-purple-200 bg-purple-50 px-4 py-3"><div className="text-xs text-purple-700">Warnings</div><div className="text-xl font-semibold text-purple-700">{userImportResult?.summary?.warnings ?? 0}</div></div>
                    </div>

                    <div className="rounded-xl border border-gray-200 overflow-hidden">
                      <div className="px-4 py-3 border-b bg-gray-50">
                        <h4 className="text-sm font-semibold text-gray-800">Row-by-row result</h4>
                      </div>
                      <div className="max-h-[40vh] overflow-y-auto">
                        <table className="min-w-full text-sm">
                          <thead className="bg-white sticky top-0">
                            <tr className="text-gray-500 border-b">
                              <th className="px-3 py-2 text-left font-medium">Row</th>
                              <th className="px-3 py-2 text-left font-medium">Name</th>
                              <th className="px-3 py-2 text-left font-medium">Status</th>
                              <th className="px-3 py-2 text-left font-medium">Reason / Warning</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(userImportResult?.rows || []).map((row) => (
                              <tr key={`${row.row_number}-${row.name}-${row.status}`} className="border-b border-gray-100">
                                <td className="px-3 py-2">{row.row_number}</td>
                                <td className="px-3 py-2">{row.name || row.email || '-'}</td>
                                <td className="px-3 py-2">
                                  <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                                    row.status === 'created' ? 'bg-green-100 text-green-700' :
                                    row.status === 'updated' ? 'bg-blue-100 text-blue-700' :
                                    'bg-yellow-100 text-yellow-700'
                                  }`}>
                                    {row.status}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-gray-600">{row.reason || row.warning || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="flex gap-3 p-6 border-t">
              {userImportStep === 'mapping' && (
                <>
                  <button
                    onClick={resetUserImportState}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRunUserImport}
                    disabled={userImportIsProcessing || !userImportRows.length}
                    className="flex-1 px-4 py-2 bg-yellow-400 rounded-lg font-semibold hover:bg-yellow-500 transition-colors disabled:opacity-60"
                  >
                    Start Import
                  </button>
                </>
              )}
              {userImportStep === 'progress' && (
                <button
                  disabled
                  className="w-full px-4 py-2 bg-gray-200 rounded-lg font-semibold text-gray-500 cursor-not-allowed"
                >
                  Importing...
                </button>
              )}
              {userImportStep === 'result' && (
                <>
                  <button
                    onClick={resetUserImportState}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                  <button
                    onClick={handleOpenUserImportPicker}
                    className="flex-1 px-4 py-2 bg-yellow-400 rounded-lg font-semibold hover:bg-yellow-500 transition-colors"
                  >
                    Import Another File
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ==================== CREATE/EDIT USER MODAL ==================== */}
      {showUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-bold">{editingUser ? 'Edit User' : 'Add New User'}</h3>
              <button onClick={() => setShowUserModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Legal Name *</label>
                <input
                  type="text"
                  value={userFormData.full_legal_name}
                  onChange={(e) => setUserFormData({...userFormData, full_legal_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  placeholder="Full legal name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={userFormData.email}
                  onChange={(e) => setUserFormData({...userFormData, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  placeholder="user@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                <input
                  type="text"
                  value={userFormData.phone}
                  onChange={(e) => setUserFormData({...userFormData, phone: normalizeMalaysiaPhone(e.target.value)})}
                  onBlur={(e) => setUserFormData({...userFormData, phone: normalizeMalaysiaPhone(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  placeholder="+60123456789"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                <input
                  type="text"
                  value={userFormData.position}
                  onChange={(e) => setUserFormData({...userFormData, position: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  placeholder="Enter position"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Join Date {editingUser ? '' : '*'} (dd/mm/yyyy)</label>
                <input
                  type="date"
                  value={userFormData.join_date}
                  onChange={(e) => setUserFormData({...userFormData, join_date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Employment Date (Optional)</label>
                <input
                  type="date"
                  value={userFormData.resign_date}
                  onChange={(e) => setUserFormData({...userFormData, resign_date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Entity *</label>
                <select
                  value={userFormData.entity}
                  onChange={(e) => setUserFormData({...userFormData, entity: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                >
                  <option value="">Select Entity</option>
                  {companies.filter(c => c.is_active).map(c => (
                    <option key={c.id} value={c.full_name}>{c.full_name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <select
                  value={userFormData.department}
                  onChange={(e) => setUserFormData({...userFormData, department: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                >
                  <option value="">Select Department</option>
                  {departments.filter(d => d.is_active).map(d => (
                    <option key={d.id} value={d.name}>{d.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sub-department (Optional)</label>
                <select
                  value={userFormData.proj_no}
                  onChange={(e) => setUserFormData({...userFormData, proj_no: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                >
                  <option value="">Select Sub-department</option>
                  <option value="T1">T1</option>
                  <option value="T2">T2</option>
                  <option value="T3">T3</option>
                  <option value="T4">T4</option>
                  <option value="JB">JB</option>
                  <option value="Corporate Projects">Corporate Projects</option>
                  <option value="Site Officer">Site Officer</option>
                  <option value="Labbits">Labbits</option>
                  <option value="Wall Expert">Wall Expert</option>
                  <option value="Launchpad">Launchpad</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                <select
                  value={userFormData.role}
                  onChange={(e) => setUserFormData({...userFormData, role: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Manage User</label>
                    <p className="text-xs text-gray-500 mt-1">Enable access to Manage &gt; Manage User only.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setUserFormData((prev) => ({ ...prev, manage_user_access: !prev.manage_user_access }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${userFormData.manage_user_access ? 'bg-yellow-400' : 'bg-gray-300'}`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${userFormData.manage_user_access ? 'translate-x-5' : 'translate-x-1'}`}
                    />
                  </button>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Manage Company</label>
                    <p className="text-xs text-gray-500 mt-1">Enable Manage Company, Manage Department, and Manage Webhook.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setUserFormData((prev) => ({ ...prev, manage_company_access: !prev.manage_company_access }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${userFormData.manage_company_access ? 'bg-yellow-400' : 'bg-gray-300'}`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${userFormData.manage_company_access ? 'translate-x-5' : 'translate-x-1'}`}
                    />
                  </button>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Manage Expense GL Code</label>
                    <p className="text-xs text-gray-500 mt-1">Enable access to Manage &gt; Manage Expense GL Code only.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setUserFormData((prev) => ({ ...prev, manage_expense_gl_code_access: !prev.manage_expense_gl_code_access }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${userFormData.manage_expense_gl_code_access ? 'bg-yellow-400' : 'bg-gray-300'}`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${userFormData.manage_expense_gl_code_access ? 'translate-x-5' : 'translate-x-1'}`}
                    />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Report To *</label>
                <div className="relative report-to-dropdown-container">
                  <input
                    type="text"
                    value={reportToSearch}
                    onChange={(e) => {
                      const value = e.target.value;
                      setReportToSearch(value);
                      setShowReportToDropdown(true);
                      if (!value.trim()) {
                        setUserFormData((prev) => ({ ...prev, manager_id: '' }));
                      }
                    }}
                    onFocus={() => setShowReportToDropdown(true)}
                    placeholder="Search by name or email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  />
                  {showReportToDropdown && (
                    <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                      {reportToUserPoolLoading ? (
                        <div className="px-3 py-2 text-sm text-gray-500">Loading active users...</div>
                      ) : filteredReportToOptions.length > 0 ? (
                        filteredReportToOptions.map((user) => (
                          <button
                            key={user.id}
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setUserFormData((prev) => ({ ...prev, manager_id: user.id }));
                              setReportToSearch(user.full_name || user.email || '');
                              setShowReportToDropdown(false);
                            }}
                            className="flex w-full flex-col px-3 py-2 text-left hover:bg-yellow-50"
                          >
                            <span className="text-sm font-medium text-gray-900">{user.full_name || '-'}</span>
                            <span className="text-xs text-gray-500">{user.email || '-'}</span>
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-500">No matching users found.</div>
                      )}
                    </div>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">Type to search, then click a user to select.</p>
                {userFormData.manager_id && reportToSearch && (
                  <p className="mt-1 text-xs text-green-600">Selected: {reportToSearch}</p>
                )}
              </div>

              {editingUser && (
                <div className="flex items-center justify-between py-2">
                  <label className="text-sm font-medium text-gray-700">Active Status</label>
                  <button
                    onClick={() => setUserFormData({...userFormData, is_active: !userFormData.is_active})}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${userFormData.is_active ? 'bg-green-500' : 'bg-gray-300'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${userFormData.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
              )}

              <div className="border-t pt-4 mt-2">
                <h4 className="text-sm font-semibold text-gray-800 mb-3">Entitlements (RM)</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Monthly Entitlement</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={userFormData.monthly_entitlement}
                      onChange={(e) => setUserFormData({...userFormData, monthly_entitlement: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Yearly Entitlement</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={userFormData.yearly_entitlement}
                      onChange={(e) => setUserFormData({...userFormData, yearly_entitlement: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-2">Projects claim type has no limit. User entitlements default to RM 0.00 unless you set an amount.</p>
              </div>
            </div>

            <div className="flex gap-3 p-6 border-t">
              <button
                onClick={() => setShowUserModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveUser}
                className="flex-1 px-4 py-2 bg-yellow-400 rounded-lg font-semibold hover:bg-yellow-500 transition-colors"
              >
                {editingUser ? 'Save Changes' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== REASSIGN SUBORDINATES MODAL ==================== */}
      {showReassignModal && deactivatingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b">
              <h3 className="text-lg font-bold text-gray-900">Reassign Subordinates</h3>
              <p className="text-sm text-gray-500 mt-1">
                {deactivatingUser.full_name} has {subordinatesOfDeactivating.length} active subordinate{subordinatesOfDeactivating.length > 1 ? 's' : ''}. Please select who to reassign them to before deactivating.
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subordinates to be reassigned:</label>
                <div className="space-y-1">
                  {subordinatesOfDeactivating.map(sub => (
                    <div key={sub.id} className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
                      {sub.full_name} ({sub.email})
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reassign to *</label>
                <select
                  value={reassignToUserId}
                  onChange={(e) => setReassignToUserId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                >
                  <option value="">Select a person</option>
                  {userList
                    .filter(u => u.is_active && u.id !== deactivatingUser.id)
                    .map(u => (
                      <option key={u.id} value={u.id}>{u.full_name}</option>
                    ))}
                </select>
              </div>
            </div>
            <div className="flex gap-3 p-6 border-t">
              <button
                onClick={() => { setShowReassignModal(false); setDeactivatingUser(null); }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDeactivation}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-colors"
              >
                Reassign & Deactivate
              </button>
            </div>
          </div>
        </div>
      )}

        <div className="bg-gray-900 py-4 mt-auto">
        <p className="text-center text-gray-400 text-xs px-4">Copyright © The Makeover Guys Sdn Bhd (1125623-P). All rights reserved</p>
      </div>
    </div>
  );
};

export default TMGClaimSystem;
